import {
  CAMPAIGN_SLOT_PRICE,
  DEFAULT_OUTREACH_DATE_FORMAT,
  DEFAULT_PAY_DAY,
  Iso8601Schema,
  QuestStatus,
  ROSTER_DROP_KIND_LABEL,
  ROSTER_STATUSES,
  RosterBallHolder,
  RosterPhase,
  RosterStatus,
  SETTLEMENT_AMOUNT_PATTERN,
  ScenarioCampaignType,
  ScenarioProgressMetricKey,
  ScenarioStatus,
  SegmentSourceType,
  SettlementKind,
  SettlementStatus,
  TeamRole,
  TeamStatus,
  TeamType,
  TimelineEventType,
  assertStatus,
  assertThreadBody,
  calculateCampaignPrice,
  creatorEmailSchema,
  deriveRosterBallHolder,
  deriveRosterPhase,
  evaluateTargetSlotConditions,
  formatOutreachDate,
  getLedgerPeriod,
  getPayPeriodUtcWindow,
  groupPrerequisites,
  isQuestChainCleared,
  isRosterUploadComplete,
  kstDate,
  kstStartOfDay,
  normalizeCreatorHandle,
  normalizeVideoUrl,
  paypalMeSchema,
  previousKstPeriod,
  seedCheckpointDiscriminator,
  sortRelationHistory,
  toEpochMs,
  toKstDateString,
  toKstParts,
  toKstWorkDateString,
  toUtcDateString
} from "./chunk-WAJJ5T7C.mjs";

// src/domain/events.ts
var DOKKAEBI_EVENTS = {
  ATTENDANCE_CLOCK_IN: "dokkaebi.attendance.clockIn",
  POINT_EARNED: "dokkaebi.point.earned",
  QUEST_COMPLETED: "dokkaebi.quest.completed",
  USER_LOGIN: "dokkaebi.user.login",
  PROOF_APPROVED: "dokkaebi.proof.approved",
  SIGNAL_RECEIVED: "dokkaebi.signal.received"
};
var APP_EVENTS = {
  USER_REGISTERED: "user.registered",
  // 계정 프로비저닝 완료(팀 배정 시도까지 끝난 시점) — self-register·batch 등록 양쪽이 발사.
  // USER_REGISTERED 와 분리한 이유: 그 이벤트는 채팅 자동가입 리스너가 물려 있어 batch 에
  // 재사용하면 기존 동작이 바뀐다. 현재 소비처는 온보딩 퀘스트 발행(CP3) 하나.
  USER_PROVISIONED: "user.provisioned",
  // 자가 가입 계정이 메일 인증코드로 소유를 확인했는데 아직 활성 부서가 없다(87bac89b).
  // 소비처는 인사 담당 알림 리스너 하나 — 부서가 있었으면 그 자리에서 열려 이 이벤트 대신
  // USER_REGISTERED·USER_PROVISIONED 가 나간다.
  SIGNUP_VERIFIED: "user.signup_verified",
  ATTENDANCE_CLOCKED_OUT: "attendance.clocked_out",
  OPENCLAW_CHAT: "openclaw.chat",
  QUEST_UPDATED: "notification.quest_updated",
  QUEST_DELETED: "notification.quest_deleted",
  // 퀘스트 ACTIVE 전이 fan-out. quest.service 가 이미 쓰는 TimelineEventType.QUEST_CREATED
  // (시나리오 타임라인, 알림과 무관)와 이름이 겹치지 않도록 'published' 로 명명한다.
  QUEST_PUBLISHED: "notification.quest_published",
  // 퀘스트가 연계 판정상 끝난 상태로 막 넘어갔다(잠김·완료·보관). 그 퀘스트를 선행으로 둔 후행의
  // 남은 묶음을 다시 세어 0 이 된 후행에 「이제 시작할 수 있다」를 보낸다. 상태 전이 커밋 뒤에 쏜다.
  QUEST_CHAIN_CLEARED: "notification.quest_chain_cleared",
  CHECKPOINT_SUBMITTED: "notification.checkpoint_submitted",
  CHECKPOINT_APPROVED: "notification.checkpoint_approved",
  CHECKPOINT_REJECTED: "notification.checkpoint_rejected",
  OUTBREAK_TRIGGERED: "notification.outbreak_triggered",
  OUTBREAK_REVOKED: "notification.outbreak_revoked",
  OPEN_CALL_REVOKED: "notification.open_call_revoked",
  RANK_CHANGED: "notification.rank_changed",
  POINT_SETTLED: "notification.point_settled",
  ATTENDANCE_CORRECTION_REQUESTED: "attendance.correction_requested",
  ATTENDANCE_CORRECTION_APPROVED: "attendance.correction_approved",
  ATTENDANCE_CORRECTION_REJECTED: "attendance.correction_rejected",
  ATTENDANCE_SEGMENT_CORRECTION_REQUESTED: "attendance.segment_correction_requested",
  ATTENDANCE_SEGMENT_CORRECTION_APPROVED: "attendance.segment_correction_approved",
  ATTENDANCE_SEGMENT_CORRECTION_REJECTED: "attendance.segment_correction_rejected",
  CP_TASK_COMPLETED: "notification.cp_task_completed",
  // ADCODE 요청자-승인 흐름 (plan 2026-06-22 L5) — CHECKPOINT 3종 선례 미러.
  ADCODE_PROOF_SUBMITTED: "notification.adcode_proof_submitted",
  ADCODE_PROOF_ACCEPTED: "notification.adcode_proof_accepted",
  ADCODE_PROOF_REJECTED: "notification.adcode_proof_rejected",
  // 시나리오의 애드코드 신청이 퀘스트로 자동 생성된 시점 → 구독(opt-in)을 켠 ADCODE capability
  // 보유자 알림. emit 은 생성 커밋 후·catch 밖이며 같은 경로의 VA_REQUEST 는 발사하지 않는다.
  // 수신자 fan-out(구독자 조회·capability 교집합·신청자 억제)은 notification 리스너 담당.
  ADCODE_QUEST_CREATED: "notification.adcode_quest_created",
  // 일반 퀘스트 증빙 제출 → 생성자·소유팀 팀장·시나리오 진행 책임자 알림. ADCODE_PROOF_SUBMITTED 와
  // 별개 경로다(ADCODE 는 요청자 시나리오 참여팀, 이쪽은 소유팀 팀장). emit 은 tx 성공 후,
  // 수신자 fan-out(팀장 조회·진행 책임자 조회·중복 제거·제출자 억제)은 리스너 담당.
  QUEST_PROOF_SUBMITTED: "notification.quest_proof_submitted",
  // 일반 퀘스트 증빙 반려 → 제출자 1명. 제출·승인은 알림이 가는데 반려만 침묵이라
  // 제출자가 목록을 폴링해야 알 수 있었다. 수신자가 한 명뿐이라 fan-out 조회가 없다.
  // emit 은 tx 성공 후 — 반려 사유(comment)는 커밋된 ProofComment 와 같은 문자열이다.
  QUEST_PROOF_REJECTED: "notification.quest_proof_rejected",
  // 태스크 피드백 도착 → 태스크 담당자(= 그 퀘스트를 수락해 일하고 있는 사람) 알림.
  // emit 은 피드백 row 생성 커밋 후. 딥링크 좌표(퀘스트 scope·시나리오)는 payload 에 싣지 않고
  // 리스너가 questId 로 조회해 해소한다 — 서비스는 태스크 원재료만 안다(QuestProofSubmittedEvent 선례).
  QUEST_TASK_FEEDBACK_ADDED: "notification.quest_task_feedback_added",
  // 검토 대기(IN_REVIEW) 중인 정성 CP 에 담당자가 자료를 보탠 시점 → 그 CP 를 승인할 팀 LEADER.
  // 형제 CHECKPOINT_SUBMITTED 가 '검토를 시작해달라'면 이쪽은 '검토 중인 건에 자료가 늘었다' —
  // 팀장이 이미 승인 화면을 지나쳤을 수 있어 문구·타입을 가른다. IN_REVIEW 가 아니면 아무도
  // 기다리고 있지 않으므로 리스너가 통째로 억제한다(작업 중 업로드마다 팀장을 부르지 않는다).
  QUEST_TASK_ATTACHMENT_ADDED: "notification.quest_task_attachment_added",
  // 퀘스트 질문 스레드에 질문이 등록됨 → 그 퀘스트가 걸린 팀의 팀장 전원 알림.
  // 수신자를 팀장 축으로 좁히는 근거는 위계가 아니라 **답글 권한**이다 — canAnswerQuestQuestion
  // (shared/domain/quest-question)이 quest.teamId 의 LEADER 와 질문 작성자만 허용하고
  // 생성자·ADMIN·OPERATOR 에게는 403 이라, 그들에게 보내면 열어도 답할 수 없는 알림이 된다.
  // emit 은 질문 row 생성 후·try/catch 밖(단건 create 라 트랜잭션이 없다).
  // 수신자 fan-out(팀 LEADER 조회·퇴사자 제외·질문자 억제)은 notification 리스너 담당 —
  // payload 는 그 판별에 필요한 원재료만 싣는다(QuestProposalCreatedEvent 선례).
  QUEST_QUESTION_CREATED: "notification.quest_question_created",
  // 운영 시그널 어려움 보고 알림 트랙 (IA §6 N-1~N-6). 쓰기 경로 3개 = 이벤트 3개인데
  // 알림 타입은 4개다 — helpNeeded=true 인 생성 하나가 팀장(N-4)·동료(N-6)를 한 배치로 묶는
  // OPS_SIGNAL_HELP_NEEDED 로 갈리기 때문(팀장에게 1건만 가는 IA §6:132 가드를 구조로 충족).
  // emit 은 세 경로 모두 쓰기 커밋 후(단건 쿼리라 await 결과 캡처 뒤).
  // 수신자 fan-out(보고자 팀 resolve·LEADER/팀전원 조회·퇴사자 제외·행위자 억제)과 타입 선택은
  // 전부 notification-event.listener 담당 — payload 는 그 판별에 필요한 원재료만 싣는다.
  // 퀘스트 제안함 (docs/design/quest-proposal-inbox.md). 생성 → 해당 팀 팀장 전원,
  // 처리(채택·거절) → 제안자. emit 은 쓰기 커밋 후.
  // 수신자 fan-out(팀 LEADER 조회·퇴사자 제외·행위자 억제)은 notification-event.listener 담당 —
  // payload 는 그 판별에 필요한 원재료만 싣는다(OPS_SIGNAL_* 선례).
  // ⚠ 리스너는 아직 없다(T3 트랙). 아래 두 키는 app-events-contract.spec 의
  // KNOWN_NO_LISTENER 에 올려두었으니, 리스너를 배선할 때 그 allowlist 에서 빼야 한다.
  QUEST_PROPOSAL_CREATED: "notification.quest_proposal_created",
  QUEST_PROPOSAL_RESOLVED: "notification.quest_proposal_resolved",
  QUEST_APPROVAL_RESOLVED: "notification.quest_approval_resolved",
  OPS_SIGNAL_REPORT_CREATED: "notification.ops_signal_report_created",
  OPS_SIGNAL_COMMENT_ADDED: "notification.ops_signal_comment_added",
  OPS_SIGNAL_STATUS_CHANGED: "notification.ops_signal_status_changed",
  RESERVATION_ATTENDEE_ADDED: "notification.reservation_attendee_added",
  RESERVATION_ATTENDEE_REMOVED: "notification.reservation_attendee_removed",
  RESERVATION_CANCELLED: "notification.reservation_cancelled",
  RESERVATION_RESCHEDULED: "notification.reservation_rescheduled",
  CREATOR_RELATION_PREMIUM_CONTACT_RECORDED: "notification.creator_relation_premium_contact_recorded",
  CHAT_MESSAGE_SENT: "chat.message.sent",
  OPENCLAW_CONNECTED: "openclaw.connected",
  OPENCLAW_DISCONNECTED: "openclaw.disconnected",
  // 급여기간 정산 상태가 움직였다 — 배당 확정(team) · 배당 지급(point) 두 쓰기 경로가 발사한다.
  // 리스너가 마감 조건을 다시 판정해 방금 충족됐으면 대표에게 N4 를 보낸다
  // (design/budget-ledger-period-close.md §8).
  //
  // ⚠️ 'notification.' 접두사를 쓰지 않는 것은 의도다. 그 이름공간은 키 ↔ 알림 타입 1:1 규약
  // (events.test.ts SHR-4)이 걸려 있는데 이 이벤트는 **알림 1종에 대응하지 않는다** —
  // 조건이 안 찼으면 아무 알림도 안 만든다. 도메인 이벤트 쪽(attendance.* · worklog.*)이 맞다.
  //
  // 이벤트를 경유하는 이유는 순환 의존이다: NotificationModule 이 TeamModule 을 이미
  // import 하므로 TeamModule 쪽 확정 경로가 NotificationService 를 직접 주입할 수 없다
  // (같은 이유로 이벤트를 경유한다).
  BUDGET_SETTLEMENT_CHANGED: "budget.settlement_changed"
};

// src/domain/auth/token.ts
var TokenType = {
  ACCESS: "access",
  REFRESH: "refresh"
};

// src/domain/auth/role.ts
var createPermission = (resource, action) => {
  return { resource, action };
};
var hasPermission = (userPermissions, required) => {
  return userPermissions.some(
    (p) => p.resource === required.resource && p.action === required.action
  );
};
var RolePermissions = {
  ADMIN: [
    { resource: "knowledge", action: "read" },
    { resource: "knowledge", action: "manage" },
    { resource: "knowledge", action: "approve" },
    // User - all
    { resource: "user", action: "create" },
    { resource: "user", action: "read" },
    { resource: "user", action: "update" },
    { resource: "user", action: "delete" },
    // Quest - all
    { resource: "quest", action: "create" },
    { resource: "quest", action: "read" },
    { resource: "quest", action: "update" },
    { resource: "quest", action: "delete" },
    { resource: "quest", action: "accept" },
    { resource: "quest", action: "outbreak" },
    { resource: "quest", action: "approve" },
    // Checkpoint - all
    { resource: "checkpoint", action: "create" },
    { resource: "checkpoint", action: "read" },
    { resource: "checkpoint", action: "update" },
    { resource: "checkpoint", action: "delete" },
    { resource: "checkpoint", action: "submit" },
    { resource: "checkpoint", action: "approve" },
    { resource: "checkpoint", action: "reject" },
    // Proof - all
    { resource: "proof", action: "create" },
    { resource: "proof", action: "read" },
    { resource: "proof", action: "verify" },
    { resource: "proof", action: "delete" },
    // Rank - all
    { resource: "rank", action: "read" },
    { resource: "rank", action: "update" },
    // System - all
    { resource: "system", action: "admin_access" },
    { resource: "system", action: "manage" },
    // Attendance - all
    { resource: "attendance", action: "create" },
    { resource: "attendance", action: "read" },
    { resource: "attendance", action: "manage" },
    // Team - all
    { resource: "team", action: "create" },
    { resource: "team", action: "read" },
    { resource: "team", action: "update" },
    { resource: "team", action: "archive" },
    { resource: "team", action: "manage_members" },
    // WorkLog - all (admin oversight + own)
    { resource: "worklog", action: "create" },
    { resource: "worklog", action: "read" },
    { resource: "worklog", action: "update" },
    { resource: "worklog", action: "submit" },
    // Scenario - all
    { resource: "scenario", action: "create" },
    { resource: "scenario", action: "read" },
    { resource: "scenario", action: "update" },
    { resource: "scenario", action: "delete" },
    { resource: "scenario", action: "activate" },
    { resource: "scenario", action: "archive" },
    { resource: "scenario", action: "roster_write" },
    { resource: "scenario", action: "outreach_form_write" },
    { resource: "scenario", action: "historical_source_register" },
    // 정산 실행자 축 — 전사 운영 오버라이드 (QE-10b §3.2).
    { resource: "scenario", action: "settlement_read" },
    { resource: "scenario", action: "settlement_execute" },
    // Operational signal - report + manage (전체 관리)
    { resource: "operational-signal", action: "report" },
    { resource: "operational-signal", action: "manage" },
    // Advertiser - all (운영 휴면·sentinel 처리 포함)
    { resource: "advertiser", action: "create" },
    { resource: "advertiser", action: "read" },
    { resource: "advertiser", action: "update" },
    { resource: "advertiser", action: "delete" },
    // Leave - all
    { resource: "leave", action: "create" },
    { resource: "leave", action: "read" },
    { resource: "leave", action: "approve" },
    { resource: "leave", action: "manage" },
    // Payout - all
    { resource: "payout", action: "read" },
    { resource: "payout", action: "create" },
    { resource: "payout", action: "manage" },
    // Presence - all
    { resource: "presence", action: "read" },
    { resource: "presence", action: "join" },
    // Point - all
    { resource: "point", action: "read" },
    { resource: "point", action: "manage" },
    // Wiki - all (인턴 포함 전원)
    { resource: "wiki", action: "read" },
    { resource: "wiki", action: "create" },
    { resource: "wiki", action: "update" },
    { resource: "wiki", action: "delete" },
    { resource: "wiki", action: "restore" },
    { resource: "wiki", action: "manage_areas" },
    // BugReport - all
    { resource: "bug-report", action: "create" },
    { resource: "bug-report", action: "read" },
    { resource: "bug-report", action: "triage" },
    { resource: "bug-report", action: "comment" },
    { resource: "bug-report", action: "affect" },
    // Holiday - all
    { resource: "holiday", action: "create" },
    { resource: "holiday", action: "update" },
    { resource: "holiday", action: "delete" },
    { resource: "holiday", action: "sync" },
    // Piecework - all (HR / 복지)
    { resource: "piecework", action: "create" },
    { resource: "piecework", action: "read" },
    { resource: "piecework", action: "update" },
    { resource: "piecework", action: "delete" },
    { resource: "piecework", action: "decide" },
    // Room - all (인벤토리 관리)
    { resource: "room", action: "read" },
    { resource: "room", action: "manage" },
    // ClubFeed - 동아리 피드(증빙) 승인 (HR / 복지 governance)
    { resource: "club-feed", action: "approve" },
    // BudgetLedger - 예산 원장 조회 (재무 governance, ADMIN + HR)
    { resource: "budget-ledger", action: "read" },
    // CompanySettings - 회사 근태 설정 변경 (ADMIN + HR)
    { resource: "company-settings", action: "manage" },
    // TaskStatistics - 조회 (스코프는 서비스가 강제)
    { resource: "task-statistics", action: "read" },
    // QuestProposal - all (전 리소스 보유 관례, 스코프는 서비스가 강제)
    { resource: "quest-proposal", action: "create" },
    { resource: "quest-proposal", action: "read" },
    { resource: "quest-proposal", action: "resolve" },
    // Creator - 배송·정산 개인정보 열람. 이번 유일한 부여이고 정산 실행자
    // (CapabilityPermissionModifiers.CAMPAIGN_SETTLEMENT) 부여는 후속 퀘스트 몫이다.
    { resource: "creator", action: "pii:view" }
  ],
  OPERATOR: [
    { resource: "knowledge", action: "read" },
    { resource: "knowledge", action: "manage" },
    { resource: "knowledge", action: "approve" },
    // User
    { resource: "user", action: "create" },
    { resource: "user", action: "read" },
    { resource: "user", action: "update" },
    // Quest
    { resource: "quest", action: "create" },
    { resource: "quest", action: "read" },
    { resource: "quest", action: "update" },
    { resource: "quest", action: "delete" },
    // 팀장/운영자도 퀘스트를 직접 수행한다 — 수락해야 도전자 집계(acceptance)에 잡히고
    // 팀 워크로드 분배가 가능. 관리 권한(update/delete)과 직교하는 수행자 축.
    { resource: "quest", action: "accept" },
    // Checkpoint - all
    { resource: "checkpoint", action: "create" },
    { resource: "checkpoint", action: "read" },
    { resource: "checkpoint", action: "update" },
    { resource: "checkpoint", action: "delete" },
    { resource: "checkpoint", action: "submit" },
    { resource: "checkpoint", action: "approve" },
    { resource: "checkpoint", action: "reject" },
    // Proof
    { resource: "proof", action: "read" },
    { resource: "proof", action: "verify" },
    { resource: "proof", action: "delete" },
    // Rank
    { resource: "rank", action: "read" },
    { resource: "rank", action: "update" },
    // Attendance
    { resource: "attendance", action: "create" },
    { resource: "attendance", action: "read" },
    { resource: "attendance", action: "manage" },
    // Team (no create - only admin can create teams)
    { resource: "team", action: "read" },
    { resource: "team", action: "update" },
    { resource: "team", action: "archive" },
    { resource: "team", action: "manage_members" },
    // WorkLog - all (전직원 업무일지)
    { resource: "worklog", action: "create" },
    { resource: "worklog", action: "read" },
    { resource: "worklog", action: "update" },
    { resource: "worklog", action: "submit" },
    // Scenario - all except delete (archive 는 보관 전용이라 개방 — 완료/실패 시나리오 정리)
    { resource: "scenario", action: "create" },
    { resource: "scenario", action: "read" },
    { resource: "scenario", action: "update" },
    { resource: "scenario", action: "activate" },
    { resource: "scenario", action: "archive" },
    { resource: "scenario", action: "roster_write" },
    { resource: "scenario", action: "outreach_form_write" },
    { resource: "scenario", action: "historical_source_register" },
    // 정산 마킹 — 현행 scenario:update 보유자 집합을 유지한다. 전사 큐(settlement_read)는 실행자·ADMIN 만.
    { resource: "scenario", action: "settlement_execute" },
    // Operational signal - report + manage (팀장 전체 관리)
    { resource: "operational-signal", action: "report" },
    { resource: "operational-signal", action: "manage" },
    // Advertiser - 운영자가 영업 수주 (Create + Read + Update). 휴면·삭제는 ADMIN
    { resource: "advertiser", action: "create" },
    { resource: "advertiser", action: "read" },
    { resource: "advertiser", action: "update" },
    // Leave - read, approve, manage
    { resource: "leave", action: "read" },
    { resource: "leave", action: "approve" },
    { resource: "leave", action: "manage" },
    // Payout - read only
    { resource: "payout", action: "read" },
    // Presence - all
    { resource: "presence", action: "read" },
    { resource: "presence", action: "join" },
    // Point - read + manage (운영자도 포인트 관리)
    { resource: "point", action: "read" },
    { resource: "point", action: "manage" },
    // Wiki - all (인턴 포함 전원)
    { resource: "wiki", action: "read" },
    { resource: "wiki", action: "create" },
    { resource: "wiki", action: "update" },
    { resource: "wiki", action: "delete" },
    { resource: "wiki", action: "restore" },
    { resource: "wiki", action: "manage_areas" },
    // BugReport - all
    { resource: "bug-report", action: "create" },
    { resource: "bug-report", action: "read" },
    { resource: "bug-report", action: "triage" },
    { resource: "bug-report", action: "comment" },
    { resource: "bug-report", action: "affect" },
    // Piecework - all (운영자 = HR 보조)
    { resource: "piecework", action: "create" },
    { resource: "piecework", action: "read" },
    { resource: "piecework", action: "update" },
    { resource: "piecework", action: "delete" },
    { resource: "piecework", action: "decide" },
    // Room - all (운영자 = HR 보조)
    { resource: "room", action: "read" },
    { resource: "room", action: "manage" },
    // TaskStatistics - 조회 (팀장 = 관리 팀 스코프, 서비스 강제)
    { resource: "task-statistics", action: "read" },
    // QuestProposal - read + resolve. create 는 의도적 제외 — 팀장은 퀘스트를 직접 발행한다.
    { resource: "quest-proposal", action: "read" },
    { resource: "quest-proposal", action: "resolve" }
  ],
  PLAYER: [
    { resource: "knowledge", action: "read" },
    // User
    { resource: "user", action: "read" },
    // Quest
    { resource: "quest", action: "read" },
    { resource: "quest", action: "accept" },
    // Checkpoint
    { resource: "checkpoint", action: "read" },
    // 담당자 본인이 자기 CP 를 검수 요청하는 행위 — 승인·반려와 달리 팀장 축이 아니다.
    // 실제 "본인 것인가" 판정은 서비스가 한다(권한은 필요조건일 뿐).
    { resource: "checkpoint", action: "submit" },
    // Proof
    { resource: "proof", action: "create" },
    { resource: "proof", action: "read" },
    // Rank
    { resource: "rank", action: "read" },
    // Attendance (own only)
    { resource: "attendance", action: "create" },
    { resource: "attendance", action: "read" },
    // Team (read only)
    { resource: "team", action: "read" },
    // WorkLog - own
    { resource: "worklog", action: "create" },
    { resource: "worklog", action: "read" },
    { resource: "worklog", action: "update" },
    { resource: "worklog", action: "submit" },
    // Scenario - read + 가이드라인 확정 (팀원 개방, quest #10)
    { resource: "scenario", action: "read" },
    { resource: "scenario", action: "confirm_guideline" },
    // 캠페인 로스터 행 쓰기 (팀원 개방) — 시딩매니저·팀원이 섭외 행을 직접 넣는 것이 정상 운영.
    { resource: "scenario", action: "roster_write" },
    { resource: "scenario", action: "outreach_form_write" },
    // Operational signal - report only (본인 어려움/이탈 보고 + 본인 보고 해결/취소)
    { resource: "operational-signal", action: "report" },
    // Leave - create and read (own)
    { resource: "leave", action: "create" },
    { resource: "leave", action: "read" },
    // Payout - read (own)
    { resource: "payout", action: "read" },
    // Presence - read and join
    { resource: "presence", action: "read" },
    { resource: "presence", action: "join" },
    // Point - read (자기 포인트 이력 조회)
    { resource: "point", action: "read" },
    // Wiki - all (인턴 포함 전원)
    { resource: "wiki", action: "read" },
    { resource: "wiki", action: "create" },
    { resource: "wiki", action: "update" },
    { resource: "wiki", action: "delete" },
    { resource: "wiki", action: "restore" },
    // BugReport - create, read, comment, affect (no triage)
    { resource: "bug-report", action: "create" },
    { resource: "bug-report", action: "read" },
    { resource: "bug-report", action: "comment" },
    { resource: "bug-report", action: "affect" },
    // Room - read (전사 회의실 조회·예약)
    { resource: "room", action: "read" },
    // TaskStatistics - 본인 통계 조회 (스코프는 서비스가 본인 assigneeId 로 강제)
    { resource: "task-statistics", action: "read" },
    // QuestProposal - 제안 올리기 + 자기 팀 제안 읽기 (팀 내 중복 제안 방지를 위한 투명성).
    // resolve 는 LEADER 모디파이어 — 팀장 = 번역 계층 원칙.
    { resource: "quest-proposal", action: "create" },
    { resource: "quest-proposal", action: "read" }
  ]
};
var TeamRolePermissionModifiers = {
  LEADER: [
    { resource: "proof", action: "verify" },
    // 정성 CP 검수 게이트 — proof:verify 와 같은 축(팀장이 판정하는 행위).
    // 팀 스코프(cp.quest.team 의 LEADER 인가)는 백엔드 isLeaderOf 가 강제한다.
    { resource: "checkpoint", action: "approve" },
    { resource: "checkpoint", action: "reject" },
    { resource: "attendance", action: "manage" },
    { resource: "leave", action: "approve" },
    // 참여팀 팀장의 시나리오 운영 권한 (docs scenario.md §8.1·§8.2).
    // 본인 팀이 참여팀인 시나리오에 한정된 스코프는 백엔드 assertCanWriteScenario 가 강제.
    // create/delete 는 의도적 제외 (별 surface·고위험). archive 는 보관 전용 저위험이라 개방.
    { resource: "scenario", action: "update" },
    { resource: "scenario", action: "activate" },
    { resource: "scenario", action: "archive" },
    { resource: "scenario", action: "roster_write" },
    { resource: "scenario", action: "outreach_form_write" },
    // 정산 마킹 — 참여팀 팀장은 scenario:update 로 이미 찍던 집합이라 줄이지 않는다 (QE-10b §6.4).
    { resource: "scenario", action: "settlement_execute" },
    // 운영 신호 처리 — 「팀원 보고 · 팀장 처리」. 참여팀 범위는 백엔드 isLeaderOfScenario 가 강제한다
    // (백엔드는 이 목록이 아니라 UserRole 로 manage 를 계산하고 팀장은 따로 판정한다).
    { resource: "operational-signal", action: "manage" },
    { resource: "quest", action: "create" },
    // 제안 처리(채택·거절)는 팀장 권한. 관리 팀 스코프는 백엔드 getManagingTeamIds 가 강제.
    { resource: "quest-proposal", action: "resolve" }
  ],
  MEMBER: []
};
var CapabilityPermissionModifiers = {
  HR_MANAGEMENT: [
    { resource: "attendance", action: "manage" },
    { resource: "leave", action: "manage" },
    // 지정 결재자가 아닌 신청의 대리 승인. 누구 것을 승인하는지는 서비스가
    // resolveLeaveApprovalAuthority 로 다시 가른다 — 이 권한은 가드를 지나게만 한다.
    { resource: "leave", action: "approve" },
    { resource: "holiday", action: "create" },
    { resource: "holiday", action: "update" },
    { resource: "holiday", action: "delete" },
    { resource: "holiday", action: "sync" },
    { resource: "room", action: "manage" },
    { resource: "club-feed", action: "approve" },
    { resource: "budget-ledger", action: "read" },
    { resource: "company-settings", action: "manage" }
  ],
  SYSTEM_ADMIN: [],
  FINANCE: [],
  BETA_TESTER: [],
  ADCODE: [],
  VA: [],
  DEV: [],
  // 캠페인 정산 실행자(피플팀). 정산 표면의 두 액션만 연다 — scenario:read/update 는 주지 않아
  // 시나리오 본문은 참여팀 축 그대로다 (qe10b 설계안 §2.3 · §3.2).
  //
  // `creator:pii:view` 는 예고대로 여기 붙었다 (퀘스트 c38d7883). 실행자는 큐에서 수취
  // 식별자를 읽고 송금하는 것이 업무이므로(agreement §7 정정) 큐를 여는 집합과 페이팔을 보는
  // 집합이 갈리면 실행자는 "누구에게 얼마"까지만 보고 주소는 여전히 카카오톡에서 찾는다.
  // 이 자격이 여는 것은 관계 층 PII 뿐이고, 열람은 전건 감사에 남는다.
  CAMPAIGN_SETTLEMENT: [
    { resource: "scenario", action: "settlement_read" },
    { resource: "scenario", action: "settlement_execute" },
    { resource: "creator", action: "pii:view" }
  ],
  // 국내 캠페인 정산 실행자(3팀장, fact #2902). 정산 표면의 두 액션만 연다 — 가로 범위(국내만)는
  // 백엔드 정산 서비스가 캠페인 종류로 가른다. 페이팔을 보는 자리가 아니라 `creator:pii:view` 는
  // 없다: 국내 지급 정보(계좌·주민번호)는 AXIOM 에 담지 않는다.
  DOMESTIC_CAMPAIGN_SETTLEMENT: [
    { resource: "scenario", action: "settlement_read" },
    { resource: "scenario", action: "settlement_execute" }
  ],
  // 병가 자동 수신자를 고르는 표지일 뿐이다. 인사 권한은 HR_MANAGEMENT 가 준다.
  HR_DEPARTMENT: []
};
var getPermissionsForUser = (roles, teamRole, capabilities) => {
  const permissions = /* @__PURE__ */ new Set();
  for (const role of roles) {
    for (const perm of RolePermissions[role] ?? []) {
      permissions.add(`${perm.resource}:${perm.action}`);
    }
  }
  if (teamRole) {
    for (const perm of TeamRolePermissionModifiers[teamRole] ?? []) {
      permissions.add(`${perm.resource}:${perm.action}`);
    }
  }
  if (capabilities) {
    for (const cap of capabilities) {
      for (const perm of CapabilityPermissionModifiers[cap] ?? []) {
        permissions.add(`${perm.resource}:${perm.action}`);
      }
    }
  }
  return Array.from(permissions).map((p) => {
    const separator = p.indexOf(":");
    return {
      resource: p.slice(0, separator),
      action: p.slice(separator + 1)
    };
  });
};
var canPerform = (user, resource, action) => {
  const perms = getPermissionsForUser(user.roles, user.teamRole, user.capabilities);
  return hasPermission(perms, { resource, action });
};

// src/domain/auth/ceo-gate.ts
function isCeo(user) {
  return (user.roles ?? []).includes("ADMIN") && user.level === "EXECUTIVE";
}

// src/domain/auth/usage-dashboard-gate.ts
function canViewUsageDashboard(user, activeTeamCapabilities) {
  return user.level === "EXECUTIVE" || (activeTeamCapabilities ?? []).includes("DEV");
}

// src/domain/auth/mcp-token.ts
import { z } from "zod";
var MCP_TOKEN_PREFIX = "axm_pat_";
var MCP_TOKEN_RANDOM_BYTES = 32;
var MCP_SCOPES = [
  "user:read",
  "quest:read",
  "quest:accept",
  "quest:create",
  "quest:update",
  "quest-proposal:read",
  "checkpoint:create",
  "checkpoint:update",
  "task:write",
  "proof:create",
  "proof:read",
  "proof:verify",
  "attendance:read",
  "point:read",
  "rank:read",
  "knowledge:read",
  "wiki:read",
  "wiki:create",
  "wiki:update",
  "telemetry:read",
  "telemetry:write",
  "budget:write",
  "dividend:write",
  "distribution:write",
  "mcp:impersonate"
];
var MCP_FINANCIAL_WRITE_SCOPES = [
  "budget:write",
  "dividend:write",
  "distribution:write"
];
var MCP_IMPERSONATE_SCOPE = "mcp:impersonate";
var isMcpScope = (value) => MCP_SCOPES.includes(value);
var MCP_SCOPE_ISSUE_REQUIREMENTS = {
  "user:read": { resource: "user", action: "read" },
  "quest:read": { resource: "quest", action: "read" },
  "quest:accept": { resource: "quest", action: "accept" },
  "quest:create": { resource: "quest", action: "create" },
  "quest:update": { resource: "quest", action: "update" },
  "quest-proposal:read": { resource: "quest-proposal", action: "read" },
  "checkpoint:create": { resource: "checkpoint", action: "create" },
  "checkpoint:update": { resource: "checkpoint", action: "update" },
  "task:write": null,
  "proof:create": { resource: "proof", action: "create" },
  "proof:read": { resource: "proof", action: "read" },
  "proof:verify": { resource: "proof", action: "verify" },
  "attendance:read": { resource: "attendance", action: "read" },
  "point:read": { resource: "point", action: "read" },
  "rank:read": { resource: "rank", action: "read" },
  "knowledge:read": { resource: "knowledge", action: "read" },
  "wiki:read": { resource: "wiki", action: "read" },
  "wiki:create": { resource: "wiki", action: "create" },
  "wiki:update": { resource: "wiki", action: "update" },
  "telemetry:read": null,
  "telemetry:write": null,
  "budget:write": null,
  "dividend:write": null,
  "distribution:write": null,
  "mcp:impersonate": { resource: "system", action: "admin_access" }
};
var MCP_SCOPE_PERMISSIONS = {
  ...MCP_SCOPE_ISSUE_REQUIREMENTS,
  "mcp:impersonate": null
};
var permissionsFromScopes = (scopes) => {
  const permissions = [];
  for (const scope of scopes) {
    if (!isMcpScope(scope)) continue;
    const permission = MCP_SCOPE_PERMISSIONS[scope];
    if (permission) permissions.push(permission);
  }
  return permissions;
};
var ungrantableScopes = (issuerPermissions, scopes, issuer = {}) => scopes.filter((scope) => {
  if (!isMcpScope(scope)) return true;
  if (scope === "budget:write") return !isCeo(issuer);
  if (scope === "dividend:write" || scope === "distribution:write") {
    return issuer.isTeamLeader !== true;
  }
  const required = MCP_SCOPE_ISSUE_REQUIREMENTS[scope];
  if (!required) return false;
  return !hasPermission(issuerPermissions, required);
});
var MCP_SCOPE_PRESETS = {
  READ_ONLY: ["user:read", "quest:read", "proof:read"],
  PLAYER: ["user:read", "quest:read", "proof:read", "quest:accept", "task:write", "proof:create"],
  VERIFIER: [
    "user:read",
    "quest:read",
    "proof:read",
    "quest:accept",
    "task:write",
    "proof:create",
    "proof:verify"
  ]
};
var mcpScopeSchema = z.enum(MCP_SCOPES);
var createMcpTokenSchema = z.object({
  label: z.string().min(1, "\uB77C\uBCA8\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694").max(50, "\uB77C\uBCA8\uC740 50\uC790\uB97C \uB118\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"),
  scopes: z.array(mcpScopeSchema).min(1, "\uAD8C\uD55C \uBC94\uC704\uB97C 1\uAC1C \uC774\uC0C1 \uC120\uD0DD\uD574\uC8FC\uC138\uC694"),
  expiresAt: Iso8601Schema.optional()
}).strict();

// src/domain/auth/oauth.ts
import { z as z2 } from "zod";
var OAUTH_OFFLINE_ACCESS_SCOPE = "offline_access";
var OAUTH_AUTHORIZATION_CODE_TTL_SECONDS = 10 * 60;
var OAUTH_AUTHORIZE_REQUEST_TTL_SECONDS = 10 * 60;
var OAUTH_REFRESH_TOKEN_PREFIX = "axm_ort_";
var OAUTH_REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60;
var oauthConsentApproveSchema = z2.object({
  request: z2.string().min(1),
  expectedUserId: z2.string().min(1),
  scopes: z2.array(z2.string().min(1)).min(1)
});
var oauthConsentDenySchema = z2.object({
  request: z2.string().min(1)
});

// src/domain/auth/onboarding.ts
var ONBOARDING_ALLOWED_VIEWS = [
  "attendance",
  "quests",
  "today"
];
function isOnboardingIsolated(user) {
  if (!user) return false;
  if (user.onboardingCompletedAt !== null) return false;
  const roles = Array.isArray(user.roles) ? user.roles : [];
  if (roles.includes("ADMIN") || roles.includes("OPERATOR")) return false;
  if (user.level !== "INTERN" && user.level !== "ASSOCIATE") return false;
  if (user.teamRole === "LEADER") return false;
  return true;
}
function canOpenWindowDuringOnboarding(windowId) {
  return ONBOARDING_ALLOWED_VIEWS.includes(windowId);
}

// src/domain/auth/view-access.ts
var VIEW_ELIGIBILITIES = ["creator-list"];
var LEVEL_ORDER = {
  INTERN: 0,
  ASSOCIATE: 1,
  MANAGER: 2,
  EXECUTIVE: 3
};
var APP_VIEW_REGISTRY = {
  quests: {
    label: "\uD018\uC2A4\uD2B8",
    minLevel: "INTERN"
  },
  "world-quests": {
    label: "\uC6D4\uB4DC \uD018\uC2A4\uD2B8",
    minLevel: "INTERN"
  },
  // 사내 제품(크롤러·AXIOM)별로 팀 퀘스트를 보는 창. 퀘스트 종류가 늘어난 게 아니라
  // 같은 팀 퀘스트를 제품 축으로 보는 것이라 QuestScope 는 그대로 TEAM 이다.
  //
  // 게이트를 `DEV` capability 로 둔 이유: "제품이 등록돼 있으면 창이 열린다"로 하면
  // 게이트와 데이터가 한 몸이라, 어느 팀이 제품을 만드는 순간 그 팀에도 창이 열린다.
  // 확장 순서를 사람이 쥐려면 "이 팀에 이 기능을 켰다"가 따로 선언돼야 한다.
  // `Product.teamId` 는 게이트가 아니라 소속이다 — 스파인이 내 팀 제품만 보여주는 축.
  "dev-quests": {
    label: "\uAC1C\uBC1C \uD018\uC2A4\uD2B8",
    capability: "DEV"
  },
  "work-log": {
    label: "\uC5C5\uBB34\uC77C\uC9C0",
    minLevel: "INTERN"
  },
  // 'team-work'(팀 근무) 창은 2026-06-12 제거 — 승인 표면은 team-settings(팀 운영)의
  // [승인] 세그먼트(widgets/team-approvals)가 정본. 접근 레벨 동일(MANAGER)해 접근 손실 0.
  "team-settings": {
    label: "\uD300 \uC6B4\uC601",
    minLevel: "MANAGER",
    openToTeamLeader: true
  },
  attendance: {
    label: "\uCD9C\uD1F4\uADFC \uAE30\uB85D",
    minLevel: "INTERN"
  },
  leaderboard: {
    label: "\uB9AC\uB354\uBCF4\uB4DC",
    minLevel: "ASSOCIATE"
  },
  "admin-attendance": {
    label: "\uADFC\uD0DC \uC6B4\uC601",
    minLevel: "MANAGER",
    requiresPermission: { resource: "attendance", action: "manage" }
  },
  chat: {
    label: "\uCC44\uD305",
    minLevel: "INTERN"
  },
  "hr-management": {
    label: "\uC778\uC0AC/\uC870\uC9C1 \uC6B4\uC601",
    capability: "HR_MANAGEMENT"
  },
  /*
      정산 실행자 큐 — 피플팀 1팀만 여는 창이다.
  
      `HR_MANAGEMENT` 를 재사용하지 않는다. 그 capability 는 피플팀과 **개발팀** 둘이 갖고 있어
      (QE-10b §2 실측) 인사 창 안 세그먼트로 넣으면 개발팀에도 전사 정산 큐가 열린다. 부여 대상을
      "피플팀 1팀"으로 못박은 설계(§3)가 화면에서 그대로 깨지는 자리다.
    */
  "campaign-settlement": {
    label: "\uAE00\uB85C\uBC8C \uC815\uC0B0",
    capability: "CAMPAIGN_SETTLEMENT"
  },
  // 국내 정산 큐 — 모든 팀의 국내 캠페인 정산을 3팀장이 처리한다 (fact #2902). 위 창과
  // capability 를 가른다: 피플팀은 해외만, 국내 실행자는 국내만 본다.
  "domestic-settlement": {
    label: "\uAD6D\uB0B4 \uC815\uC0B0",
    capability: "DOMESTIC_CAMPAIGN_SETTLEMENT"
  },
  "system-admin": {
    label: "\uC2DC\uC2A4\uD15C \uC6B4\uC601",
    capability: "SYSTEM_ADMIN"
  },
  finance: {
    label: "\uD3EC\uC778\uD2B8 \uC815\uC0B0",
    minLevel: "EXECUTIVE",
    capability: "FINANCE"
  },
  "bug-reports": {
    label: "\uC81C\uBCF4 \xB7 \uC694\uCCAD",
    minLevel: "INTERN"
  },
  // 사내 위키 — 누구나 읽고 고친다(설계 docs/design/axiom-wiki.md ③).
  wiki: {
    label: "\uC704\uD0A4",
    minLevel: "INTERN"
  },
  today: {
    label: "\uC624\uB298",
    minLevel: "INTERN"
  },
  // 레벨로 좁히지 않는다 — 백엔드가 이미 참여팀으로 좁힌다(`scenario.service` 의
  // `findAll` where절 + `assertCanReadScenario`). 레벨 게이트는 그 위에 얹힌 두 번째
  // 겹이었고, `scenario.md §8.3`("진입은 참여팀 전원")과 어긋난 채 참여팀 팀원에게서
  // 자기 캠페인을 감췄다. 참여 시나리오가 없는 사람은 창을 열어도 빈 목록을 본다.
  scenarios: {
    label: "\uC2DC\uB098\uB9AC\uC624",
    minLevel: "INTERN"
  },
  "advertiser-console": {
    label: "\uC131\uC88C",
    minLevel: "MANAGER",
    requiresPermission: { resource: "advertiser", action: "read" }
  },
  // scenarios 와 같은 이유로 레벨을 올리지 않는다 — 경계는 창이 아니라 서비스가 진다
  // (`creator-relation.service` 가 뷰어 단면별로 행을 좁히고, 못 볼 행은 404 로 막는다).
  // 레벨 게이트를 얹으면 담당인데도 자기 카드가 안 보이는 사람이 생긴다.
  "creator-relations": {
    label: "\uD06C\uB9AC\uC5D0\uC774\uD130 \uAD00\uACC4",
    minLevel: "INTERN"
  },
  // 명단 내려받기 창 — docs/design/ia/creator-lists-ia.md §1. 시딩 팀 팀장 자격이 있는 사람에게만
  // 보인다. 직급은 묻지 않는다 — 자격이 있으면 열고, 없으면 임원도 막힌다.
  "creator-lists": {
    label: "\uBA85\uB2E8 \uB0B4\uB824\uBC1B\uAE30",
    minLevel: "INTERN",
    requiresEligibility: "creator-list"
  },
  // 사용량 대시보드 — 대표와 개발팀이 같은 표를 본다(Q03 ②, 이강주 2026-10-30).
  // `DEV` capability 만 건다: 별도 자격도 역할 요건도 두지 않으므로 EXECUTIVE 전권 단락을 그대로 타서
  // **대표가 아닌 EXECUTIVE 에게도 열린다**. 서버 가드(`canViewUsageDashboard`)가 같은 경계를 진다.
  "usage-dashboard": {
    label: "\uC0AC\uC6A9\uB7C9",
    capability: "DEV"
  },
  // 몫을 받는 사람은 부서가 아니라 단계를 옮긴 기록으로 정해져 부서로 자를 수 없어 권한은 전원에게 둔다.
  // 피처 플래그 'campaign-bonus'(hidden)로 dev·베타 팀을 포함해 숨긴다(이강주 2026-11-02).
  // 팀 결산의 경계는 서버가 진다(`campaign-manager-bonus-query.service`), 창은 빈 상태 한 문장을 보일 뿐이다.
  "campaign-bonus": {
    label: "\uCEA0\uD398\uC778 \uC131\uACFC\uAE09",
    minLevel: "INTERN"
  },
  "ceo-console": {
    label: "\uB300\uD45C \uCF58\uC194",
    // D-CEO-CAP (IA 2026-06-04): CEO_CONSOLE capability 게이트 폐기.
    // 2026-08-28: EXECUTIVE 단일 → **EXECUTIVE ∧ ADMIN**. 비대표 EXECUTIVE 는 창 자체가
    // 목록에 뜨지 않는다. `cross-cutting-constraints.md` §1.1 이 이미 "대표 계정은 ADMIN
    // role 보존" 을 정책으로 두고 비대표 EXECUTIVE 의 재무 조회를 의도적으로 막고 있었는데,
    // 창 게이트만 위계 단일이라 조회는 막히고 창은 열리는 상태였다.
    minLevel: "EXECUTIVE",
    requiresRole: "ADMIN"
  },
  lounge: {
    label: "\uB77C\uC6B4\uC9C0",
    minLevel: "INTERN"
  }
};
var VIEW_API_PERMISSION = {
  quests: null,
  "world-quests": null,
  "dev-quests": null,
  "work-log": null,
  "team-settings": null,
  attendance: null,
  leaderboard: null,
  "admin-attendance": { resource: "attendance", action: "manage" },
  chat: null,
  "hr-management": { resource: "user", action: "read" },
  "campaign-settlement": { resource: "scenario", action: "settlement_read" },
  "domestic-settlement": { resource: "scenario", action: "settlement_read" },
  "system-admin": { resource: "attendance", action: "manage" },
  finance: null,
  "bug-reports": { resource: "bug-report", action: "read" },
  wiki: { resource: "wiki", action: "read" },
  today: null,
  scenarios: null,
  "advertiser-console": { resource: "advertiser", action: "read" },
  "creator-relations": { resource: "scenario", action: "read" },
  "creator-lists": null,
  "usage-dashboard": null,
  "campaign-bonus": { resource: "scenario", action: "read" },
  "ceo-console": null,
  lounge: null
};
var APP_VIEWS = Object.keys(APP_VIEW_REGISTRY);
function isAppView(value) {
  return typeof value === "string" && value in APP_VIEW_REGISTRY;
}
function getAppViewMeta(view) {
  return APP_VIEW_REGISTRY[view];
}
function getAppViewLabel(view) {
  return APP_VIEW_REGISTRY[view].label;
}
function canAccessView(level, view, roles, grants) {
  const meta = getAppViewMeta(view);
  if (meta.requiresEligibility && !(grants?.eligibilities ?? []).includes(meta.requiresEligibility))
    return false;
  if (meta.requiresRole && !(roles ?? []).includes(meta.requiresRole)) return false;
  if (meta.requiresPermission && !hasPermission(
    getPermissionsForUser(roles ?? [], grants?.teamRole, grants?.capabilities ?? []),
    meta.requiresPermission
  ))
    return false;
  if (level === "EXECUTIVE")
    return !meta.capability || holdsViewApiPermission(view, roles, grants?.teamRole, grants?.capabilities ?? []);
  if (meta.openToTeamLeader && (grants?.teamRole === "LEADER" || grants?.leadsTeam)) return true;
  if (!meta.minLevel) return false;
  return LEVEL_ORDER[level] >= LEVEL_ORDER[meta.minLevel];
}
function getAccessibleViews(level, roles, grants) {
  return APP_VIEWS.filter((v) => canAccessView(level, v, roles, grants));
}
function getCapabilityViews(capabilities) {
  return APP_VIEWS.filter((view) => {
    const meta = getAppViewMeta(view);
    if (meta.requiresRole || meta.requiresPermission || meta.requiresEligibility) return false;
    return meta.capability ? capabilities.includes(meta.capability) : false;
  });
}
function getAllAccessibleViews(level, teamCapabilities, roles, teamRole, eligibilities, leadsTeam = false) {
  const levelViews = getAccessibleViews(level, roles, {
    teamRole,
    leadsTeam,
    capabilities: teamCapabilities,
    eligibilities
  });
  const capViews = getCapabilityViews(teamCapabilities).filter(
    (v) => holdsViewApiPermission(v, roles, teamRole, teamCapabilities)
  );
  return [.../* @__PURE__ */ new Set([...levelViews, ...capViews])];
}
function isCapabilityView(view) {
  return Boolean(getAppViewMeta(view).capability);
}
function canAccessViewFull(level, teamCapabilities, view, roles, teamRole, eligibilities) {
  if (canAccessView(level, view, roles, { teamRole, capabilities: teamCapabilities, eligibilities }))
    return true;
  return getCapabilityViews(teamCapabilities).includes(view) && holdsViewApiPermission(view, roles, teamRole, teamCapabilities);
}
function holdsViewApiPermission(view, roles, teamRole, capabilities) {
  const required = VIEW_API_PERMISSION[view];
  return !required || hasPermission(getPermissionsForUser(roles ?? [], teamRole, capabilities ?? []), required);
}

// src/domain/knowledge/contracts.ts
import { z as z3 } from "zod";
var KNOWLEDGE_SOURCE_MEMBER_ROLES = ["INDEX_OPERATOR", "POLICY_APPROVER"];
var KNOWLEDGE_AUTHORITY_STATUSES = ["REFERENCE", "OFFICIAL", "RETIRED"];
var KNOWLEDGE_INDEX_STATES = ["BUILDING", "READY", "FAILED", "PURGED"];
var KNOWLEDGE_INDEXING_STATUSES = [
  "NOT_INDEXED",
  "QUEUED",
  "INDEXING",
  "RETRY_WAIT",
  "READY",
  "FAILED",
  "UNSUPPORTED"
];
var KNOWLEDGE_DOCUMENT_AVAILABILITIES = ["AVAILABLE", "UPDATING", "UNAVAILABLE"];
var KNOWLEDGE_SYNC_JOB_STATES = [
  "PENDING",
  "RUNNING",
  "RETRY_WAIT",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED"
];
var KNOWLEDGE_INITIAL_EXTRACTOR_VERSION = "google-drive-v1";
var KNOWLEDGE_INITIAL_CHUNKER_VERSION = "paragraph-v1";
var KnowledgeIndexConfigSchema = z3.object({
  extractorVersion: z3.string().trim().min(1).max(100),
  chunkerVersion: z3.string().trim().min(1).max(100),
  embeddingProvider: z3.literal("OPENAI"),
  embeddingModel: z3.string().trim().min(1).max(200),
  embeddingDimensions: z3.number().int().positive().max(3072),
  distanceMetric: z3.literal("COSINE")
}).strict();
var SearchKnowledgeQuerySchema = z3.object({
  query: z3.string().trim().min(1).max(2e3),
  limit: z3.number().int().min(1).max(10).default(5)
}).strict();
var GetKnowledgeDocumentQuerySchema = z3.object({
  documentId: z3.uuid(),
  revisionId: z3.uuid().optional(),
  cursor: z3.string().min(1).max(2e3).optional(),
  limit: z3.number().int().min(1).max(20).default(10)
}).strict();
var CreateKnowledgeSourceSchema = z3.object({
  workspaceId: z3.string().trim().min(1).max(200),
  workspaceDomain: z3.string().trim().min(1).max(253),
  name: z3.string().trim().min(1).max(200),
  allowedDriveIds: z3.array(z3.string().trim().min(1).max(200)),
  allowedFileIds: z3.array(z3.string().trim().min(1).max(200)),
  indexConfig: KnowledgeIndexConfigSchema
}).strict();
var UpdateKnowledgeSourceSchema = CreateKnowledgeSourceSchema.pick({
  name: true,
  allowedDriveIds: true,
  allowedFileIds: true,
  indexConfig: true
}).extend({ enabled: z3.boolean() }).partial().strict();
var KnowledgeSourceMemberSchema = z3.object({
  userId: z3.uuid(),
  role: z3.enum(KNOWLEDGE_SOURCE_MEMBER_ROLES)
}).strict();
var KnowledgeDateTimeSchema = Iso8601Schema.and(z3.iso.datetime({ offset: true }));
var CreateKnowledgeAuthorityDecisionSchema = z3.object({
  revisionId: z3.uuid(),
  expectedSequence: z3.number().int().nonnegative(),
  status: z3.enum(KNOWLEDGE_AUTHORITY_STATUSES),
  policyKey: z3.string().trim().min(1).max(200).optional(),
  appliesTo: z3.object({ kind: z3.literal("COMPANY") }).strict(),
  effectiveAt: KnowledgeDateTimeSchema,
  validUntil: KnowledgeDateTimeSchema.optional(),
  supersedesRevisionId: z3.uuid().optional(),
  reason: z3.string().trim().min(1).max(2e3)
}).strict().superRefine((value, ctx) => {
  if (value.status === "OFFICIAL" && !value.policyKey) {
    ctx.addIssue({
      code: "custom",
      path: ["policyKey"],
      message: "\uACF5\uC2DD \uC9C0\uC815\uC5D0\uB294 \uC815\uCC45 \uD0A4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4"
    });
  }
  if (value.validUntil && Date.parse(value.validUntil) <= Date.parse(value.effectiveAt)) {
    ctx.addIssue({
      code: "custom",
      path: ["validUntil"],
      message: "\uC885\uB8CC \uC2DC\uAC01\uC740 \uC2DC\uD589 \uC2DC\uAC01\uBCF4\uB2E4 \uB2A6\uC5B4\uC57C \uD569\uB2C8\uB2E4"
    });
  }
});

// src/domain/club/duration.ts
var CLUB_DURATION_CAP_MS = 60 * 60 * 60 * 1e3;
function calculateClubDuration(segments) {
  return segments.filter((s) => s.sourceType === SegmentSourceType.CLUB_FEED && s.duration !== null).reduce((total, s) => total + (s.duration ?? 0), 0);
}
function checkClubDurationCap(currentMs, addMs) {
  const remaining = Math.max(0, CLUB_DURATION_CAP_MS - currentMs);
  const total = currentMs + addMs;
  const exceeded = Math.max(0, total - CLUB_DURATION_CAP_MS);
  return {
    withinCap: total <= CLUB_DURATION_CAP_MS,
    remaining,
    exceeded
  };
}

// src/domain/club/feed.ts
function approveFeed(feedId, attendees, attendanceByUser, startAt, endAt) {
  const duration = Math.max(0, new Date(endAt).getTime() - new Date(startAt).getTime());
  const segmentsToInsert = [];
  const skipped = [];
  for (const userId of attendees) {
    const attendanceId = attendanceByUser.get(userId);
    if (!attendanceId) {
      skipped.push(userId);
      continue;
    }
    segmentsToInsert.push({
      attendanceId,
      userId,
      startAt,
      endAt,
      duration,
      sourceType: SegmentSourceType.CLUB_FEED,
      sourceId: feedId
    });
  }
  return { segmentsToInsert, skipped };
}

// src/domain/club/values.ts
var ClubPollStatus = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  CONFIRMED: "CONFIRMED"
};

// src/domain/club/poll.ts
function confirmPoll(poll, optionId, actor) {
  if (!actor.isLeader) {
    throw new Error(`Cannot confirm poll ${poll.id}: actor ${actor.userId} is not the club leader`);
  }
  if (optionId === "") {
    throw new Error(`Cannot confirm poll ${poll.id}: optionId is required`);
  }
  assertStatus(
    "club poll",
    poll.id,
    poll.status,
    [ClubPollStatus.OPEN, ClubPollStatus.CLOSED],
    "confirm"
  );
  return {
    ...poll,
    status: ClubPollStatus.CONFIRMED,
    confirmedOptionId: optionId
  };
}

// src/domain/club/tint.ts
var CLUB_TINT_PALETTE = [
  "#E8A04A",
  "#7BC4A4",
  "#C28A6E",
  "#9F7BB5",
  "#5C8DBC",
  "#D87E5C",
  "#6BAE8A",
  "#B58A50"
];
function hashString(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash >>> 0;
}
function hashToAvatarTint(userId) {
  const idx = hashString(userId) % CLUB_TINT_PALETTE.length;
  return CLUB_TINT_PALETTE[idx];
}
function memoryImageTint(feedId) {
  const hash = hashString(feedId);
  const len = CLUB_TINT_PALETTE.length;
  const startIdx = hash % len;
  const offset = 1 + Math.floor(hash / len) % (len - 1);
  const endIdx = (startIdx + offset) % len;
  return `linear-gradient(135deg, ${CLUB_TINT_PALETTE[startIdx]} 0%, ${CLUB_TINT_PALETTE[endIdx]} 100%)`;
}

// src/domain/club/code.ts
var CHOSEONG_ROMAN = [
  "G",
  // ㄱ
  "G",
  // ㄲ
  "N",
  // ㄴ
  "D",
  // ㄷ
  "D",
  // ㄸ
  "R",
  // ㄹ
  "M",
  // ㅁ
  "B",
  // ㅂ
  "B",
  // ㅃ
  "S",
  // ㅅ
  "S",
  // ㅆ
  "O",
  // ㅇ
  "J",
  // ㅈ
  "J",
  // ㅉ
  "C",
  // ㅊ
  "K",
  // ㅋ
  "T",
  // ㅌ
  "P",
  // ㅍ
  "H"
  // ㅎ
];
var HANGUL_BASE = 44032;
var HANGUL_END = 55203;
var CHOSEONG_COUNT = 21 * 28;
function choseongRoman(ch) {
  const code = ch.codePointAt(0);
  if (code === void 0 || code < HANGUL_BASE || code > HANGUL_END) return null;
  const choseongIndex = Math.floor((code - HANGUL_BASE) / CHOSEONG_COUNT);
  return CHOSEONG_ROMAN[choseongIndex] ?? null;
}
function clubCodePrefix(clubName) {
  const ascii = clubName.replace(/[^A-Za-z]/g, "");
  let prefix = "";
  if (ascii.length > 0) {
    prefix = ascii.slice(0, 3).toUpperCase();
  } else {
    for (const ch of clubName) {
      const roman = choseongRoman(ch);
      if (roman) prefix += roman;
      if (prefix.length >= 3) break;
    }
  }
  if (prefix.length === 0) prefix = "CLB";
  return (prefix + "XXX").slice(0, 3);
}
function generateClubCode(clubName, sequence) {
  const prefix = clubCodePrefix(clubName);
  const padded = String(Math.max(1, Math.trunc(sequence))).padStart(2, "0");
  return `${prefix}-${padded}`;
}

// src/domain/club/aggregate.ts
import { format } from "date-fns";
function clubDDay(deadlineIso, now = /* @__PURE__ */ new Date()) {
  if (!deadlineIso) return "";
  const deadlineMidnight = kstStartOfDay(new Date(deadlineIso)).getTime();
  const todayMidnight = kstStartOfDay(now).getTime();
  const days = Math.round((deadlineMidnight - todayMidnight) / 864e5);
  if (days === 0) return "D-DAY";
  if (days > 0) return `D-${days}`;
  return `D+${-days}`;
}
function clubDeadlineLabel(deadlineIso) {
  if (!deadlineIso) return "";
  return format(kstDate(new Date(deadlineIso)), "M/d HH:mm");
}
function clubDateLabel(dateIso) {
  if (!dateIso) return "";
  return format(kstDate(new Date(dateIso)), "M/dd");
}
function assembleHeroPoll(poll, options, votes, totalMembers, now = /* @__PURE__ */ new Date(), viewerUserId = null) {
  const countByOption = /* @__PURE__ */ new Map();
  for (const v of votes) {
    countByOption.set(v.optionId, (countByOption.get(v.optionId) ?? 0) + 1);
  }
  const maxCount = options.reduce((max, o) => Math.max(max, countByOption.get(o.id) ?? 0), 0);
  const optionResponses = options.map((o) => {
    const voteCount = countByOption.get(o.id) ?? 0;
    return {
      id: o.id,
      label: o.content,
      voteCount,
      // 전원 0표(maxCount===0)면 선두 없음 → 전부 false.
      isLeading: maxCount > 0 && voteCount === maxCount
    };
  });
  const participantCount = new Set(votes.map((v) => v.userId)).size;
  const myVotedOptionId = viewerUserId == null ? null : votes.find((v) => v.userId === viewerUserId)?.optionId ?? null;
  const response = {
    id: poll.id,
    title: poll.title,
    status: poll.status,
    confirmedOptionId: poll.confirmedOptionId,
    description: poll.description ?? null,
    dDay: clubDDay(poll.deadline, now),
    deadlineLabel: clubDeadlineLabel(poll.deadline),
    participantCount,
    totalMembers,
    myVotedOptionId,
    options: optionResponses
  };
  if (poll.location) response.location = poll.location;
  if (poll.activityNote) response.activityNote = poll.activityNote;
  return response;
}

// src/domain/club/index.ts
var ABSENCE_OPTION_CONTENT = "\uBD88\uCC38";
var ClubFeedStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};

// src/domain/flex/weekly-work-gate.ts
import { isWeekend, startOfWeek, format as format2 } from "date-fns";
var FLEX_WEEKLY_WORK_THRESHOLD_MS = 40 * 36e5;
function qualifiesForFlexWeeklyWork(attendances, isHolidayDate = () => false) {
  const workMsByWeek = /* @__PURE__ */ new Map();
  for (const attendance of attendances) {
    if (attendance.status === "ABSENT") continue;
    const clockIn = attendance.clockInAt instanceof Date ? attendance.clockInAt : new Date(attendance.clockInAt);
    const kstClockIn = kstDate(clockIn);
    if (isWeekend(kstClockIn)) continue;
    if (isHolidayDate(toKstDateString(clockIn))) continue;
    const weekKey = format2(startOfWeek(kstClockIn, { weekStartsOn: 1 }), "yyyy-MM-dd");
    let dayMs = 0;
    for (const seg of attendance.segments) {
      if (seg.type === "WORK" && seg.duration != null) dayMs += seg.duration;
    }
    workMsByWeek.set(weekKey, (workMsByWeek.get(weekKey) ?? 0) + dayMs);
  }
  for (const ms of workMsByWeek.values()) {
    if (ms > FLEX_WEEKLY_WORK_THRESHOLD_MS) return true;
  }
  return false;
}

// src/domain/attendance-anomaly/detect.ts
var MUST_FIX_SCORE = 5;
var INFO_SCORE = 1;
var MS_30_MIN = 30 * 6e4;
var MS_20_HOUR = 20 * 60 * 6e4;
var MS_24_HOUR = 24 * 60 * 6e4;
var MS_30_HOUR = 30 * 60 * 6e4;
var MS_46_8_HOUR = 46.8 * 60 * 6e4;
var MS_52_HOUR = 52 * 60 * 6e4;
var ANOMALY_LABELS = {
  // mustFix
  A: {
    label: "ABSENT \uD45C\uC2DC\uC778\uB370 \uC2E4\uC81C \uADFC\uBB34 \uAE30\uB85D \uC788\uC74C",
    shortLabel: "\uACB0\uADFC\xB7\uADFC\uBB34 \uC911\uBCF5",
    severity: "mustFix"
  },
  B: { label: "ABSENT + \uC2B9\uC778 \uD734\uAC00 \uC911\uBCF5", shortLabel: "\uACB0\uADFC\xB7\uD734\uAC00 \uC911\uBCF5", severity: "mustFix" },
  C: { label: "\uD1F4\uADFC \uADFC\uBB34 \uAD6C\uAC04 \uB204\uB77D/0h", shortLabel: "\uAD6C\uAC04 \uB204\uB77D", severity: "mustFix" },
  D: { label: "\uCD9C\uADFC \uC2DC\uAC04 \uBD88\uBA85", shortLabel: "\uCD9C\uADFC \uBD88\uBA85", severity: "mustFix" },
  F: { label: "\uD1F4\uADFC \uC2DC\uAC04\uC774 \uCD9C\uADFC\uBCF4\uB2E4 \uBE60\uB984", shortLabel: "\uD1F4\uADFC \uC5ED\uC804", severity: "mustFix" },
  G: {
    label: "\uADFC\uBB34 \uAD6C\uAC04\uC774 \uCD9C\uD1F4\uADFC \uBC94\uC704\uB97C \uBC97\uC5B4\uB098\uAC70\uB098 \uACB9\uCE68",
    shortLabel: "\uAD6C\uAC04 \uC774\uD0C8\xB7\uACB9\uCE68",
    severity: "mustFix"
  },
  H: { label: "CLOCKED_IN 30\uC2DC\uAC04+ \uC815\uC9C0 (\uD1F4\uADFC \uB204\uB77D)", shortLabel: "\uD1F4\uADFC \uB204\uB77D", severity: "mustFix" },
  I: {
    label: "ON_BREAK 24\uC2DC\uAC04+ \uC815\uC9C0 (\uD734\uAC8C \uC7AC\uAC1C \uB204\uB77D)",
    shortLabel: "\uD734\uAC8C \uC7AC\uAC1C \uB204\uB77D",
    severity: "mustFix"
  },
  J: { label: "\uAC19\uC740 \uB0A0 \uCD9C\uADFC \uAE30\uB85D 2\uAC74 \uC774\uC0C1", shortLabel: "\uCD9C\uADFC \uC911\uBCF5", severity: "mustFix" },
  K: { label: "CLOCKED_OUT + \uC2B9\uC778 \uD734\uAC00 \uC911\uBCF5", shortLabel: "\uADFC\uBB34\xB7\uD734\uAC00 \uC911\uBCF5", severity: "mustFix" },
  L: { label: "\uADFC\uBB34 \uAD6C\uAC04 \uBBF8\uB2EB\uD798 (endAt null)", shortLabel: "\uAD6C\uAC04 \uBBF8\uC885\uB8CC", severity: "mustFix" },
  N: { label: "\uADFC\uBB34 \uAD6C\uAC04 duration \uC74C\uC218", shortLabel: "\uC74C\uC218 \uADFC\uBB34", severity: "mustFix" },
  O: { label: "ABSENT\uC778\uB370 \uADFC\uBB34 \uAD6C\uAC04 \uC874\uC7AC", shortLabel: "\uACB0\uADFC\xB7\uAD6C\uAC04 \uC911\uBCF5", severity: "mustFix" },
  // info
  E: { label: "\uB2E8\uC77C \uC77C\uC790 20h \uC774\uC0C1 (\uD655\uC778 \uD544\uC694)", shortLabel: "20h \uC774\uC0C1", severity: "info" },
  M: { label: "\uBBF8\uB798 \uB0A0\uC9DC \uAE30\uB85D (\uC9D1\uACC4 \uC601\uD5A5 \uC5C6\uC74C)", shortLabel: "\uBBF8\uB798 \uB0A0\uC9DC", severity: "info" },
  W1: { label: "\uC8FC\uAC04 \uB204\uC801 46.8\uC2DC\uAC04 \uC774\uC0C1 (52h\uC758 90%)", shortLabel: "\uC8FC 52h \uC784\uBC15", severity: "info" },
  W2: { label: "\uC8FC\uAC04 \uB204\uC801 52\uC2DC\uAC04 \uCD08\uACFC", shortLabel: "\uC8FC 52h \uCD08\uACFC", severity: "info" }
};
function detectSingleRecord(record, now = /* @__PURE__ */ new Date(), approvedLeaveDates) {
  if (approvedLeaveDates) {
    const date = record.date ?? (record.clockInAt ? toKstDateString(new Date(record.clockInAt)) : null);
    if (date && approvedLeaveDates.has(date)) {
      return [];
    }
  }
  const codes = [];
  if (record.status !== "ABSENT" && record.clockInAt && record.clockOutAt && record.clockOutAt < record.clockInAt) {
    codes.push({ code: "F", severity: "mustFix" });
  }
  const hasInactiveStatus = record.status === "CLOCKED_OUT" || record.status === "ABSENT";
  if (hasInactiveStatus && record.segments.some((s) => s.endAt === null)) {
    codes.push({ code: "L", severity: "mustFix" });
  }
  if (record.segments.some((s) => s.duration !== null && s.duration < 0)) {
    codes.push({ code: "N", severity: "mustFix" });
  }
  if (record.status === "ABSENT" && record.segments.length > 0) {
    codes.push({ code: "O", severity: "mustFix" });
  }
  if (record.status !== "ABSENT" && record.clockInUnknown === true && !record.clockInUnknownAckAt) {
    codes.push({ code: "D", severity: "mustFix" });
  }
  const workMs = sumWorkMs(record.segments);
  if (workMs > MS_20_HOUR) {
    codes.push({ code: "E", severity: "info" });
  }
  if (record.status === "CLOCKED_OUT" && workMs === 0) {
    codes.push({ code: "C", severity: "mustFix" });
  }
  if (record.clockInAt && record.clockOutAt && record.segments.length > 0 && record.segments.every((s) => s.endAt !== null)) {
    const clockInMs = new Date(record.clockInAt).getTime();
    const clockOutMs = new Date(record.clockOutAt).getTime();
    const intervals = record.segments.map((s) => ({
      start: new Date(s.startAt).getTime(),
      end: new Date(s.endAt).getTime()
    })).sort((a, b) => a.start - b.start);
    const outOfBounds = intervals.some(
      (iv) => iv.start < clockInMs - MS_30_MIN || iv.end > clockOutMs + MS_30_MIN
    );
    let overlap = false;
    let maxEnd = -Infinity;
    for (const iv of intervals) {
      if (maxEnd !== -Infinity && iv.start < maxEnd - MS_30_MIN) {
        overlap = true;
        break;
      }
      if (iv.end > maxEnd) maxEnd = iv.end;
    }
    if (outOfBounds || overlap) {
      codes.push({ code: "G", severity: "mustFix" });
    }
  }
  if (record.status === "CLOCKED_IN" && record.clockInAt && now.getTime() - new Date(record.clockInAt).getTime() > MS_30_HOUR) {
    codes.push({ code: "H", severity: "mustFix" });
  }
  if (record.status === "ON_BREAK") {
    const lastBreak = [...record.segments].reverse().find((s) => s.type === "BREAK" && s.endAt === null);
    if (lastBreak && now.getTime() - new Date(lastBreak.startAt).getTime() > MS_24_HOUR) {
      codes.push({ code: "I", severity: "mustFix" });
    }
  }
  if (record.clockInAt && new Date(record.clockInAt).getTime() > now.getTime()) {
    codes.push({ code: "M", severity: "info" });
  }
  return codes;
}
function detectMultiRecord(records) {
  const codes = [];
  const byDate = /* @__PURE__ */ new Map();
  for (const r of records) {
    const date = r.date ?? (r.clockInAt ? toKstDateString(new Date(r.clockInAt)) : null);
    if (!date) continue;
    byDate.set(date, (byDate.get(date) ?? 0) + 1);
  }
  if ([...byDate.values()].some((count) => count >= 2)) {
    codes.push({ code: "J", severity: "mustFix" });
  }
  const weekly = /* @__PURE__ */ new Map();
  for (const r of records) {
    const dateStr = r.date ?? (r.clockInAt ? toKstDateString(new Date(r.clockInAt)) : null);
    if (!dateStr) continue;
    const weekKey = isoWeekKey(dateStr);
    const cur = weekly.get(weekKey);
    if (cur) cur.ms += sumWorkMs(r.segments);
    else weekly.set(weekKey, { ms: sumWorkMs(r.segments), sampleDate: dateStr });
  }
  let peak = null;
  for (const w of weekly.values()) {
    if (!peak || w.ms > peak.ms) peak = w;
  }
  if (peak && peak.ms > MS_52_HOUR) {
    const { start, end } = isoWeekRange(peak.sampleDate);
    codes.push({ code: "W2", severity: "info", week: { start, end, workMs: peak.ms } });
  } else if (peak && peak.ms >= MS_46_8_HOUR) {
    const { start, end } = isoWeekRange(peak.sampleDate);
    codes.push({ code: "W1", severity: "info", week: { start, end, workMs: peak.ms } });
  }
  return codes;
}
function scoreOf(codes) {
  let score = 0;
  for (const c of codes) {
    score += c.severity === "mustFix" ? MUST_FIX_SCORE : INFO_SCORE;
  }
  return score;
}
function sumWorkMs(segments) {
  return segments.filter((s) => s.type === "WORK" && s.duration != null).reduce((acc, s) => acc + (s.duration ?? 0), 0);
}
function isoWeekRange(dateYmd) {
  const [y, m, d] = dateYmd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (dt.getUTCDay() + 6) % 7;
  const monday = new Date(dt);
  monday.setUTCDate(dt.getUTCDate() - dayNum);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return {
    start: toUtcDateString(monday),
    end: toUtcDateString(sunday)
  };
}
function isoWeekKey(dateYmd) {
  const [y, m, d] = dateYmd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(dt.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const weekNum = Math.round((dt.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 6e4)) + 1;
  return `${dt.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

// src/domain/quest-consistency/rules.ts
function assertRuleWellFormed(rule) {
  if (rule.normSource.trim() === "") {
    throw new Error(`Rule ${rule.id}: normSource is required`);
  }
  if (rule.enforcedBy.trim() === "") {
    throw new Error(`Rule ${rule.id}: enforcedBy is required`);
  }
  if (rule.blindSpot.trim() === "") {
    throw new Error(`Rule ${rule.id}: blindSpot is required`);
  }
  if (rule.basis === "observation" && rule.kind === "violation") {
    throw new Error(
      `Rule ${rule.id}: observation-based rule cannot be a violation \u2014 \uAD00\uCC30\uC744 \uADDC\uBC94\uC73C\uB85C \uC2B9\uACA9\uC2DC\uD0AC \uC218 \uC5C6\uB2E4`
    );
  }
}
var CONSISTENCY_RULES = [
  {
    id: "INV-01",
    name: "\uC885\uACB0\uB41C \uC120\uD589\uC774 \uD6C4\uD589\uC744 \uC601\uAD6C \uB300\uAE30\uB85C \uAC00\uB460",
    kind: "decision-needed",
    basis: "decision",
    normSource: "docs/design/quest-dag.md \xA71.2 B2 \u2014 \uC120\uD589\uC774 FAILED\xB7ARCHIVED \uB85C \uB2EB\uD788\uBA74 \uD6C4\uD589\uC758 \uC5F0\uACC4 \uB300\uAE30\uAC00 \uC790\uB3D9\uC73C\uB85C \uD480\uB9AC\uB294 \uACBD\uB85C\uAC00 \uC5C6\uB2E4",
    enforcedBy: "packages/shared/src/domain/quest/quest-chain.ts (\uD574\uC81C \uC220\uC5B4 status === COMPLETED \uD558\uB098\uBFD0)",
    blindSpot: "ARCHIVED \uC120\uD589\uC774 COMPLETED \uB97C \uAC70\uCCD0 \uC654\uB294\uC9C0\uB294 completedAt \uC774 \uC77D\uAE30 API \uC5D0 \uC5C6\uC5B4 \uD310\uC815 \uBD88\uAC00 \u2014 \uADF8 \uAC74\uC740 \uBBF8\uACB0\uC815\uC73C\uB85C \uB0A8\uB294\uB2E4",
    implemented: true,
    ruleVersion: 3
  },
  {
    id: "INV-09",
    name: "\uC720\uB2E8\uAC00 \uD65C\uC131 \uD018\uC2A4\uD2B8\uAC00 \uC608\uC0B0 \uC6D0\uC7A5\uC5D0 \uC548 \uBB36\uC784",
    kind: "violation",
    basis: "decision",
    normSource: 'docs/spec/quest-pricing-policy.md \xA74 \u2014 \uC608\uC0B0\uC740 \uC644\uB8CC \uC2DC\uC810\uC5D0 \uCC28\uAC10\uB418\uACE0 \uD300\uC7A5\uC740 "\uBC1C\uD589 \uB2E8\uAC00 \uCD1D\uD569 / \uC6D4\uC608\uC0B0" \uAC8C\uC774\uC9C0\uB97C \uC0C1\uC2DC \uD655\uC778\uD55C\uB2E4. \uC6D0\uC7A5\uC5D0 \uC548 \uBB36\uC778 \uD018\uC2A4\uD2B8\uB294 \uADF8 \uBD84\uBAA8\uC5D0\uC11C \uD1B5\uC9F8\uB85C \uBE60\uC9C4\uB2E4',
    enforcedBy: "packages/backend/src/modules/budget-ledger/budget-ledger.service.ts (\uC6D0\uC7A5 \uBD80\uCC29 \uD6C5)",
    blindSpot: "\uBD80\uCC29 \uD6C5 \uB3C4\uC785(2026-07-31\xB72026-08-06) \uC774\uC804\uC5D0 \uD65C\uC131\uD654\uB41C \uD018\uC2A4\uD2B8\uB294 \uBC31\uD544\uC774 \uC5C6\uC5B4 \uC804\uBD80 null \uC774\uB2E4. \uBC31\uD544\uD558\uBA74 \uC548 \uB41C\uB2E4 \u2014 \uADC0\uC18D\uC740 \uD65C\uC131\uD654 \uC2DC\uC810\uC5D0 \uACE0\uC815\uB418\uBBC0\uB85C \uC0AC\uD6C4 \uBD80\uCC29\uC740 \uC798\uBABB\uB41C \uAE30\uAC04\uC758 \uC6D0\uC7A5\uC5D0 \uC608\uC57D\uC744 \uB9CC\uB4E0\uB2E4",
    implemented: true,
    ruleVersion: 1
  },
  {
    id: "INV-22",
    name: "CP \uB2E8\uAC00 \uD569\uC774 \uD018\uC2A4\uD2B8 \uB2E8\uAC00\uC640 \uB2E4\uB984",
    kind: "violation",
    basis: "decision",
    normSource: 'docs/spec/quest-pricing-policy.md \xA73 "\uCCB4\uD06C\uD3EC\uC778\uD2B8 \uB2E8\uAC00\uC758 \uD569\uC774 \uD018\uC2A4\uD2B8 \uB2E8\uAC00\uC640 \uAC19\uC544\uC57C \uD569\uB2C8\uB2E4" \xB7 gbrain 2026-08-14 \uD018\uC2A4\uD2B8 \uBC1C\uD589 \uD45C\uC900 "\uB2E8\uAC00 \uAC8C\uC774\uD2B8(CP \uD569=\uD018\uC2A4\uD2B8 \uB2E8\uAC00 top-down, \uBCF4\uC0C1>0 \u2192 \uB300\uD45C \uC2B9\uC778)"',
    enforcedBy: "packages/shared/src/domain/quest/quest.ts validateCheckpointRewardBalanced \u2014 \uBC1C\uD589 \uC2B9\uC778 \uACBD\uB85C\uC5D0\uC11C \uAC15\uC81C",
    blindSpot: "\uAC8C\uC774\uD2B8 \uB3C4\uC785 \uC774\uC804\uC5D0 \uC2B9\uC778\uB41C \uD018\uC2A4\uD2B8\uB294 \uAC80\uC0AC\uB418\uC9C0 \uC54A\uB294\uB2E4. CP \uB97C \uD558\uB098\uC529 \uBD99\uC774\uB294 \uC911\uAC04 \uC0C1\uD0DC\uC640 \uC2B9\uACA9(0\u2192\uC591\uC218) \uC9C1\uD6C4\uB3C4 \uB300\uC0C1\uC774 \uC544\uB2C8\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-21",
    name: "\uC99D\uBE59 \uC81C\uCD9C \uD6C4 2\uC601\uC5C5\uC77C \uBBF8\uAC80\uC218",
    kind: "violation",
    basis: "decision",
    normSource: "gbrain 2026-08-14 (\uC774\uAC15\uC8FC \uC9C0\uC2DC, \uAC80\uC218 SLA 2\uC601\uC5C5\uC77C \uAE30\uC900 \uAC31\uC2E0) \u2014 \uC2DC\uACC4\uB294 KST \uC601\uC5C5\uC77C \uAE30\uC900\uC774\uACE0 \uB2EC\uB825 48h \uAC00 \uC544\uB2C8\uB2E4. \uAE08\uC694\uC77C \uC81C\uCD9C \uAC74\uC774 \uC8FC\uB9D0\uC5D0 \uB9CC\uAE30\uB418\uB294 \uC65C\uACE1\uC744 \uB9C9\uAE30 \uC704\uD568",
    enforcedBy: "\uC5C6\uC74C \u2014 \uACB0\uC815\uB9CC \uC11C \uC788\uACE0 \uC9D1\uD589 \uCF54\uB4DC\uAC00 \uC544\uC9C1 \uC5C6\uB2E4. \uC601\uC5C5\uC77C \uD310\uC815\uC740 attendance \uB3C4\uBA54\uC778\uC744 \uC7AC\uC0AC\uC6A9\uD55C\uB2E4",
    blindSpot: "\uAC80\uC218\uC790\uAC00 \uC2E4\uC81C\uB85C \uBCFC \uC218 \uC788\uB294 \uD018\uC2A4\uD2B8\uC778\uC9C0(\uAD8C\uD55C \uC740\uB2C9)\uB97C \uBCF4\uC9C0 \uC54A\uC73C\uBBC0\uB85C, \uC544\uBB34\uB3C4 \uAC80\uC218 \uAD8C\uD55C\uC774 \uC5C6\uB294 \uAC74\uAE4C\uC9C0 \uB9CC\uAE30\uB85C \uC13C\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-03",
    name: "\uC804 CP \uC885\uACB0\uC778\uB370 \uD018\uC2A4\uD2B8\uAC00 \uC548 \uB2EB\uD798",
    kind: "violation",
    basis: "decision",
    normSource: "docs/spec/quest-pricing-policy.md \xA74 \u2014 \uC608\uC0B0\uC740 \uC644\uB8CC \uC2DC\uC810\uC5D0 \uCC28\uAC10\uB41C\uB2E4. \uC644\uC8FC\uD55C \uD018\uC2A4\uD2B8\uAC00 ACTIVE \uB85C \uB0A8\uC73C\uBA74 \uADF8 \uCC28\uAC10\uC774 \uC77C\uC5B4\uB098\uC9C0 \uC54A\uC740 \uCC44 \uCC38\uC5EC \uAC00\uB2A5 \uBAA9\uB85D\uC5D0 \uACC4\uC18D \uB72C\uB2E4",
    enforcedBy: "packages/backend/src/modules/quest/distribution.service.ts (\uB9C8\uC9C0\uB9C9 CP \uC815\uC0B0 \uC2DC ACTIVE\u2192LOCKED \uC804\uC774)",
    blindSpot: "CP \uAC00 0 \uAC1C\uC778 \uD018\uC2A4\uD2B8\uB294 \uC9C4\uD589 \uCD95 \uC790\uCCB4\uAC00 \uC815\uC758\uB418\uC9C0 \uC54A\uC544 \uB300\uC0C1\uC774 \uC544\uB2C8\uB2E4. LOCKED \uB294 \uC124\uACC4\uB41C \uB300\uAE30 \uC0C1\uD0DC\uB77C \uC704\uBC18\uC774 \uC544\uB2C8\uB77C \uC7AC\uACE0\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-08",
    name: "\uC2B9\uC778\uB41C CP \uC758 \uBCF4\uC0C1\uC774 \uC9C0\uAE09\uB418\uC9C0 \uC54A\uC74C",
    kind: "violation",
    basis: "decision",
    normSource: "docs/spec/quest-pricing-policy.md \xA74 \u2014 \uAE30\uAC04 \uB0B4 \uD3EC\uC778\uD2B8 \uC801\uB9BD \uB0B4\uC5ED\uC744 \uD569\uC0B0\uD574 \uC9C0\uAE09\uD55C\uB2E4. \uC2B9\uC778\uB41C CP \uC758 \uBCF4\uC0C1\uC774 \uC801\uB9BD\uB418\uC9C0 \uC54A\uC73C\uBA74 \uADF8 \uD569\uC0B0\uC5D0\uC11C \uBE60\uC9C4\uB2E4",
    enforcedBy: "packages/backend/src/modules/quest/distribution.service.ts (CP \uC2B9\uC778 \uC2DC \uBD84\uBC30)",
    blindSpot: 'PointHistory.checkpointId \uC2E0\uC124 \uC774\uC804 \uAD6C\uAC04\uC740 null \uC774 "Quest \uB2E8\uC704 \uBD84\uBC30"\uC640 \uACB8\uC6A9\uC774\uB77C \uC870\uC778\uC0C1 0 \uACFC \uAD6C\uBCC4 \uBD88\uAC00 \u2014 \uADF8 \uAD6C\uAC04\uC740 \uBBF8\uACB0\uC815',
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-13",
    name: "DRAFT \uC99D\uBE59 \uC911\uBCF5\uC73C\uB85C \uC81C\uCD9C \uACBD\uB85C \uBD09\uC1C4",
    kind: "violation",
    basis: "decision",
    normSource: "packages/backend/src/modules/proof/proof.service.ts hasActiveQuestProof \u2014 \uD018\uC2A4\uD2B8 \uB2E8\uC704 \uC99D\uBE59\uC740 \uD65C\uC131 1\uAC74\uC774\uB77C\uB294 \uACC4\uC57D\uC774 \uC774\uBBF8 \uCF54\uB4DC\uC5D0 \uC220\uC5B4\uB85C \uC874\uC7AC\uD55C\uB2E4",
    enforcedBy: "proof.service.ts \uC81C\uCD9C \uC2DC\uC810 1\uACF3 (\uC0DD\uC131 \uC2DC\uC810\uC5D0\uB294 \uAC19\uC740 \uC220\uC5B4\uAC00 \uD638\uCD9C\uB418\uC9C0 \uC54A\uC544 \uB450 \uBC88\uC9F8 \uCD08\uC548\uC774 \uD0DC\uC5B4\uB09C\uB2E4)",
    blindSpot: "\uC774\uBBF8 \uC313\uC778 \uC7AC\uACE0\uB294 \uC0DD\uC131 \uAC00\uB4DC\uB97C \uB123\uC5B4\uB3C4 \uD480\uB9AC\uC9C0 \uC54A\uB294\uB2E4 \u2014 \uC81C\uCD9C \uAC00\uB4DC\uAC00 \uADF8\uB300\uB85C\uB77C \uBCC4\uB3C4 1\uD68C\uC131 \uC815\uB9AC\uAC00 \uD544\uC694\uD558\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-19",
    name: "\uB9CC\uB8CC \uC9D1\uD589\uC774 \uBC00\uB9BC",
    kind: "canary",
    basis: "decision",
    normSource: "packages/backend/src/modules/quest/quest-expiry-cron.service.ts \u2014 \uB9C8\uAC10\uC774 \uC9C0\uB09C ACTIVE \uD018\uC2A4\uD2B8\uB97C FAILED \uB85C \uB9CC\uB4E0\uB2E4\uB294 \uC9D1\uD589 \uADDC\uCE59\uC774 \uCF54\uB4DC\uB85C \uD655\uC815\uB3FC \uC788\uB2E4",
    enforcedBy: "quest-expiry-cron.service.ts",
    blindSpot: "\uC2A4\uC715 \uBBF8\uBC1C\uD654\uC640 \uAC74\uBCC4 \uC2E4\uD328\uAC00 \uAC19\uC740 \uD754\uC801\uC744 \uB0A8\uAE34\uB2E4 \u2014 \uAC74\uC218\uB9CC \uC801\uACE0 \uC6D0\uC778\uC740 \uB2E8\uC815\uD558\uC9C0 \uC54A\uB294\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-16",
    name: "\uC21C\uC11C \uADF8\uB798\uD504\uAC00 DAG \uAC00 \uC544\uB2D8",
    kind: "canary",
    basis: "decision",
    normSource: "docs/design/quest-dag.md \u2014 \uC21C\uD658\uC740 \uC11C\uBC84\uAC00 \uAC70\uBD80\uD55C\uB2E4. \uC874\uC7AC\uD558\uBA74 \uADF8 \uAC70\uBD80\uB97C \uC6B0\uD68C\uD55C \uC4F0\uAE30\uAC00 \uC788\uC5C8\uB2E4\uB294 \uB73B\uC774\uB2E4",
    enforcedBy: "\uC758\uC874 \uAC04\uC120 \uC0DD\uC131 \uC5D4\uB4DC\uD3EC\uC778\uD2B8\uC758 \uC21C\uD658 \uAC80\uC0AC",
    blindSpot: "\uC790\uAE30\uCC38\uC870\uB294 DB \uC81C\uC57D\uC774 \uC5C6\uC5B4 \uD1B5\uACFC\uD558\uACE0, \uC77D\uAE30\uC640 \uC4F0\uAE30\uAC00 \uB2E4\uB978 \uCEE4\uB125\uC158\uC774\uB77C \uB3D9\uC2DC \uC0DD\uC131 \uC2DC \uAC80\uC0AC\uB97C \uBE44\uAEF4\uAC08 \uC218 \uC788\uB2E4",
    implemented: false,
    ruleVersion: 1
  },
  {
    id: "INV-23",
    name: "\uC57D\uC18D \uAE30\uD55C\uC73C\uB85C \uB2E8\uAC00\uB97C \uBD99\uC600\uB294\uB370 \uB9C8\uAC10\uC774 \uC5C6\uC74C",
    kind: "violation",
    basis: "decision",
    normSource: 'docs/spec/quest-pricing-policy.md \xA72 \u2014 \uC6B0\uC120\uC21C\uC704 3\uBB38 \uC911 3\uBC88\uC740 "\uB300\uD45C, \uB2E4\uB978 \uD300, \uAD11\uACE0\uC8FC\uC5D0\uAC8C \uC2DC\uC810\uC744 \uC57D\uC18D\uD588\uB294\uAC00" \uC774\uACE0, \uB2E8\uAC00\uB97C \uBD99\uC77C \uB54C \uADF8 \uBB38\uD56D \uBC88\uD638\uB97C \uBA54\uBAA8\uB85C \uB0A8\uAE34\uB2E4. 3\uBC88\uC73C\uB85C \uAC12\uC744 \uB9E4\uACBC\uB294\uB370 \uB9C8\uAC10\uC774 \uC5C6\uC73C\uBA74 \uC57D\uC18D\uD55C \uC2DC\uC810\uC774 \uC2DC\uC2A4\uD15C \uC5B4\uB514\uC5D0\uB3C4 \uC5C6\uB2E4',
    enforcedBy: "packages/backend/src/modules/quest/quest-consistency.service.ts \u2014 \uC2B9\uC778 \uC774\uB825(QuestApprovalEvent.note)\uC758 \uCD5C\uC2E0 \uBA54\uBAA8\uC5D0\uC11C \uBB38\uD56D \uBC88\uD638\uB97C \uC77D\uC5B4 \uD310\uC815\uD55C\uB2E4. \uBC1C\uD589 \uC2DC\uC810\uC5D0 \uB9C9\uB294 \uC11C\uBC84 \uAC00\uB4DC\uB294 \uC5C6\uB2E4",
    blindSpot: "\uADFC\uAC70 \uBA54\uBAA8\uAC00 \uC5C6\uB294 \uC720\uB2E8\uAC00 \uD018\uC2A4\uD2B8\uB294 \uBB38\uD56D\uC744 \uC54C \uC218 \uC5C6\uC5B4 \uBBF8\uACB0\uC815\uC73C\uB85C \uAC04\uB2E4(2026-09-03 \uC6B4\uC601 \uC2E4\uCE21 66\uAC74 \uC911 14\uAC74). \uBA54\uBAA8\uB97C \uC4F0\uB294 \uD654\uBA74\uC774 \uB300\uD45C \uC218\uC815\xB7\uC2B9\uC778 \uCC3D \uD558\uB098\uBFD0\uC774\uB77C \uD300\uC7A5\uC774 \uCC98\uC74C\uBD80\uD130 \uB2E8\uAC00\uB97C \uBD99\uC5EC \uBC1C\uD589\uD558\uBA74 \uADFC\uAC70\uB97C \uC801\uC744 \uCE78\uC774 \uC5C6\uB2E4. DRAFT\xB7LOCKED \uB294 \uB300\uC0C1\uC774 \uC544\uB2C8\uB2E4 \u2014 \uBC1C\uD589 \uC804\uC774\uAC70\uB098 \uC774\uBBF8 \uC815\uC0B0\uB410\uB2E4",
    implemented: true,
    ruleVersion: 1
  },
  {
    id: "INV-20",
    name: "\uC2B9\uC778 \uB300\uAE30 \uC7AC\uACE0",
    kind: "inventory",
    basis: "observation",
    normSource: 'docs/spec/quest-pricing-policy.md \xA73 \u2014 \uBCF4\uC0C1 0 \uCD08\uACFC \uBC1C\uD589\uC740 \uB300\uD45C \uC2B9\uC778\uC744 \uAC70\uCE5C\uB2E4. \uB2E4\uB9CC "\uBA70\uCE60\uC774\uBA74 \uBC29\uCE58"\uB77C\uB294 \uC784\uACC4\uB294 \uC5B4\uB514\uC5D0\uB3C4 \uACB0\uC815\uB3FC \uC788\uC9C0 \uC54A\uB2E4',
    enforcedBy: "packages/backend/src/modules/quest/quest-query.service.ts (PENDING_APPROVAL \uC744 \uC804 \uC870\uD68C\uC790\uC5D0\uAC8C \uB178\uCD9C)",
    blindSpot: "\uC9C4\uC785 \uC2DC\uAC01\uC744 \uC18C\uC720\uD55C QuestApprovalEvent \uC5D0 \uC77D\uAE30 API \uAC00 \uC5C6\uC5B4 \uCCB4\uB958 \uAE30\uAC04\uC744 \uC7B4 \uC218 \uC5C6\uB2E4. updatedAt \uC740 \uC7AC\uC81C\uCD9C\uC774 \uB9AC\uC14B\uD574 \uBD80\uD638\uAC00 \uB4A4\uC9D1\uD78C\uB2E4",
    implemented: false,
    ruleVersion: 1
  }
];
CONSISTENCY_RULES.forEach(assertRuleWellFormed);
function parsePriorityBasis(note) {
  if (note === null) return null;
  const matched = /우선순위\s*근거\s*:\s*([123])(?![0-9])/.exec(note);
  if (!matched) return null;
  return Number(matched[1]);
}
var ABSORBING_STATUSES = /* @__PURE__ */ new Set(["FAILED", "WITHDRAWN", "ARCHIVED"]);
var UNSETTLED_STATUSES = /* @__PURE__ */ new Set(["DRAFT", "ACTIVE", "LOCKED"]);
function findBlockedByAbsorbedPredecessor(quests) {
  const byId = new Map(quests.map((q) => [q.id, q]));
  const open = [];
  const undecided = [];
  for (const quest of quests) {
    if (!UNSETTLED_STATUSES.has(quest.status)) {
      continue;
    }
    const edgeById = new Map((quest.prerequisiteEdges ?? []).map((edge) => [edge.id, edge]));
    const blockedPredecessors = groupPrerequisites(
      quest.dependsOn.map((id) => ({ id, groupKey: edgeById.get(id)?.groupKey }))
    ).flatMap((group) => {
      if (group[0].groupKey != null && group.some((edge) => {
        const predecessor = byId.get(edge.id);
        return !predecessor || isQuestChainCleared(predecessor) || !ABSORBING_STATUSES.has(predecessor.status);
      }))
        return [];
      return group.map((edge) => edge.id);
    });
    for (const predecessorId of blockedPredecessors) {
      const predecessor = byId.get(predecessorId);
      if (!predecessor || !ABSORBING_STATUSES.has(predecessor.status)) {
        continue;
      }
      if (predecessor.status === "ARCHIVED" && predecessor.completedAt === null) {
        undecided.push({
          ruleId: "INV-01",
          subjectId: quest.id,
          subjectTitle: quest.title,
          note: `\uC120\uD589 ${predecessorId} \uAC00 ARCHIVED \uC778\uB370 \uC885\uACB0 \uACBD\uB85C \uBBF8\uC0C1 (completedAt \uC5C6\uC74C)`
        });
        continue;
      }
      open.push({
        ruleId: "INV-01",
        subjectId: quest.id,
        subjectTitle: quest.title,
        note: `\uC120\uD589 ${predecessorId} \uAC00 ${predecessor.status} \uB85C \uB2EB\uD600 \uC5F0\uACC4 \uB300\uAE30\uAC00 \uD480\uB9AC\uC9C0 \uC54A\uB294\uB2E4`
      });
    }
  }
  return { ruleId: "INV-01", open, undecided, truncated: false };
}
function findQuestsMissingBudgetLedger(quests, ledgerExistsForPeriod) {
  if (!ledgerExistsForPeriod) {
    return { ruleId: "INV-09", open: [], undecided: [], truncated: false };
  }
  const open = [];
  for (const quest of quests) {
    if (quest.status !== "ACTIVE") continue;
    if (quest.rewardPoints <= 0) continue;
    if (quest.budgetLedgerId !== null) continue;
    if (quest.scope === "TEAM" && quest.teamId === null) continue;
    open.push({
      ruleId: "INV-09",
      subjectId: quest.id,
      subjectTitle: quest.title,
      note: `\uC720\uB2E8\uAC00 ${quest.rewardPoints}P \uD65C\uC131 \uD018\uC2A4\uD2B8\uAC00 \uC608\uC0B0 \uC6D0\uC7A5\uC5D0 \uBB36\uC774\uC9C0 \uC54A\uC558\uB2E4`
    });
  }
  return { ruleId: "INV-09", open, undecided: [], truncated: false };
}
function findPricedQuestsWithoutPromisedDeadline(quests) {
  const open = [];
  const undecided = [];
  for (const quest of quests) {
    if (quest.status !== "ACTIVE") continue;
    if (quest.rewardPoints <= 0) continue;
    if (quest.deadline !== null) continue;
    const basis = parsePriorityBasis(quest.priorityBasisNote);
    if (basis === null) {
      undecided.push({
        ruleId: "INV-23",
        subjectId: quest.id,
        subjectTitle: quest.title,
        note: `\uC720\uB2E8\uAC00 ${quest.rewardPoints}P \uC778\uB370 \uC6B0\uC120\uC21C\uC704 \uADFC\uAC70 \uBB38\uD56D\uC774 \uC2B9\uC778 \uC774\uB825\uC5D0 \uC5C6\uC5B4 \uD310\uC815\uD560 \uC218 \uC5C6\uB2E4`
      });
      continue;
    }
    if (basis !== 3) continue;
    open.push({
      ruleId: "INV-23",
      subjectId: quest.id,
      subjectTitle: quest.title,
      note: `\uC6B0\uC120\uC21C\uC704 \uADFC\uAC70 3\uBC88(\uC57D\uC18D \uAE30\uD55C)\uC73C\uB85C ${quest.rewardPoints}P \uB97C \uBD99\uC600\uB294\uB370 \uB9C8\uAC10\uC774 \uC5C6\uB2E4`
    });
  }
  return { ruleId: "INV-23", open, undecided, truncated: false };
}

// src/domain/checkpoint/values.ts
var CheckpointStatus = {
  LOCKED: "LOCKED",
  OPEN: "OPEN",
  IN_REVIEW: "IN_REVIEW",
  COMPLETED: "COMPLETED",
  FORFEITED: "FORFEITED"
};
var CHECKPOINT_CLOSED = {
  LOCKED: true,
  OPEN: false,
  IN_REVIEW: false,
  COMPLETED: true,
  FORFEITED: true
};
var TERMINAL_CHECKPOINT_STATUSES = Object.keys(CHECKPOINT_CLOSED).filter((status) => CHECKPOINT_CLOSED[status]);
function isCheckpointClosed(status) {
  return CHECKPOINT_CLOSED[status] === true;
}
var CheckpointType = {
  COMPLETION: "completion",
  NUMERIC: "numeric"
};

// src/domain/checkpoint/checkpoint.ts
function isValidCheckpointType(value) {
  return value === CheckpointType.COMPLETION || value === CheckpointType.NUMERIC;
}
function createCheckpoint(params) {
  if (params.rewardPoints < 0) {
    throw new Error(`rewardPoints cannot be negative, got ${params.rewardPoints}`);
  }
  const chained = params.chainedToPrevious ?? false;
  const initialStatus = chained ? CheckpointStatus.LOCKED : CheckpointStatus.OPEN;
  return {
    id: params.id,
    title: params.title,
    description: params.description,
    order: params.order,
    status: initialStatus,
    questId: params.questId,
    deadline: params.deadline,
    assignedTo: null,
    rewardPoints: params.rewardPoints,
    chainedToPrevious: chained,
    checkpointType: params.checkpointType ?? CheckpointType.COMPLETION,
    targetValue: params.targetValue ?? null,
    createdAt: params.now,
    updatedAt: params.now
  };
}
function assignCheckpoint(checkpoint, userId, now) {
  assertStatus("checkpoint", checkpoint.id, checkpoint.status, [CheckpointStatus.OPEN], "assign");
  if (checkpoint.assignedTo !== null) {
    throw new Error(
      `Cannot assign checkpoint ${checkpoint.id}: already assigned to ${checkpoint.assignedTo}`
    );
  }
  return {
    ...checkpoint,
    status: CheckpointStatus.IN_REVIEW,
    assignedTo: userId,
    updatedAt: now
  };
}
function completeCheckpoint(checkpoint, now, ctx) {
  assertStatus(
    "checkpoint",
    checkpoint.id,
    checkpoint.status,
    [CheckpointStatus.IN_REVIEW],
    "complete"
  );
  if (checkpoint.targetValue != null) {
    const sum = ctx?.taskValueSum ?? 0;
    if (sum < checkpoint.targetValue) {
      throw new Error(
        `Cannot complete checkpoint ${checkpoint.id}: numeric CP requires task value sum (${sum}) >= targetValue (${checkpoint.targetValue})`
      );
    }
  }
  return {
    ...checkpoint,
    status: CheckpointStatus.COMPLETED,
    updatedAt: now
  };
}
function releaseCheckpoint(checkpoint, now) {
  assertStatus(
    "checkpoint",
    checkpoint.id,
    checkpoint.status,
    [CheckpointStatus.IN_REVIEW],
    "release"
  );
  return {
    ...checkpoint,
    status: CheckpointStatus.OPEN,
    assignedTo: null,
    updatedAt: now
  };
}
function forfeitCheckpoint(checkpoint, now) {
  if (checkpoint.status === CheckpointStatus.COMPLETED) {
    throw new Error(`Cannot forfeit checkpoint ${checkpoint.id}: already COMPLETED`);
  }
  if (checkpoint.status === CheckpointStatus.FORFEITED) {
    return checkpoint;
  }
  return {
    ...checkpoint,
    status: CheckpointStatus.FORFEITED,
    assignedTo: null,
    updatedAt: now
  };
}
function forfeitCheckpointsOnQuestFail(checkpoints, now) {
  return checkpoints.filter(
    (cp) => cp.status !== CheckpointStatus.COMPLETED && cp.status !== CheckpointStatus.FORFEITED
  ).map((cp) => forfeitCheckpoint(cp, now));
}
function releaseCheckpointsOnDrop(checkpoints, userId, now) {
  return checkpoints.filter((cp) => cp.status === CheckpointStatus.IN_REVIEW && cp.assignedTo === userId).map((cp) => ({
    ...cp,
    status: CheckpointStatus.OPEN,
    assignedTo: null,
    updatedAt: now
  }));
}
function isCheckpointLockExpired(checkpoint, now) {
  if (checkpoint.assignedTo === null) return false;
  if (checkpoint.deadline === null) return false;
  return now >= checkpoint.deadline;
}
function canModifyCheckpoint(checkpoint) {
  return checkpoint.status !== CheckpointStatus.COMPLETED && checkpoint.status !== CheckpointStatus.FORFEITED;
}
function canDeleteCheckpoint(checkpoint) {
  return checkpoint.status !== CheckpointStatus.COMPLETED && checkpoint.status !== CheckpointStatus.FORFEITED;
}
function compareCheckpointOrder(a, b) {
  if (a.order !== b.order) return a.order - b.order;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}
function recalculateLockStates(checkpoints, now) {
  if (checkpoints.length === 0) return [];
  const sorted = [...checkpoints].sort(compareCheckpointOrder);
  const immutableStatuses = [
    CheckpointStatus.COMPLETED,
    CheckpointStatus.IN_REVIEW,
    CheckpointStatus.FORFEITED
  ];
  return sorted.map((cp, index) => {
    if (immutableStatuses.includes(cp.status)) return cp;
    const hasPredecessor = index > 0;
    const isChained = cp.chainedToPrevious && hasPredecessor;
    if (isChained) {
      const prev = sorted[index - 1];
      const prevCompleted = prev.status === CheckpointStatus.COMPLETED;
      const shouldBeLocked = !prevCompleted;
      if (shouldBeLocked && cp.status !== CheckpointStatus.LOCKED) {
        return { ...cp, status: CheckpointStatus.LOCKED, updatedAt: now };
      }
      if (!shouldBeLocked && cp.status === CheckpointStatus.LOCKED) {
        return { ...cp, status: CheckpointStatus.OPEN, updatedAt: now };
      }
    } else {
      if (cp.status === CheckpointStatus.LOCKED) {
        return { ...cp, status: CheckpointStatus.OPEN, updatedAt: now };
      }
    }
    return cp;
  });
}
function updateCheckpoint(checkpoint, params, now) {
  if (!canModifyCheckpoint(checkpoint)) {
    throw new Error(`Cannot modify checkpoint ${checkpoint.id}: status is ${checkpoint.status}`);
  }
  if (params.rewardPoints !== void 0 && params.rewardPoints < 0) {
    throw new Error(`rewardPoints cannot be negative, got ${params.rewardPoints}`);
  }
  const switchingToCompletion = params.checkpointType === CheckpointType.COMPLETION && checkpoint.checkpointType === CheckpointType.NUMERIC;
  return {
    ...checkpoint,
    ...params.title !== void 0 && { title: params.title },
    ...params.description !== void 0 && { description: params.description },
    ...params.rewardPoints !== void 0 && { rewardPoints: params.rewardPoints },
    ...params.order !== void 0 && { order: params.order },
    ...params.deadline !== void 0 && { deadline: params.deadline },
    ...params.chainedToPrevious !== void 0 && { chainedToPrevious: params.chainedToPrevious },
    ...params.checkpointType !== void 0 && { checkpointType: params.checkpointType },
    ...params.targetValue !== void 0 ? { targetValue: params.targetValue } : switchingToCompletion ? { targetValue: null } : {},
    updatedAt: now
  };
}
function assertCanDeleteCheckpoint(checkpoint) {
  if (!canDeleteCheckpoint(checkpoint)) {
    throw new Error(`Cannot delete checkpoint ${checkpoint.id}: status is ${checkpoint.status}`);
  }
}

// src/domain/checkpoint/transitions.ts
var LEGACY_CHECKPOINT_STATUS = {
  IN_PROGRESS: "IN_PROGRESS"
};
var CHECKPOINT_TRANSITIONS = {
  // 값은 리터럴로 쓴다 — index.ts 가 이 파일을 다시 내보내는 순환이라 모듈 평가 시점에 CheckpointStatus 값을 읽을 수 없다.
  LOCKED: ["OPEN", "FORFEITED"],
  OPEN: ["IN_REVIEW", "LOCKED", "FORFEITED"],
  IN_REVIEW: ["COMPLETED", "OPEN", "LOCKED", "FORFEITED"],
  COMPLETED: [],
  FORFEITED: [],
  IN_PROGRESS: ["COMPLETED", "LOCKED", "FORFEITED"]
};
function canTransitionCheckpoint(from, to) {
  if (from === to) return false;
  return CHECKPOINT_TRANSITIONS[from]?.includes(to) ?? false;
}
function checkpointTransitionSourcesTo(to) {
  return Object.keys(CHECKPOINT_TRANSITIONS).filter(
    (from) => canTransitionCheckpoint(from, to)
  );
}

// src/domain/checkpoint/index.ts
var CheckpointRevisionKind = {
  /** 내용 수정 — 스냅샷은 고치기 직전 값이라 첫 행에 발행 원본이 남는다 */
  UPDATE: "UPDATE",
  /** 정성 CP 반려 — 스냅샷은 반려 시점 값, reason·rejectReasonKind 동봉 */
  REJECT: "REJECT",
  /**
   * 정성 CP 검수 요청(OPEN → IN_REVIEW) — 스냅샷은 제출 시점 값. 제출·재제출마다 행 1건이라
   * 첫 행이 첫 제출, 마지막 행이 마지막 제출이다. 결재함의 제출 시각과 리드타임 분해(작업 시간 ·
   * 승인 대기)가 이 행을 읽는다. 과거 제출은 백필하지 않는다.
   */
  SUBMIT: "SUBMIT"
};
var CHECKPOINT_REVISION_FIELDS = [
  "title",
  "description",
  "deadline",
  "rewardPoints",
  "targetValue",
  "metricKey"
];

// src/domain/proof/values.ts
var ProofStatus = {
  DRAFT: "DRAFT",
  // 임시 보관 (아직 제출하지 않음)
  SUBMITTED: "SUBMITTED",
  // 제출됨 (검토 대기/검토 중)
  COMPLETED: "COMPLETED",
  // 증명 완료
  REJECTED: "REJECTED",
  // 리뷰어 거절
  CANCELLED: "CANCELLED"
  // 퀘스트 포기로 취소됨 (BUG-21)
};

// src/domain/proof/proof.ts
function createProof(params) {
  return {
    id: params.id,
    userId: params.userId,
    questId: params.questId,
    checkpointId: params.checkpointId,
    description: params.description,
    status: ProofStatus.DRAFT,
    attachments: [],
    comments: [],
    reviewedAt: null,
    reviewedBy: null,
    pointsAwarded: 0,
    contentRevision: 0,
    createdAt: params.now,
    updatedAt: params.now
  };
}
function submitProof(proof, checkpoint, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.DRAFT], "submit");
  if (proof.checkpointId === null) {
    throw new Error(
      `Cannot submit quest-level proof ${proof.id} via submitProof. Use submitQuestProof instead.`
    );
  }
  if (proof.checkpointId !== checkpoint.id) {
    throw new Error(
      `Proof checkpoint mismatch: proof references ${proof.checkpointId}, got checkpoint ${checkpoint.id}`
    );
  }
  if (checkpoint.status !== CheckpointStatus.OPEN) {
    throw new Error(
      `Cannot submit proof: checkpoint ${checkpoint.id} status is ${checkpoint.status}, expected OPEN`
    );
  }
  if (checkpoint.assignedTo !== null) {
    throw new Error(
      `Cannot submit proof: checkpoint ${checkpoint.id} is already assigned to ${checkpoint.assignedTo}`
    );
  }
  return {
    ...proof,
    status: ProofStatus.SUBMITTED,
    updatedAt: now
  };
}
function submitQuestProof(proof, questStatus, hasActiveQuestProof, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.DRAFT], "submit");
  if (proof.checkpointId !== null) {
    throw new Error(
      `Cannot submit checkpoint-level proof ${proof.id} via submitQuestProof. Use submitProof instead.`
    );
  }
  if (questStatus !== QuestStatus.ACTIVE) {
    throw new Error(`Cannot submit quest proof: quest status is ${questStatus}, expected ACTIVE`);
  }
  if (hasActiveQuestProof) {
    throw new Error(`Cannot submit quest proof: user already has an active proof for this quest`);
  }
  return {
    ...proof,
    status: ProofStatus.SUBMITTED,
    updatedAt: now
  };
}
function approveProof(proof, reviewerId, pointsAwarded, maxRewardPoints, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.SUBMITTED], "approve");
  if (maxRewardPoints === 0) {
    if (pointsAwarded !== 0) {
      throw new Error(`pointsAwarded must be 0 for no-reward quest, got ${pointsAwarded}`);
    }
  } else {
    if (pointsAwarded < 1) {
      throw new Error(`pointsAwarded must be at least 1, got ${pointsAwarded}`);
    }
    if (pointsAwarded > maxRewardPoints) {
      throw new Error(
        `pointsAwarded (${pointsAwarded}) exceeds maxRewardPoints (${maxRewardPoints})`
      );
    }
  }
  return {
    ...proof,
    status: ProofStatus.COMPLETED,
    reviewedBy: reviewerId,
    reviewedAt: now,
    pointsAwarded,
    updatedAt: now
  };
}
function rejectProof(proof, reviewerId, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.SUBMITTED], "reject");
  return {
    ...proof,
    status: ProofStatus.REJECTED,
    reviewedBy: reviewerId,
    reviewedAt: now,
    pointsAwarded: 0,
    updatedAt: now
  };
}
function updateProofDraft(proof, description, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.DRAFT], "update");
  return { ...proof, description, updatedAt: now };
}
function resubmitProof(proof, now) {
  assertStatus("proof", proof.id, proof.status, [ProofStatus.REJECTED], "resubmit");
  return {
    ...proof,
    status: ProofStatus.DRAFT,
    reviewedAt: null,
    reviewedBy: null,
    pointsAwarded: 0,
    updatedAt: now
  };
}
function cancelProof(proof, now) {
  assertStatus(
    "proof",
    proof.id,
    proof.status,
    [ProofStatus.DRAFT, ProofStatus.SUBMITTED],
    "cancel"
  );
  return {
    ...proof,
    status: ProofStatus.CANCELLED,
    reviewedAt: null,
    reviewedBy: null,
    pointsAwarded: 0,
    updatedAt: now
  };
}

// src/domain/proof/review-overdue.ts
import { addDays, isWeekend as isWeekend2 } from "date-fns";
var PROOF_REVIEW_OVERDUE_BUSINESS_DAYS = 2;
function isProofReviewOverdue({
  submittedAt,
  now,
  holidays,
  thresholdBusinessDays = PROOF_REVIEW_OVERDUE_BUSINESS_DAYS
}) {
  if (thresholdBusinessDays <= 0) return true;
  const today = toKstDateString(now);
  let cursor = kstDate(submittedAt);
  let elapsed = 0;
  for (let i = 0; i < 366; i += 1) {
    cursor = addDays(cursor, 1);
    const date = toKstDateString(cursor);
    if (date > today) break;
    if (isWeekend2(cursor) || holidays.has(date)) continue;
    elapsed += 1;
    if (elapsed >= thresholdBusinessDays) return true;
  }
  return false;
}

// src/domain/proof/index.ts
var AttachmentType = {
  URL: "URL",
  FILE: "FILE"
};

// src/domain/worklog/values.ts
var WorkLogStatus = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED"
};

// src/domain/worklog/worklog.ts
function createWorkLog(params) {
  const { id, userId, logDate, content, now } = params;
  if (!logDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    throw new Error("logDate must be in YYYY-MM-DD format");
  }
  return {
    id,
    userId,
    logDate,
    content: content ?? "",
    status: WorkLogStatus.DRAFT,
    revisionCount: 0,
    submittedAt: null,
    createdAt: now,
    updatedAt: now
  };
}
function updateWorkLog(workLog, content, now) {
  if (workLog.status !== WorkLogStatus.SUBMITTED) {
    assertStatus("workLog", workLog.id, workLog.status, [WorkLogStatus.DRAFT], "update");
  }
  return {
    ...workLog,
    content,
    revisionCount: workLog.status === WorkLogStatus.SUBMITTED ? workLog.revisionCount + 1 : workLog.revisionCount,
    updatedAt: now
  };
}
function submitWorkLog(workLog, now) {
  if (workLog.content.trim().length === 0) {
    throw new Error(`Cannot submit workLog ${workLog.id}: content is empty`);
  }
  return {
    ...workLog,
    status: WorkLogStatus.SUBMITTED,
    submittedAt: now,
    updatedAt: now
  };
}
function revisedAfterLogDay(logDate, lastRevisedAt) {
  if (!lastRevisedAt) return null;
  const revisedOn = toKstWorkDateString(new Date(lastRevisedAt));
  return revisedOn > logDate ? revisedOn : null;
}

// src/domain/campaign/creator-closure.ts
function isRosterCreatorOperable(row) {
  return row.externalCreatorId !== null && row.externalCreatorId !== "" || // 판정 칸도 같은 규율로 빈 문자열을 세지 않는다 — 화면 draft 모델이 빈 값을 `''` 로 드는
  // 관행이 있어(`rosterDraft`), 문자열을 받게 된 이상 여기서도 걸러야 한다.
  row.creatorUnregisteredAt !== null && row.creatorUnregisteredAt !== "" || row.creatorHandleSnapshot !== null && row.creatorHandleSnapshot.trim() !== "";
}

// src/domain/campaign/outreach-form-url.ts
var FORMS_ORIGIN = "https://docs.google.com";
var FORM_ID_PATTERN = /^\/forms\/d\/(?!e\/)([a-zA-Z0-9-_]+)/;
function parseOutreachFormUrl(url) {
  const trimmed = url.trim();
  if (!trimmed.startsWith(`${FORMS_ORIGIN}/`)) return null;
  const pathname = trimmed.slice(FORMS_ORIGIN.length).split(/[?#]/)[0] ?? "";
  return FORM_ID_PATTERN.exec(pathname)?.[1] ?? null;
}
function isValidOutreachFormUrl(url) {
  return parseOutreachFormUrl(url) !== null;
}
function outreachFormRespondUrl(editUrl) {
  const formId = parseOutreachFormUrl(editUrl);
  return formId === null ? null : `${FORMS_ORIGIN}/forms/d/${formId}/viewform`;
}

// src/domain/campaign/form-response-row.ts
var URL_IN_TITLE = /https?:\/\/\S/;
var EMAIL_MARKER = /e-?mail|이메일|메일/i;
var RECIPIENT_MARKER = /full ?name|수령인|본명|실명|성함|이름/i;
var PHONE_MARKER = /phone|연락처|전화|휴대폰/i;
var ADDRESS_MARKER = /address|주소|city|state|province|country|zip|postal|우편번호/i;
function resolveFormQuestionIds(questions) {
  const bySection = /* @__PURE__ */ new Map();
  for (const question of questions) {
    bySection.set(question.sectionIndex, [
      ...bySection.get(question.sectionIndex) ?? [],
      question
    ]);
  }
  const resolved = [];
  for (const sectionIndex of [...bySection.keys()].sort((a, b) => a - b)) {
    const section = bySection.get(sectionIndex) ?? [];
    const urlAt = section.reduce(
      (acc, q, index) => URL_IN_TITLE.test(q.title ?? "") ? [...acc, index] : acc,
      []
    );
    if (urlAt.length !== 1) continue;
    const urlIndex = urlAt[0] ?? -1;
    if (urlIndex < 2) continue;
    const nickname = section[urlIndex - 2];
    const handle = section[urlIndex - 1];
    const tiktokUrl = section[urlIndex];
    if (nickname === void 0 || handle === void 0 || tiktokUrl === void 0) continue;
    const core = /* @__PURE__ */ new Set([nickname.questionId, handle.questionId, tiktokUrl.questionId]);
    resolved.push({
      nickname: nickname.questionId,
      handle: handle.questionId,
      tiktokUrl: tiktokUrl.questionId,
      emailCandidates: section.filter((q) => EMAIL_MARKER.test(q.title ?? "")).map((q) => q.questionId),
      recipientCandidates: pickByMarker(section, RECIPIENT_MARKER, core),
      phoneCandidates: pickByMarker(section, PHONE_MARKER, core),
      addressGroup: longestRun(section, ADDRESS_MARKER, core)
    });
  }
  return resolved;
}
function pickByMarker(section, marker, core) {
  return section.filter((q) => !core.has(q.questionId) && marker.test(q.title ?? "")).map((q) => q.questionId);
}
function longestRun(section, marker, core) {
  const runs = [];
  let current = [];
  for (const question of section) {
    if (!core.has(question.questionId) && marker.test(question.title ?? "")) {
      current.push(question);
      continue;
    }
    if (current.length > 0) runs.push(current);
    current = [];
  }
  if (current.length > 0) runs.push(current);
  const longest = Math.max(0, ...runs.map((run) => run.length));
  const winners = runs.filter((run) => run.length === longest);
  return longest === 0 || winners.length !== 1 ? [] : (winners[0] ?? []).map((q) => q.questionId);
}
function pickSingle(candidates, answers) {
  if (candidates.length === 0) return { value: null, unresolved: false };
  const found = candidates.map((questionId) => (answers[questionId] ?? "").trim()).filter((value) => value.length > 0);
  if (found.length === 1) return { value: found[0] ?? null, unresolved: false };
  return { value: null, unresolved: found.length > 1 };
}
function joinAddress(group, answers) {
  const parts = group.map((questionId) => (answers[questionId] ?? "").trim()).filter((value) => value.length > 0);
  return parts.length === 0 ? null : parts.join(" ");
}
function pickPii(ids, answers) {
  const recipient = pickSingle(ids.recipientCandidates, answers);
  const phone = pickSingle(ids.phoneCandidates, answers);
  const address = joinAddress(ids.addressGroup, answers);
  return {
    pii: { recipientName: recipient.value, address, phone: phone.value },
    unresolved: recipient.unresolved || phone.unresolved
  };
}
function valueShapeSuspect(handle, tiktokUrl) {
  if (/\s/.test(handle)) return true;
  return !/^https?:\/\//i.test(tiktokUrl) && !tiktokUrl.includes(".");
}
function pickEmail(candidates, answers) {
  const found = candidates.map((questionId) => (answers[questionId] ?? "").trim()).filter((value) => creatorEmailSchema.safeParse(value).success);
  return found.length === 1 ? found[0] ?? null : null;
}
function matchSection(sections, answers) {
  const filled = sections.filter((ids) => {
    if ((answers[ids.nickname] ?? "").trim().length === 0) return false;
    return (answers[ids.handle] ?? "").trim().length > 0 || (answers[ids.tiktokUrl] ?? "").trim().length > 0;
  });
  return filled[0] ?? null;
}
var TIKTOK_PROFILE_PREFIX = "https://www.tiktok.com/@";
function urlFromHandle(handle) {
  const normalized = normalizeCreatorHandle(handle);
  return normalized.length > 0 ? `${TIKTOK_PROFILE_PREFIX}${normalized}` : "";
}
function handleFromUrl(url) {
  const at = url.indexOf("/@");
  if (at < 0) return "";
  const tail = url.slice(at + 2);
  const handle = tail.split(/[/?#]/)[0] ?? "";
  return normalizeCreatorHandle(handle);
}
function pickRound(answers, roundIds) {
  if (roundIds.size === 0) return null;
  for (const answer of Object.values(answers)) {
    const value = answer.trim();
    if (roundIds.has(value)) return value;
  }
  return null;
}
function mapFormResponses(questions, responses, outreachRoundIds = []) {
  const roundIds = new Set(outreachRoundIds);
  const sections = resolveFormQuestionIds(questions);
  if (sections.length === 0) {
    return {
      rows: [],
      sectionUnmatched: responses.length,
      valueSuspect: 0,
      emailUnresolved: 0,
      piiUnresolved: 0
    };
  }
  const rows = [];
  let sectionUnmatched = 0;
  let valueSuspect = 0;
  let emailUnresolved = 0;
  let piiUnresolved = 0;
  for (const response of responses) {
    const ids = matchSection(sections, response.answers);
    if (ids === null) {
      sectionUnmatched += 1;
      continue;
    }
    const answeredHandle = (response.answers[ids.handle] ?? "").trim();
    const answeredUrl = (response.answers[ids.tiktokUrl] ?? "").trim();
    const handle = answeredHandle.length > 0 ? answeredHandle : handleFromUrl(answeredUrl);
    const tiktokUrl = answeredUrl.length > 0 ? answeredUrl : urlFromHandle(answeredHandle);
    if (answeredHandle.length > 0 && answeredUrl.length > 0 && valueShapeSuspect(answeredHandle, answeredUrl)) {
      valueSuspect += 1;
    }
    const creatorEmail = pickEmail(ids.emailCandidates, response.answers);
    if (creatorEmail === null) emailUnresolved += 1;
    const { pii, unresolved } = pickPii(ids, response.answers);
    if (unresolved) piiUnresolved += 1;
    rows.push({
      ...response.responseId === void 0 ? {} : { responseId: response.responseId },
      creatorNicknameSnapshot: (response.answers[ids.nickname] ?? "").trim(),
      creatorHandleSnapshot: handle,
      tiktokUrlSnapshot: tiktokUrl,
      creatorEmail,
      pii,
      formSubmittedAt: response.submittedAt,
      outreachRoundId: pickRound(response.answers, roundIds)
    });
  }
  return { rows, sectionUnmatched, valueSuspect, emailUnresolved, piiUnresolved };
}

// src/domain/campaign/outreach-prefill.ts
function formEntryKeyOf(questionId) {
  if (!/^[0-9a-f]+$/i.test(questionId)) return null;
  return `entry.${parseInt(questionId, 16)}`;
}
function outreachHandleEntryKeys(questions, handleFieldKey) {
  if (handleFieldKey === null) return [];
  return resolveFormQuestionIds(questions).map((ids) => formEntryKeyOf(handleFieldKey === "tiktokerUrl" ? ids.tiktokUrl : ids.handle)).filter((key) => key !== null);
}

// src/domain/campaign/outreach-form-watch.ts
var iso = (value) => value ? value.toISOString() : null;
function deriveOutreachFormWatch(record, now) {
  if (!record) {
    return {
      state: "UNKNOWN",
      expiresAt: null,
      lastResponseAt: null,
      brokenSince: null,
      failureStatus: null
    };
  }
  const expired = record.watchExpiresAt !== null && record.watchExpiresAt <= now;
  const failing = record.watchState === "FAILED" || record.readable === false || expired;
  let state;
  if (!failing) {
    state = record.watchState === "WATCHING" ? "WATCHING" : "UNKNOWN";
  } else {
    state = record.everWatched ? "BROKEN" : "NOT_WATCHABLE";
  }
  return {
    state,
    expiresAt: iso(record.watchExpiresAt),
    lastResponseAt: iso(record.lastResponseAt),
    brokenSince: state === "BROKEN" ? iso(record.watchFailedSince ?? record.watchExpiresAt) : null,
    failureStatus: record.watchFailureStatus
  };
}

// src/domain/campaign/import-stage.ts
function deriveImportRosterStage(entry) {
  return entry.videoUrl !== null || entry.uploadDate !== null ? RosterStatus.UPLOAD_REQUESTED : RosterStatus.GUIDELINE_SENT;
}
function importRosterStageAdvance(current, derived) {
  const order = ROSTER_STATUSES;
  const from = order.indexOf(current ?? "");
  if (from === -1) return derived;
  return order.indexOf(derived) > from ? derived : void 0;
}

// src/domain/system/system-actor.ts
var SYSTEM_ACTOR_USER_ID = "00000000-0000-7000-a000-000000000002";
var SYSTEM_ACTOR_USER_SEED = {
  id: SYSTEM_ACTOR_USER_ID,
  username: "\uC2DC\uC2A4\uD15C",
  email: "system@system.aegislab.internal",
  password: "",
  level: "ASSOCIATE",
  roles: ["SYSTEM"],
  excludeFromAttendance: true,
  isAutomatedAgent: true
};
function buildSystemActorUpsertArgs() {
  const { id: _id, ...rest } = SYSTEM_ACTOR_USER_SEED;
  return {
    where: { id: SYSTEM_ACTOR_USER_ID },
    update: { ...rest },
    create: { ...SYSTEM_ACTOR_USER_SEED }
  };
}
async function ensureSystemActor(client) {
  await client.user.upsert(buildSystemActorUpsertArgs());
}

// src/domain/campaign/roster-stage-attribution.ts
function creditedActorOf(transition) {
  const credited = transition.creditedToId;
  return credited === void 0 || credited === null || credited === "" ? transition.actorId : credited;
}
function isForwardRosterStageTransition(transition) {
  const order = ROSTER_STATUSES;
  const from = order.indexOf(transition.fromStage ?? "");
  return from === -1 || order.indexOf(transition.toStage) > from;
}
var RosterAssigneeChangeCause = {
  STAGE_TRANSITION: "STAGE_TRANSITION",
  MANUAL: "MANUAL",
  OFFBOARD: "OFFBOARD"
};

// src/domain/campaign/manager-bonus.ts
var ManagerBonusSkipReason = {
  /** 계약 슬롯 미기재 — 단가의 분모가 없다 (§4-1). */
  NO_CONTRACT_SLOTS: "NO_CONTRACT_SLOTS",
  /** 그 난이도의 슬롯당 값이 정책에서 아직 미정이다 (§4-2). */
  PRICE_UNDEFINED: "PRICE_UNDEFINED"
};
function toTime(value) {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}
function isManagerBonusEligibleRow(row, advertiserDeadline) {
  if (row.droppedAt != null || row.deletedAt != null) return false;
  if (!isRosterUploadComplete(row.rosterStatus, { videoUrl: row.videoUrl })) return false;
  if (advertiserDeadline == null || row.uploadCompletedAt == null) return true;
  return toTime(row.uploadCompletedAt) <= toTime(advertiserDeadline);
}
function stageOwnersOf(row) {
  const ownerByStage = /* @__PURE__ */ new Map();
  for (const transition of row.stageTransitions) {
    if (!isForwardRosterStageTransition(transition)) continue;
    const owner = creditedActorOf(transition);
    if (transition.fromStage !== null) ownerByStage.set(transition.fromStage, owner);
    if (transition.toStage === RosterStatus.UPLOAD_REQUESTED) {
      ownerByStage.set(transition.toStage, owner);
    }
  }
  return [...ownerByStage.values()].filter((owner) => owner !== SYSTEM_ACTOR_USER_ID);
}
function rowDivisorOf(contractSlotCount, eligibleRowCount) {
  return Math.max(contractSlotCount ?? 0, eligibleRowCount);
}
function roundCents(value) {
  return Math.round(value * 100) / 100;
}
function managerBonusAllocationOf(args) {
  const { campaignPrice, rowCounts } = args;
  const rowDivisor = rowDivisorOf(args.contractSlotCount, args.eligibleRowCount);
  if (campaignPrice === null || rowDivisor <= 0 || !rowCounts?.length) return null;
  return {
    rowAmount: roundCents(campaignPrice / rowDivisor),
    lines: rowCounts.map(({ stageCount, myStageCount, rowCount }) => ({
      stageCount,
      myStageCount,
      rowCount,
      amount: roundCents(campaignPrice * rowCount * myStageCount / (rowDivisor * stageCount))
    }))
  };
}
function addRowCount(rowCountsByPerson, person, stageCount, myStageCount) {
  const counts = rowCountsByPerson.get(person) ?? [];
  const line = counts.find(
    (count) => count.stageCount === stageCount && count.myStageCount === myStageCount
  );
  if (line) line.rowCount += 1;
  else counts.push({ stageCount, myStageCount, rowCount: 1 });
  rowCountsByPerson.set(person, counts);
}
function calculateManagerBonus(input) {
  const eligible = input.rows.filter(
    (row) => isManagerBonusEligibleRow(row, input.advertiserDeadline)
  );
  const slotPrice = CAMPAIGN_SLOT_PRICE[input.campaignDifficulty];
  const campaignPrice = calculateCampaignPrice({
    contractSlotCount: input.contractSlotCount,
    campaignDifficulty: input.campaignDifficulty
  });
  const skipReason = input.contractSlotCount === null ? ManagerBonusSkipReason.NO_CONTRACT_SLOTS : slotPrice === null ? ManagerBonusSkipReason.PRICE_UNDEFINED : null;
  const fulfillmentRate = input.contractSlotCount === null || input.contractSlotCount <= 0 ? 0 : Math.min(1, eligible.length / input.contractSlotCount);
  const totalAmount = campaignPrice === null ? 0 : campaignPrice * fulfillmentRate;
  const perPerson = /* @__PURE__ */ new Map();
  const rowCountsByPerson = /* @__PURE__ */ new Map();
  let unattributedRowCount = 0;
  if (totalAmount > 0 && eligible.length > 0) {
    const rowShare = totalAmount / eligible.length;
    for (const row of eligible) {
      const owners = stageOwnersOf(row);
      if (owners.length === 0) {
        unattributedRowCount += 1;
        continue;
      }
      for (const person of new Set(owners)) {
        const myStageCount = owners.filter((owner) => owner === person).length;
        perPerson.set(
          person,
          (perPerson.get(person) ?? 0) + rowShare * myStageCount / owners.length
        );
        addRowCount(rowCountsByPerson, person, owners.length, myStageCount);
      }
    }
  } else {
    unattributedRowCount = eligible.filter((row) => stageOwnersOf(row).length === 0).length;
  }
  const shares = [...perPerson.entries()].map(([userId, amount]) => ({
    userId,
    amount: Math.floor(amount),
    rowCounts: (rowCountsByPerson.get(userId) ?? []).sort(
      (a, b) => a.stageCount - b.stageCount || a.myStageCount - b.myStageCount
    )
  })).filter((share) => share.amount > 0).sort((a, b) => a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0);
  return {
    contractSlotCount: input.contractSlotCount,
    slotPrice,
    campaignPrice,
    eligibleRowCount: eligible.length,
    unattributedRowCount,
    fulfillmentRate,
    totalAmount: Math.round(totalAmount),
    skipReason,
    shares
  };
}

// src/domain/campaign/manager-bonus-statement.ts
var ManagerBonusReasonCode = {
  /** 캠페인이 끝나지 않아 정산 전이다. 예상액은 만들지 않는다(fact #7782). */
  CAMPAIGN_NOT_ENDED: "CAMPAIGN_NOT_ENDED",
  /** 종료됐는데 정산 기록이 없다 — 정산 경로가 서기 전에 끝난 캠페인이나 보관 전이. */
  SETTLEMENT_RECORD_MISSING: "SETTLEMENT_RECORD_MISSING",
  NO_CONTRACT_SLOTS: ManagerBonusSkipReason.NO_CONTRACT_SLOTS,
  PRICE_UNDEFINED: ManagerBonusSkipReason.PRICE_UNDEFINED,
  /** 값은 있지만 마감 안에 종점에 닿은 행이 없다 — 이행률 0. */
  NO_ELIGIBLE_ROWS: "NO_ELIGIBLE_ROWS",
  /** 총액은 계산됐지만 대상 행 전부에 몫을 받을 사람의 흔적이 없다. */
  ALL_ROWS_UNATTRIBUTED: "ALL_ROWS_UNATTRIBUTED",
  /**
   * 캠페인에는 몫이 나왔지만 이 사람의 몫 행이 없다. 귀속 기록이 없거나 합계가 내림으로 0P 가 된
   * 경우이고, 내림으로 사라진 몫은 행이 남지 않아 정산 기록만으로는 둘을 가르지 못한다.
   */
  NOT_CREDITED: "NOT_CREDITED",
  /** 몫은 있지만 정산 시각에 소속 팀이 없어 지급되지 않았다. */
  NO_TEAM_MEMBERSHIP: "NO_TEAM_MEMBERSHIP",
  /** 기록에 든 값이 이 표에 없다. 다른 이유로 지어내지 않는다. */
  UNRECOGNIZED: "UNRECOGNIZED"
};
var MANAGER_BONUS_REASON_MESSAGE = {
  CAMPAIGN_NOT_ENDED: "\uCEA0\uD398\uC778\uC774 \uB05D\uB098\uC9C0 \uC54A\uC544 \uC544\uC9C1 \uC815\uC0B0\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4.",
  SETTLEMENT_RECORD_MISSING: "\uC774 \uCEA0\uD398\uC778\uC740 \uB05D\uB0AC\uC9C0\uB9CC \uC131\uACFC\uAE09 \uC815\uC0B0 \uAE30\uB85D\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  NO_CONTRACT_SLOTS: "\uACC4\uC57D \uC5C5\uB85C\uB4DC \uAC74\uC218\uAC00 \uC785\uB825\uB418\uC9C0 \uC54A\uC544 \uAE08\uC561\uC744 \uACC4\uC0B0\uD560 \uC218 \uC5C6\uC5C8\uC2B5\uB2C8\uB2E4.",
  PRICE_UNDEFINED: "\uC774 \uCEA0\uD398\uC778 \uB09C\uC774\uB3C4\uC758 \uAC74\uB2F9 \uAC12\uC774 \uC544\uC9C1 \uC815\uD574\uC9C0\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4.",
  NO_ELIGIBLE_ROWS: "\uC131\uACFC\uAE09 \uACC4\uC0B0\uC5D0 \uB4E4\uC5B4\uAC00\uB294 \uC5C5\uB85C\uB4DC \uC644\uB8CC \uAC74\uC774 \uC5C6\uC5C8\uC2B5\uB2C8\uB2E4. \uB9C8\uAC10 \uB4A4\uC5D0 \uC62C\uB9B0 \uAC74, \uC774\uD0C8\uD558\uAC70\uB098 \uC9C0\uC6B4 \uAC74, \uACFC\uAC70 \uCEA0\uD398\uC778\uC5D0\uC11C \uC62E\uACA8 \uC628 \uAC74\uC740 \uC138\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.",
  ALL_ROWS_UNATTRIBUTED: "\uC5C5\uB85C\uB4DC\uB97C \uB9C8\uCE5C \uAC74\uC5D0 \uB2E8\uACC4\uB97C \uC62E\uAE34 \uC0AC\uB78C\uC758 \uAE30\uB85D\uC774 \uC5C6\uC5B4 \uBAAB\uC744 \uB098\uB204\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
  NOT_CREDITED: "\uC774 \uCEA0\uD398\uC778 \uC815\uC0B0\uC5D0 \uB0B4 \uBAAB\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. \uB9C8\uAC10 \uC548\uC5D0 \uC5C5\uB85C\uB4DC\uB97C \uB9C8\uCE5C \uAC74\uC5D0\uC11C \uB0B4\uAC00 \uB05D\uB0B4\uACE0 \uC55E\uC73C\uB85C \uC62E\uAE34 \uB2E8\uACC4\uAC00 \uC5C6\uAC70\uB098, \uB098\uB208 \uBAAB\uC774 1P \uBBF8\uB9CC\uC774\uC5C8\uC2B5\uB2C8\uB2E4.",
  NO_TEAM_MEMBERSHIP: "\uC815\uC0B0 \uC2DC\uAC01\uC5D0 \uC18C\uC18D \uD300\uC774 \uC5C6\uC5B4 \uBAAB\uC774 \uC801\uB9BD\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4.",
  UNRECOGNIZED: "\uC815\uC0B0 \uAE30\uB85D\uC758 \uC0AC\uC720\uB97C \uD574\uC11D\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4."
};
function reasonOf(code) {
  return { code, message: MANAGER_BONUS_REASON_MESSAGE[code] };
}
function campaignZeroReasonOf(header) {
  const { skipReason } = header;
  if (skipReason === ManagerBonusSkipReason.NO_CONTRACT_SLOTS) {
    return reasonOf(ManagerBonusReasonCode.NO_CONTRACT_SLOTS);
  }
  if (skipReason === ManagerBonusSkipReason.PRICE_UNDEFINED) {
    return reasonOf(ManagerBonusReasonCode.PRICE_UNDEFINED);
  }
  if (skipReason !== null) return reasonOf(ManagerBonusReasonCode.UNRECOGNIZED);
  if (header.eligibleRowCount === 0) return reasonOf(ManagerBonusReasonCode.NO_ELIGIBLE_ROWS);
  if (header.totalAmount <= 0) return reasonOf(ManagerBonusReasonCode.UNRECOGNIZED);
  return header.unattributedRowCount >= header.eligibleRowCount ? reasonOf(ManagerBonusReasonCode.ALL_ROWS_UNATTRIBUTED) : null;
}
function notSettledReasonOf(scenarioStatus) {
  return scenarioStatus === ScenarioStatus.DRAFT || scenarioStatus === ScenarioStatus.ACTIVE ? reasonOf(ManagerBonusReasonCode.CAMPAIGN_NOT_ENDED) : reasonOf(ManagerBonusReasonCode.SETTLEMENT_RECORD_MISSING);
}
function shareUnpaidReasonOf(share) {
  if (share.skippedReason === null) return null;
  return share.skippedReason === ManagerBonusReasonCode.NO_TEAM_MEMBERSHIP ? reasonOf(ManagerBonusReasonCode.NO_TEAM_MEMBERSHIP) : reasonOf(ManagerBonusReasonCode.UNRECOGNIZED);
}
function myShareReasonOf(args) {
  if (args.share) return shareUnpaidReasonOf(args.share);
  return args.campaignReason ?? reasonOf(ManagerBonusReasonCode.NOT_CREDITED);
}

// src/domain/campaign/video-tracking-current.ts
var VIDEO_TRACKING_SUCCEEDED_END_REASONS = ["REPLACED", "CORRECTED"];
function isChainOpen(row) {
  return row.replacedByTrackingId == null && row.supersededByTrackingId == null;
}
function isLinkedChainOpen(row) {
  return row.linkState === "LINKED" && isChainOpen(row);
}
var CHAIN_OPEN_WHERE = {
  replacedByTrackingId: null,
  supersededByTrackingId: null
};
var LINKED_CHAIN_OPEN_WHERE = {
  linkState: "LINKED",
  ...CHAIN_OPEN_WHERE
};
function isCurrentVideoTracking(row) {
  if (row.supersededByTrackingId !== null) return false;
  if (row.endReason !== null && VIDEO_TRACKING_SUCCEEDED_END_REASONS.includes(row.endReason)) {
    return false;
  }
  if (row.replacesTrackingId !== null && row.registrationStatus !== "ACTIVE") return false;
  return true;
}

// src/domain/campaign/campaign-performance-report.ts
var PERFORMANCE_REPORT_ANCHOR_DAYS = [7, 14, 30];
var PERFORMANCE_REPORT_STATUS = {
  /** 누락이 있거나, 이탈·삭제하지 않은 미게시 의무 영상이 있거나, D+30 에 닿지 않은 영상이 있다. */
  PROVISIONAL: "PROVISIONAL",
  /** 의무 영상(이탈·삭제한 행 제외) 전부 게시돼 D+30 에 닿았고 누락이 없다. */
  FINAL: "FINAL"
};
function readAnchor(tracking, day) {
  const anchor = tracking?.anchors.find((a) => a.day === day);
  if (!anchor?.reached) return { kind: "unreached" };
  return anchor.views === null ? { kind: "missing" } : { kind: "value", views: anchor.views };
}
function isCurrent(tracking) {
  return isChainOpen(tracking);
}
function median(sorted) {
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
function isPosted(row) {
  return row.droppedAt === null && row.deletedAt === null && row.videoUrl !== null;
}
function summarizeCampaignPerformance(input) {
  const postedRows = input.rows.filter(isPosted);
  const contract = input.contractSlotCount && input.contractSlotCount > 0 ? input.contractSlotCount : null;
  const videos = postedRows.map((row) => {
    const current = row.trackings.find(isCurrent) ?? null;
    const registered = current !== null && current.registered !== false;
    return {
      rosterEntryId: row.id,
      creatorHandle: row.creatorHandle ?? null,
      registered,
      trackingId: current?.trackingId ?? null,
      previousTrackingIds: row.trackings.filter((t) => t !== current).map((t) => t.trackingId),
      visibility: current?.visibility ?? null,
      viewsByDay: Object.fromEntries(
        PERFORMANCE_REPORT_ANCHOR_DAYS.map((day) => {
          const reading = registered ? readAnchor(current, day) : null;
          return [day, reading?.kind === "value" ? reading.views : null];
        })
      )
    };
  });
  const currentOf = new Map(
    postedRows.map((row) => [row.id, row.trackings.find(isCurrent) ?? null])
  );
  const anchors = PERFORMANCE_REPORT_ANCHOR_DAYS.map((day) => {
    const values = [];
    let missingCount = 0;
    for (const video of videos) {
      if (!video.registered) continue;
      const reading = readAnchor(currentOf.get(video.rosterEntryId) ?? null, day);
      if (reading.kind === "value") values.push(reading.views);
      else if (reading.kind === "missing") missingCount += 1;
    }
    values.sort((a, b) => a - b);
    const totalViews = values.reduce((sum, v) => sum + v, 0);
    return {
      day,
      aggregatableCount: values.length,
      missingCount,
      totalViews,
      averageViews: values.length ? totalViews / values.length : null,
      medianViews: median(values),
      minViews: values[0] ?? null,
      maxViews: values[values.length - 1] ?? null
    };
  });
  const lastDay = PERFORMANCE_REPORT_ANCHOR_DAYS[2];
  const final = videos.length > 0 && input.rows.every((row) => isPosted(row) || row.droppedAt !== null || row.deletedAt !== null) && anchors.every((a) => a.missingCount === 0) && videos.every(
    (v) => v.registered && readAnchor(currentOf.get(v.rosterEntryId) ?? null, lastDay).kind === "value"
  );
  const firstDay = PERFORMANCE_REPORT_ANCHOR_DAYS[0];
  const ranked = videos.map((video, index) => ({ video, index, views: video.viewsByDay[firstDay] ?? null })).sort(
    (a, b) => a.views === b.views ? a.index - b.index : a.views === null ? 1 : b.views === null ? -1 : b.views - a.views
  ).map((entry) => entry.video);
  return {
    contractVideoCount: contract,
    postedVideoCount: postedRows.length,
    fulfillmentRate: contract === null ? null : postedRows.length / contract,
    anchors,
    deletedCount: videos.filter((v) => v.visibility === "DELETED").length,
    privateCount: videos.filter((v) => v.visibility === "PRIVATE").length,
    unregisteredCount: videos.filter((v) => !v.registered).length,
    videos: ranked,
    status: final ? PERFORMANCE_REPORT_STATUS.FINAL : PERFORMANCE_REPORT_STATUS.PROVISIONAL
  };
}
var number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
function formatViews(value) {
  return value === null ? "\u2014" : number.format(Math.round(value));
}
function statusLabel(status) {
  return status === PERFORMANCE_REPORT_STATUS.FINAL ? "\uCD5C\uC885" : "\uC7A0\uC815";
}
function formatKstMinute(iso2) {
  const p = toKstParts(new Date(iso2));
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}
function videoRemark(video) {
  if (!video.registered) return "\uC870\uD68C\uC218 \uC218\uC9D1 \uC804";
  const marks = [];
  if (video.previousTrackingIds.length > 0) marks.push("\uC7AC\uC5C5\uB85C\uB4DC \uC601\uC0C1\uC73C\uB85C \uAD50\uCCB4");
  if (video.visibility === "DELETED") marks.push("\uC0AD\uC81C\uB428 \xB7 \uB9C8\uC9C0\uB9C9 \uC815\uC0C1 \uAC12");
  if (video.visibility === "PRIVATE") marks.push("\uBE44\uACF5\uAC1C \xB7 \uB9C8\uC9C0\uB9C9 \uC815\uC0C1 \uAC12");
  return marks.join(" \xB7 ");
}
function table(head, rows) {
  return [
    `| ${head.join(" | ")} |`,
    `| ${head.map(() => "---").join(" | ")} |`,
    ...rows.map((cells) => `| ${cells.join(" | ")} |`)
  ];
}
function formatPerformanceReportNotionTable(version) {
  const { summary } = version;
  const fulfillment = summary.contractVideoCount === null ? "\u2014" : `${summary.postedVideoCount} / ${summary.contractVideoCount}`;
  const lines = [
    ...table(
      ["\uCEA0\uD398\uC778", "\uD310", "\uC0C1\uD0DC", "\uC9D1\uACC4 \uAE30\uC900\uC77C", "\uD655\uC815"],
      [
        [
          version.campaignTitle,
          `v${version.versionNo}`,
          statusLabel(summary.status),
          version.baseDate,
          `${formatKstMinute(version.confirmedAt)} \xB7 ${version.confirmedByName}`
        ]
      ]
    ),
    "",
    ...table(
      ["\uACC4\uC57D \uC601\uC0C1", "\uAC8C\uC2DC \uC644\uB8CC", "\uAC8C\uC2DC \uC774\uD589\uB960", "\uC0AD\uC81C \xB7 \uBE44\uACF5\uAC1C"],
      [
        [
          summary.contractVideoCount === null ? "\u2014" : `${summary.contractVideoCount}\uD3B8`,
          `${summary.postedVideoCount}\uD3B8`,
          fulfillment,
          `${summary.deletedCount} \xB7 ${summary.privateCount}`
        ]
      ]
    ),
    "",
    ...table(
      ["\uACBD\uACFC\uC77C", "\uC9D1\uACC4 \uC601\uC0C1", "\uD569\uACC4", "\uD3C9\uADE0", "\uC911\uC559", "\uCD5C\uC18C~\uCD5C\uB300", "\uB204\uB77D"],
      summary.anchors.map((a) => [
        `D+${a.day}`,
        String(a.aggregatableCount),
        a.aggregatableCount ? formatViews(a.totalViews) : "\u2014",
        formatViews(a.averageViews),
        formatViews(a.medianViews),
        a.minViews === null ? "\u2014" : `${formatViews(a.minViews)}~${formatViews(a.maxViews)}`,
        String(a.missingCount)
      ])
    ),
    ...summary.videos.length ? [
      "",
      ...table(
        ["\uC21C\uC704", "\uD06C\uB9AC\uC5D0\uC774\uD130", "\uBE44\uACE0", ...summary.anchors.map((a) => `D+${a.day}`)],
        summary.videos.map((video, index) => [
          String(index + 1),
          video.creatorHandle ? `@${video.creatorHandle}` : "\u2014",
          videoRemark(video),
          ...summary.anchors.map((a) => formatViews(video.viewsByDay[a.day] ?? null))
        ])
      )
    ] : [],
    ...version.note ? ["", version.note] : [],
    ...version.sheetUrl || version.driveUrl ? [""] : [],
    ...version.sheetUrl ? [`\uC131\uACFC \uC2DC\uD2B8: [${version.campaignTitle} \xB7 \uC2DC\uD2B8](${version.sheetUrl})`] : [],
    ...version.driveUrl ? [`\uC601\uC0C1 \uB4DC\uB77C\uC774\uBE0C: [\uD3B8\uC9D1\uBCF8 \uD3F4\uB354](${version.driveUrl})`] : []
  ];
  return lines.join("\n");
}
function performanceReportPrintHeader(version, generatedAt) {
  return {
    campaignTitle: version.campaignTitle,
    baseDate: version.baseDate,
    versionLabel: `v${version.versionNo}`,
    statusLabel: statusLabel(version.summary.status),
    generatedAt
  };
}
function canConfirmCampaignPerformanceReport(scope) {
  const participating = scope.memberships.filter((m) => scope.scenarioTeamIds.includes(m.teamId));
  const departmentLeader = participating.some(
    (m) => m.role === TeamRole.LEADER && m.teamType === TeamType.DEPARTMENT && m.teamStatus === TeamStatus.ACTIVE
  );
  const owner = scope.progressOwnerId !== null && scope.progressOwnerId === scope.userId && participating.length > 0;
  return departmentLeader || owner;
}

// src/domain/campaign/outreach-round-anchors.ts
function deriveAnchorCountsFromOutreachRounds(rounds) {
  const sent = rounds.filter((round) => round.sentAt != null);
  if (sent.length === 0) return {};
  return {
    [ScenarioProgressMetricKey.CONTACTED_COUNT]: sent.reduce(
      (acc, round) => acc + Math.max(0, Math.floor(round.matchedCount)),
      0
    )
  };
}

// src/domain/campaign/retro.ts
var CAMPAIGN_RETRO_LESSON = {
  WORKED: "WORKED",
  AVOID: "AVOID"
};
function campaignRetroDueAt(completedAt) {
  return new Date(new Date(completedAt).getTime() + 7 * 24 * 60 * 60 * 1e3).toISOString();
}
function deriveCampaignRetroPermissions(scope) {
  const admin = scope.roles.includes("ADMIN");
  const memberships = scope.memberships.filter((m) => scope.scenarioTeamIds.includes(m.teamId));
  const guidelineTeamIds = admin ? [...scope.scenarioTeamIds] : memberships.filter((m) => m.role === "LEADER").map((m) => m.teamId);
  const leader = guidelineTeamIds.length > 0;
  const owner = memberships.length > 0 && (scope.progressOwnerId === scope.userId || scope.subOwnerIds.includes(scope.userId));
  return {
    canRead: admin || scope.level === "EXECUTIVE" || memberships.length > 0,
    canObserve: admin || leader || owner || memberships.length > 0 && scope.rosterAssigneeIds.includes(scope.userId),
    canChangeDeadline: admin || leader || owner,
    canExtendDeadline: admin || leader,
    canRecordReport: admin || leader,
    guidelineTeamIds
  };
}
function validateCampaignRetroDeadline(input) {
  if (!input.canChangeDeadline) throw new Error("\uD68C\uACE0 \uB9C8\uAC10\uC744 \uBC14\uAFC0 \uAD8C\uD55C\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.");
  const next = Date.parse(input.nextDueAt);
  if (!Number.isFinite(next) || next <= Date.parse(input.startedAt) || next <= Date.parse(input.now)) {
    throw new Error("\uD68C\uACE0 \uB9C8\uAC10\uC740 \uC2DC\uC791 \uC2DC\uAC01\uACFC \uD604\uC7AC \uC2DC\uAC01\uBCF4\uB2E4 \uB2A6\uC5B4\uC57C \uD569\uB2C8\uB2E4.");
  }
  if (next > Date.parse(input.dueAt) && !input.canExtendDeadline) {
    throw new Error("\uD68C\uACE0 \uB9C8\uAC10 \uC5F0\uC7A5\uC740 \uCC38\uC5EC \uD300\uC758 \uD300\uC7A5\uB9CC \uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.");
  }
}

// src/domain/campaign/roster-export-order.ts
function isExportUploadConfirmed(row) {
  return isRosterUploadComplete(row.rosterStatus, { videoUrl: row.videoUrl });
}
function compareForExport(a, b) {
  const aConfirmed = isExportUploadConfirmed(a);
  const bConfirmed = isExportUploadConfirmed(b);
  if (aConfirmed !== bConfirmed) return aConfirmed ? -1 : 1;
  if (aConfirmed && bConfirmed) {
    const aUploaded = a.uploadDate?.getTime() ?? null;
    const bUploaded = b.uploadDate?.getTime() ?? null;
    if (aUploaded !== bUploaded) {
      if (aUploaded === null) return 1;
      if (bUploaded === null) return -1;
      return bUploaded - aUploaded;
    }
  }
  return a.createdAt.getTime() - b.createdAt.getTime();
}
function orderRosterForExport(rows) {
  return rows.filter((row) => !row.excluded).sort(compareForExport).map((row, index) => ({ id: row.id, exportNo: index + 1 }));
}

// src/domain/campaign/roster-nudge.ts
function toDate(value) {
  return value instanceof Date ? value : new Date(value);
}
function summarizeRosterNudges(nudges, rosterStatusChangedAt) {
  const since = toDate(rosterStatusChangedAt).getTime();
  let count = 0;
  let last = null;
  for (const nudge of nudges) {
    const at = toDate(nudge.createdAt);
    if (at.getTime() < since) continue;
    count += 1;
    if (last === null || at.getTime() > last.getTime()) last = at;
  }
  return { nudgeCount: count, lastNudgedAt: last?.toISOString() ?? null };
}
function canNudgeRosterRow(input) {
  if (input.droppedAt !== null && input.droppedAt !== void 0 && input.droppedAt !== "") {
    return false;
  }
  return deriveRosterBallHolder(input.rosterStatus, input) === RosterBallHolder.CREATOR;
}

// src/domain/campaign/roster-draft-arrival.ts
var RosterDraftEventKind = {
  ARRIVED: "ARRIVED",
  REVISION_REREQUESTED: "REVISION_REREQUESTED"
};
function toDate2(value) {
  return value instanceof Date ? value : new Date(value);
}
function summarizeRosterDraftEvents(events, rosterStatusChangedAt) {
  const since = toDate2(rosterStatusChangedAt).getTime();
  const inStage = events.map((event) => ({ kind: event.kind, at: toDate2(event.createdAt).getTime() })).filter((event) => event.at >= since);
  let lastRerequest = null;
  let rerequestCount = 0;
  for (const event of inStage) {
    if (event.kind !== RosterDraftEventKind.REVISION_REREQUESTED) continue;
    rerequestCount += 1;
    if (lastRerequest === null || event.at > lastRerequest) lastRerequest = event.at;
  }
  let arrival = null;
  for (const event of inStage) {
    if (event.kind !== RosterDraftEventKind.ARRIVED) continue;
    if (lastRerequest !== null && event.at <= lastRerequest) continue;
    if (arrival === null || event.at < arrival) arrival = event.at;
  }
  return {
    draftArrivalAt: arrival === null ? null : new Date(arrival).toISOString(),
    revisionRerequestedAt: lastRerequest === null ? null : new Date(lastRerequest).toISOString(),
    revisionRerequestCount: rerequestCount
  };
}
function resolveRosterDraftReceivedAt(reviewReceivedAt, draftArrivalAt) {
  return reviewReceivedAt ?? draftArrivalAt;
}
var ROSTER_DRAFT_ARRIVAL_STATUSES = [
  RosterStatus.GUIDELINE_SENT,
  RosterStatus.REVISION_REQUESTED
];
function isDomesticLiveRow(input) {
  if (input.campaignType !== ScenarioCampaignType.DOMESTIC) return false;
  return input.droppedAt === null || input.droppedAt === "";
}
function canMarkRosterDraftArrival(input) {
  if (!isDomesticLiveRow(input)) return false;
  if (input.rosterStatus === null || !ROSTER_DRAFT_ARRIVAL_STATUSES.includes(input.rosterStatus)) {
    return false;
  }
  return input.draftArrivalAt === null;
}
function canRerequestRosterRevision(input) {
  if (!isDomesticLiveRow(input)) return false;
  return input.rosterStatus === RosterStatus.REVISION_REQUESTED && input.draftArrivalAt !== null;
}

// src/domain/campaign/roster-progress-anchors.ts
var MIN_STATUS_INDEX = {
  // 행이 존재한다는 것 자체가 응답이다. 레거시 단계값도 여기까지는 든다.
  [ScenarioProgressMetricKey.REPLIED_COUNT]: -1,
  [ScenarioProgressMetricKey.NEGOTIATING_COUNT]: ROSTER_STATUSES.indexOf(
    RosterStatus.PRICE_NEGOTIATION
  ),
  // 명단 확정 = 단가 합의. `가이드라인 전달` 부터는 이미 확정된 사람에게 하는 일이다 (§2-1).
  [ScenarioProgressMetricKey.RECRUITED_COUNT]: ROSTER_STATUSES.indexOf(RosterStatus.GUIDELINE_SENT),
  // `수정 요청` 은 초안이 왔기에 요청하는 것이라 영상 수급이 이미 일어났다.
  [ScenarioProgressMetricKey.DELIVERED_COUNT]: ROSTER_STATUSES.indexOf(
    RosterStatus.REVISION_REQUESTED
  )
};
var STATUS_INDEX = new Map(ROSTER_STATUSES.map((status, i) => [status, i]));
function deriveAnchorCountsFromRoster(rows) {
  if (rows.length === 0) return {};
  const live = rows.filter((row) => row.droppedAt == null);
  const counts = {
    [ScenarioProgressMetricKey.UPLOADED_COUNT]: live.filter(
      (row) => isRosterUploadComplete(row.rosterStatus, { videoUrl: row.videoUrl })
    ).length
  };
  for (const [metricKey, minIndex] of Object.entries(MIN_STATUS_INDEX)) {
    counts[metricKey] = live.filter(
      (row) => statusIndex(row.rosterStatus) >= minIndex
    ).length;
  }
  return counts;
}
function statusIndex(status) {
  return typeof status === "string" ? STATUS_INDEX.get(status) ?? -1 : -1;
}

// src/domain/campaign/roster-seed-quest.ts
var SEED_BY_STAGE = {
  // 리스트업·단가협상은 둘 다 섭외 구간이라 시드 하나로 간다.
  [RosterStatus.LISTED]: TimelineEventType.CREATOR_RECRUITED,
  [RosterStatus.PRICE_NEGOTIATION]: TimelineEventType.CREATOR_RECRUITED,
  [RosterStatus.GUIDELINE_SENT]: TimelineEventType.GUIDELINE_DELIVERED,
  [RosterStatus.REVISION_REQUESTED]: TimelineEventType.CREATOR_DELIVERED,
  [RosterStatus.UPLOAD_REQUESTED]: TimelineEventType.CREATOR_UPLOADED
};
function isRosterStatus(stage) {
  return Object.prototype.hasOwnProperty.call(SEED_BY_STAGE, stage);
}
function seedEventTypeForRosterStage(stage) {
  if (stage == null || !isRosterStatus(stage)) return null;
  return SEED_BY_STAGE[stage];
}

// src/domain/campaign/stage-proof-guide.ts
var STAGE_PROOF_GUIDES = [
  {
    eventType: TimelineEventType.CREATOR_RECRUITED,
    proofGuide: {
      stage: "RECRUIT",
      recommendedSheet: "INTERNAL",
      guide: "\uD300\uC7A5\uC740 \uC774 \uB2E8\uACC4\uC5D0\uC11C \uC12D\uC678 \uC644\uB8CC \uC778\uC6D0\uC774 \uBCF4\uC774\uB294 \uB0B4\uBD80\uC6A9 \uB9AC\uC2A4\uD2B8\uB97C \uBD05\uB2C8\uB2E4.",
      placeholder: "\uC608: \uC12D\uC678 \uC644\uB8CC 8\uBA85 \u2014 \uB0B4\uBD80\uC6A9 \uB9AC\uC2A4\uD2B8 \uAE30\uC900"
    }
  },
  {
    eventType: TimelineEventType.CREATOR_DELIVERED,
    proofGuide: {
      stage: "DELIVER",
      recommendedSheet: "INTERNAL",
      guide: "\uD300\uC7A5\uC740 \uC774 \uB2E8\uACC4\uC5D0\uC11C \uB0B4\uBD80\uC6A9 \uC2DC\uD2B8\uC758 \uC218\uAE09 \uD604\uD669 \uD0ED\uC744 \uBD05\uB2C8\uB2E4.",
      placeholder: "\uC608: \uC218\uAE09 12\uBA85 \uC644\uB8CC \u2014 \uC218\uAE09 \uD604\uD669 \uD0ED \uAE30\uC900"
    }
  },
  {
    eventType: TimelineEventType.CREATOR_UPLOADED,
    proofGuide: {
      stage: "UPLOAD",
      recommendedSheet: "EXTERNAL",
      guide: "\uD300\uC7A5\uC740 \uC774 \uB2E8\uACC4\uC5D0\uC11C \uC678\uBD80\uC6A9 \uC2DC\uD2B8\uC758 \uD655\uC815\uB9AC\uC2A4\uD2B8 \uD0ED\uACFC \uC5C5\uB85C\uB4DC \uC644\uB8CC \uC218\uCE58\uB97C \uBD05\uB2C8\uB2E4.",
      placeholder: "\uC608: \uC5C5\uB85C\uB4DC 10\uAC74 \uC644\uB8CC \u2014 \uD655\uC815\uB9AC\uC2A4\uD2B8 \uD0ED \uAE30\uC900"
    }
  },
  {
    eventType: TimelineEventType.GUIDELINE_DELIVERED,
    proofGuide: {
      stage: "GUIDELINE",
      recommendedSheet: null,
      guide: "\uD300\uC7A5\uC774 \uD655\uC778\uD560 \uC218 \uC788\uB3C4\uB85D \uCEA0\uD398\uC778 \uC2DC\uD2B8\uB97C \uBD99\uC5EC \uC8FC\uC138\uC694."
    }
  },
  {
    eventType: TimelineEventType.NOTE,
    proofGuide: {
      stage: "NOTE",
      recommendedSheet: null,
      guide: "\uD300\uC7A5\uC774 \uD655\uC778\uD560 \uC218 \uC788\uB3C4\uB85D \uCEA0\uD398\uC778 \uC2DC\uD2B8\uB97C \uBD99\uC5EC \uC8FC\uC138\uC694."
    }
  }
];
function getStageProofGuide(checkpoints, scenarioId) {
  if (!scenarioId) return null;
  const stage = STAGE_PROOF_GUIDES.find(({ eventType }) => {
    const discriminator = seedCheckpointDiscriminator(eventType);
    if (!discriminator) return false;
    return checkpoints.some(
      (checkpoint) => "metricKey" in discriminator ? checkpoint.metricKey === discriminator.metricKey : checkpoint.title === discriminator.title
    );
  });
  return stage?.proofGuide ?? null;
}

// src/domain/campaign/roster-stall.ts
var RosterStallKind = {
  /** 공이 우리에게 있는데 안 움직였다. 팀장 에스컬레이션 대상. */
  UNATTENDED: "UNATTENDED",
  /** 공이 크리에이터에게 있는데 안 움직였다. 담당자의 독촉·이탈 판단 대상. */
  NO_REPLY: "NO_REPLY",
  /**
   * 초안이 와 있는데 판정이 없다 (`creator-draft-review.md` §5). 공은 우리에게 있지만 응답
   * 방치와 시계가 다르다 — 응답 방치는 발신 한 통이면 풀려서, "검토하고 회신드릴게요" 를
   * 보내는 순간 초안이 열린 채로 경보가 꺼진다. 시계는 초안 도착 시각이다.
   */
  REVIEW_UNATTENDED: "REVIEW_UNATTENDED"
};
var ROSTER_STALL_KIND_LABEL = {
  [RosterStallKind.UNATTENDED]: "\uC6B0\uB9AC \uCC28\uB840 \uBC29\uCE58",
  [RosterStallKind.NO_REPLY]: "\uC751\uB2F5 \uBC29\uCE58",
  [RosterStallKind.REVIEW_UNATTENDED]: "\uAC80\uC218 \uBA48\uCDA4"
};
var ROSTER_STALL_THRESHOLD_DAYS = {
  [RosterStallKind.UNATTENDED]: 2,
  [RosterStallKind.NO_REPLY]: 5,
  // 초기값 — 매니저가 초안을 모아 한 번에 보는 것이 정상 경로라 응답 방치보다 길고, 공이 우리에게
  // 있어 미응답보다 짧다. 첫 GLOBAL 캠페인의 도착 → 판정 일수로 다시 본다
  // (`docs/plan/draft-review-reject-aggregate/cp1-investigation-2026-09-30.md` 결정 C).
  [RosterStallKind.REVIEW_UNATTENDED]: 3
};
var ROSTER_NO_REPLY_NUDGE_THRESHOLD = 3;
var MS_PER_DAY = 24 * 60 * 60 * 1e3;
function toDate3(value) {
  if (value === null || value === void 0 || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
function elapsedDaysBetween(from, to) {
  return Math.max(0, Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY));
}
function deriveRosterStallSignal(input, now) {
  const changedAt = toDate3(input.rosterStatusChangedAt);
  const droppedAt = toDate3(input.droppedAt);
  if (changedAt === null) {
    return { stalled: false, kind: null, elapsedDays: null };
  }
  if (droppedAt !== null) {
    return {
      stalled: false,
      kind: null,
      elapsedDays: elapsedDaysBetween(changedAt, droppedAt)
    };
  }
  const holder = deriveRosterBallHolder(input.rosterStatus, input);
  const draftReceivedAt = toDate3(input.draftReviewReceivedAt);
  if (holder === RosterBallHolder.US && draftReceivedAt !== null) {
    const reviewElapsed = elapsedDaysBetween(draftReceivedAt, now);
    return {
      stalled: reviewElapsed >= ROSTER_STALL_THRESHOLD_DAYS[RosterStallKind.REVIEW_UNATTENDED],
      kind: RosterStallKind.REVIEW_UNATTENDED,
      elapsedDays: reviewElapsed
    };
  }
  const waitingSince = toDate3(input.waitingSince);
  const clockFrom = waitingSince !== null && waitingSince.getTime() > changedAt.getTime() ? waitingSince : changedAt;
  const elapsedDays2 = elapsedDaysBetween(clockFrom, now);
  const kind = holder === RosterBallHolder.US ? RosterStallKind.UNATTENDED : holder === RosterBallHolder.CREATOR ? RosterStallKind.NO_REPLY : null;
  if (kind === null) {
    return { stalled: false, kind: null, elapsedDays: elapsedDays2 };
  }
  const byNudges = kind === RosterStallKind.NO_REPLY && (input.nudgeCount ?? 0) >= ROSTER_NO_REPLY_NUDGE_THRESHOLD;
  return {
    stalled: byNudges || elapsedDays2 >= ROSTER_STALL_THRESHOLD_DAYS[kind],
    kind,
    elapsedDays: elapsedDays2
  };
}

// src/domain/campaign/roster-delivery-conditions.ts
var RosterDeliveryBlock = {
  /** 이탈한 행. 종료 표시는 단계와 별개 축이라 단계만 보면 놓친다 (§4). */
  DROPPED: "DROPPED",
  /** `단가협상` 이 아닌 단계 — `리스트업` 은 협의 전, `가이드라인 전달` 이후는 이미 확정된 사람. */
  NOT_PRICE_NEGOTIATION: "NOT_PRICE_NEGOTIATION",
  /** 열린 회차에 판정 없이 든 행 — 같은 행을 두 회차가 들면 공이 둘로 갈린다. */
  ADVERTISER_HOLDING: "ADVERTISER_HOLDING"
};
var RosterDeliveryCondition = {
  /** 크리에이터가 설문폼을 냈다. 신호는 `formSubmittedAt` 하나 — 손입력·시트 임포트 행은 비어 막힌다. */
  FORM_SUBMISSION: "FORM_SUBMISSION",
  /**
   * 조건 협의가 끝났다. 신호는 현행 합의가 `negotiatedAmount` — 제안·합의를 가르는 저장 형태는
   * 단가 트랙(`7ee785bb`)이 정하는 중이라 지금 읽을 수 있는 칸은 이것뿐이다.
   */
  PRICE_AGREEMENT: "PRICE_AGREEMENT"
};
function isFilled(value) {
  if (value === null || value === void 0) return false;
  return typeof value !== "string" || value.trim() !== "";
}
var ROSTER_DELIVERY_RULES = [
  {
    key: RosterDeliveryBlock.DROPPED,
    message: "\uC774\uD0C8\uD55C \uD589\uC740 \uBA85\uB2E8\uC5D0 \uB123\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
    blocks: (row) => row.dropped
  },
  {
    key: RosterDeliveryBlock.NOT_PRICE_NEGOTIATION,
    message: "\uB2E8\uAC00\uD611\uC0C1 \uB2E8\uACC4\uC758 \uD589\uB9CC \uAD11\uACE0\uC8FC\uC5D0 \uC804\uB2EC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uBA85\uB2E8\uC740 \uC870\uAC74 \uD611\uC758 \uD6C4 \uB118\uAE41\uB2C8\uB2E4",
    blocks: (row) => row.rosterStatus !== RosterStatus.PRICE_NEGOTIATION
  },
  {
    key: RosterDeliveryBlock.ADVERTISER_HOLDING,
    message: "\uC774\uBBF8 \uAD11\uACE0\uC8FC\uAC00 \uAC80\uC218 \uC911\uC778 \uD589\uC785\uB2C8\uB2E4",
    blocks: (row) => row.advertiserReviewPending
  },
  {
    key: RosterDeliveryCondition.FORM_SUBMISSION,
    message: "\uD3FC \uC81C\uCD9C\uC774 \uC5C6\uB294 \uD589\uC785\uB2C8\uB2E4",
    missing: (row) => !isFilled(row.formSubmittedAt)
  },
  {
    key: RosterDeliveryCondition.PRICE_AGREEMENT,
    message: "\uD569\uC758 \uB2E8\uAC00\uAC00 \uBE44\uC5B4 \uC788\uB294 \uD589\uC785\uB2C8\uB2E4",
    missing: (row) => !isFilled(row.negotiatedAmount)
  }
];
function rosterDeliveryBlock(row) {
  for (const rule of ROSTER_DELIVERY_RULES) {
    if ("blocks" in rule && rule.blocks(row)) return { key: rule.key, message: rule.message };
  }
  return null;
}
function missingRosterDeliveryConditions(row) {
  const missing = [];
  for (const rule of ROSTER_DELIVERY_RULES) {
    if ("missing" in rule && rule.missing(row)) missing.push(rule.key);
  }
  return missing;
}

// src/domain/campaign/roster-shipping.ts
var RosterShippingState = {
  /** 폼 응답에 배송지가 비었다 — 크리에이터에게 받아야 한다. */
  NO_ADDRESS: "NO_ADDRESS",
  /** 폼 주소가 왔는데 담당자가 아직 확인하지 않았다. */
  ADDRESS_UNCONFIRMED: "ADDRESS_UNCONFIRMED",
  /** 담당자가 확인했다. 배송지 파일에는 이 상태의 행만 실린다. */
  ADDRESS_CONFIRMED: "ADDRESS_CONFIRMED",
  /** 배송지를 광고주에게 보냈고 송장을 기다린다. */
  AWAITING_TRACKING: "AWAITING_TRACKING",
  /** 광고주가 돌려준 파일로 송장이 붙었다. */
  TRACKING_RECEIVED: "TRACKING_RECEIVED"
};
function present(value) {
  return value !== null && value !== void 0 && String(value).trim() !== "";
}
function deriveRosterShippingState(input) {
  if (!input.productShipping) return null;
  if (present(input.droppedAt)) return null;
  if (deriveRosterPhase(input.rosterStatus) !== RosterPhase.PRODUCTION) return null;
  if (present(input.trackingNumber)) return RosterShippingState.TRACKING_RECEIVED;
  if (present(input.shippingAddressSentAt)) return RosterShippingState.AWAITING_TRACKING;
  if (!input.hasAddress) return RosterShippingState.NO_ADDRESS;
  if (present(input.addressConfirmedAt)) return RosterShippingState.ADDRESS_CONFIRMED;
  return RosterShippingState.ADDRESS_UNCONFIRMED;
}
function canExportRosterShipping(input) {
  return input.canViewPii || input.progressOwnerId === input.userId || input.assigneeIds.includes(input.userId);
}
var CARRIER_ALIASES = [
  ["CJ\uB300\uD55C\uD1B5\uC6B4", ["cj\uB300\uD55C\uD1B5\uC6B4", "cj", "\uB300\uD55C\uD1B5\uC6B4", "cj\uD0DD\uBC30", "cjlogistics"]],
  ["\uC6B0\uCCB4\uAD6D\uD0DD\uBC30", ["\uC6B0\uCCB4\uAD6D\uD0DD\uBC30", "\uC6B0\uCCB4\uAD6D", "epost", "koreapost"]],
  ["\uD55C\uC9C4\uD0DD\uBC30", ["\uD55C\uC9C4\uD0DD\uBC30", "\uD55C\uC9C4", "hanjin"]],
  ["\uB86F\uB370\uD0DD\uBC30", ["\uB86F\uB370\uD0DD\uBC30", "\uB86F\uB370", "lotte", "\uB86F\uB370\uAE00\uB85C\uBC8C\uB85C\uC9C0\uC2A4"]],
  ["\uB85C\uC820\uD0DD\uBC30", ["\uB85C\uC820\uD0DD\uBC30", "\uB85C\uC820", "logen"]]
];
function squash(value) {
  return value.toLowerCase().replace(/[\s.\-_()]/g, "");
}
function normalizeShippingCarrier(raw) {
  if (raw === null || raw === void 0) return null;
  const text = String(raw).trim();
  if (text === "") return null;
  const key = squash(text);
  for (const [carrier, aliases] of CARRIER_ALIASES) {
    if (aliases.includes(key)) return { carrier, recognized: true };
  }
  return { carrier: text, recognized: false };
}
function normalizeTrackingNumber(raw) {
  if (raw === null || raw === void 0) return null;
  const value = String(raw).replace(/[^0-9A-Za-z]/g, "");
  return value === "" ? null : value;
}
var TRACKING_COLUMN_LABEL = {
  no: "\uBC88\uD638",
  handle: "\uD578\uB4E4",
  carrier: "\uD0DD\uBC30\uC0AC",
  trackingNumber: "\uC1A1\uC7A5\uBC88\uD638"
};
var TRACKING_HEADER_ALIASES = {
  no: ["\uBC88\uD638", "no", "#", "\uC21C\uBC88", "seq"],
  handle: ["\uD578\uB4E4", "handle", "\uD2F1\uD1A1\uC544\uC774\uB514", "\uC544\uC774\uB514", "tiktokid", "tiktok", "\uACC4\uC815"],
  carrier: ["\uD0DD\uBC30\uC0AC", "carrier", "courier", "\uBC30\uC1A1\uC0AC"],
  trackingNumber: [
    "\uC1A1\uC7A5\uBC88\uD638",
    "\uC1A1\uC7A5",
    "\uC6B4\uC1A1\uC7A5\uBC88\uD638",
    "\uC6B4\uC1A1\uC7A5",
    "tracking",
    "trackingnumber",
    "trackingno"
  ]
};
var HEADER_SCAN_ROWS = 20;
function cellText(value) {
  if (value === null || value === void 0) return "";
  return String(value).trim();
}
function columnOf(header) {
  const key = squash(header);
  for (const column of Object.keys(TRACKING_HEADER_ALIASES)) {
    if (TRACKING_HEADER_ALIASES[column].includes(key)) return column;
  }
  return null;
}
function toRowNumber(value) {
  const text = cellText(value);
  if (!/^\d+$/.test(text)) return null;
  return Number(text);
}
function readTrackingSheet(rows) {
  const allColumns = Object.keys(TRACKING_COLUMN_LABEL);
  for (let r = 0; r < Math.min(rows.length, HEADER_SCAN_ROWS); r++) {
    const index = /* @__PURE__ */ new Map();
    (rows[r] ?? []).forEach((cell, c) => {
      const column = columnOf(cellText(cell));
      if (column && !index.has(column)) index.set(column, c);
    });
    if (!index.has("handle") || !index.has("trackingNumber")) continue;
    const pick = (row, column) => {
      const c = index.get(column);
      return c === void 0 ? "" : cellText(row[c]);
    };
    const lines = rows.slice(r + 1).filter((row) => row.some((cell) => cellText(cell) !== "")).map((row) => ({
      no: toRowNumber(pick(row, "no")),
      handle: pick(row, "handle"),
      carrier: pick(row, "carrier"),
      trackingNumber: pick(row, "trackingNumber")
    }));
    return {
      lines,
      missingColumns: allColumns.filter((c) => !index.has(c)).map((c) => TRACKING_COLUMN_LABEL[c])
    };
  }
  return { lines: [], missingColumns: allColumns.map((c) => TRACKING_COLUMN_LABEL[c]) };
}
var TrackingImportResult = {
  ADD: "ADD",
  CHANGE: "CHANGE",
  SAME: "SAME",
  BLANK: "BLANK",
  /** 파일의 핸들이 이 캠페인 명단에 없다. */
  NOT_FOUND: "NOT_FOUND",
  /** 파일 줄에 핸들이 비었다 — 찾을 열쇠가 없다. */
  HANDLE_MISSING: "HANDLE_MISSING",
  /** 같은 핸들이 명단의 두 행 이상에 있다 — 어느 행인지 고를 수 없다. */
  AMBIGUOUS: "AMBIGUOUS",
  /** 같은 핸들이 파일에 한 번 더 왔다. 첫 줄만 쓴다. */
  DUPLICATE: "DUPLICATE"
};
var TRACKING_IMPORT_APPLIED = /* @__PURE__ */ new Set([
  TrackingImportResult.ADD,
  TrackingImportResult.CHANGE
]);
function planTrackingImport(lines, rows) {
  const rowsByHandle = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = normalizeCreatorHandle(row.handle);
    rowsByHandle.set(key, [...rowsByHandle.get(key) ?? [], row]);
  }
  const seen = /* @__PURE__ */ new Set();
  return lines.map((line) => {
    const fileHandle = line.handle && line.handle.trim() !== "" ? line.handle.trim() : null;
    const carrier = normalizeShippingCarrier(line.carrier);
    const trackingNumber = normalizeTrackingNumber(line.trackingNumber);
    const candidates = fileHandle === null ? [] : rowsByHandle.get(normalizeCreatorHandle(fileHandle)) ?? [];
    const skip = (result2) => ({
      no: line.no,
      fileHandle,
      result: result2,
      entryId: null,
      nickname: candidates[0]?.nickname ?? null,
      carrier: carrier?.carrier ?? null,
      carrierRecognized: carrier?.recognized ?? false,
      trackingNumber,
      current: null
    });
    if (trackingNumber === null) return skip(TrackingImportResult.BLANK);
    if (fileHandle === null) return skip(TrackingImportResult.HANDLE_MISSING);
    const [row] = candidates;
    if (!row) return skip(TrackingImportResult.NOT_FOUND);
    if (candidates.length > 1) return skip(TrackingImportResult.AMBIGUOUS);
    if (seen.has(row.entryId)) return skip(TrackingImportResult.DUPLICATE);
    seen.add(row.entryId);
    const currentTracking = normalizeTrackingNumber(row.trackingNumber);
    const result = currentTracking === null ? TrackingImportResult.ADD : currentTracking === trackingNumber && row.shippingCarrier === (carrier?.carrier ?? null) ? TrackingImportResult.SAME : TrackingImportResult.CHANGE;
    return {
      ...skip(result),
      entryId: result === TrackingImportResult.SAME ? null : row.entryId,
      current: currentTracking === null ? null : { shippingCarrier: row.shippingCarrier, trackingNumber: row.trackingNumber }
    };
  });
}

// src/domain/campaign/roster-payout.ts
import { z as z4 } from "zod";
var rosterPayoutWriteSchema = z4.object({
  paypalEmail: creatorEmailSchema.optional(),
  paypalMe: paypalMeSchema.optional()
}).strict().refine((value) => value.paypalEmail === void 0 !== (value.paypalMe === void 0));
function parsePayoutInput(raw) {
  const text = raw.trim();
  const asEmail = creatorEmailSchema.safeParse(text);
  if (asEmail.success) return { paypalEmail: asEmail.data };
  const asLink = paypalMeSchema.safeParse(text);
  if (asLink.success) return { paypalMe: asLink.data };
  return null;
}
var MASK = "\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
function maskPayoutValue(value) {
  const at = value.lastIndexOf("@");
  if (at > 0) return `${value.slice(0, Math.max(1, Math.min(2, at - 1)))}${MASK}${value.slice(at)}`;
  const handle = value.replace(/^(https?:\/\/)?(www\.)?paypal\.me\//i, "").replace(/\/$/, "");
  return `paypal.me/${handle.slice(0, 2)}${MASK}`;
}

// src/domain/campaign/roster-drop-event.ts
var RosterDropEventAction = {
  DROP: "DROP",
  RESTORE: "RESTORE"
};
var ROSTER_DROP_EVENT_ACTIONS = [
  RosterDropEventAction.DROP,
  RosterDropEventAction.RESTORE
];
function isRosterDropEventAction(value) {
  return typeof value === "string" && ROSTER_DROP_EVENT_ACTIONS.includes(value);
}
function toMonthDay(value) {
  if (value === null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getMonth() + 1}/${date.getDate()}`;
}
function formatRosterDropEvent(event) {
  const kind = ROSTER_DROP_KIND_LABEL[event.dropKind];
  const head = event.action === RosterDropEventAction.RESTORE ? `\uBCF5\uAD6C (${kind})` : kind;
  const day = toMonthDay(event.createdAt);
  return day === null ? head : `${head} \xB7 ${day}`;
}

// src/domain/campaign/advertiser-review-display.ts
var DAY_MS = 864e5;
function toMonthDay2(value) {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}
function isAdvertiserReviewPending(row) {
  return row.dropKind === null && row.advertiserReview !== null && row.advertiserReview.verdict === null;
}
function deriveAdvertiserReviewMark(row) {
  if (row.dropKind !== null) return null;
  if (isAdvertiserReviewPending(row)) return "\uAD11\uACE0\uC8FC \uAC80\uC218 \uC911";
  if (row.advertiserReportedAt === null) return null;
  if (row.advertiserReportedAt === "") return "\uAD11\uACE0\uC8FC \uBCF4\uACE0\uD568";
  return `\uAD11\uACE0\uC8FC \uBCF4\uACE0\uD568 ${toMonthDay2(row.advertiserReportedAt)}`;
}
function describeAdvertiserReviewRound(round, now) {
  const sentAt = round.sentAt instanceof Date ? round.sentAt : new Date(round.sentAt);
  const days = Math.max(0, Math.floor((now.getTime() - sentAt.getTime()) / DAY_MS));
  const pending = round.entries.filter((entry) => entry.verdict === null).length;
  return [
    "\uAD11\uACE0\uC8FC \uAC80\uC218 \uC911",
    `\uC804\uB2EC ${toMonthDay2(sentAt)}`,
    days > 0 ? `${days}\uC77C` : null,
    `${pending}\uD589 \uB0A8\uC74C`
  ].filter((part) => part !== null).join(" \xB7 ");
}

// src/domain/campaign/target-slot-lifecycle.ts
import { z as z5 } from "zod";
var TargetSlotState = {
  EMPTY: "EMPTY",
  ASSIGNED: "ASSIGNED",
  FULFILLED: "FULFILLED",
  REPLACEMENT_NEEDED: "REPLACEMENT_NEEDED",
  CANCELLED: "CANCELLED"
};
var timestampSchema = z5.string().refine((value) => Number.isFinite(Date.parse(value)), {
  message: "\uC720\uD6A8\uD55C \uC2DC\uAC01\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4"
});
var targetSlotAssignmentSchema = z5.object({
  id: z5.string().trim().min(1),
  rosterEntryId: z5.string().trim().min(1),
  assignedAt: timestampSchema,
  /** 배정 당시 적용 조건의 불변 리비전. 캠페인 최신값으로 덮지 않는다. */
  conditionsRevisionId: z5.string().trim().min(1)
});
var targetSlotDeliverySchema = z5.object({
  id: z5.string().trim().min(1),
  scenarioId: z5.string().trim().min(1),
  rosterEntryId: z5.string().trim().min(1),
  uploadCompletedAt: timestampSchema
});
var targetSlotBase = {
  id: z5.string().trim().min(1),
  slotRevision: z5.number().int().nonnegative().safe()
};
var campaignTargetSlotSchema = z5.discriminatedUnion("lifecycleState", [
  z5.object({
    ...targetSlotBase,
    lifecycleState: z5.literal(TargetSlotState.EMPTY),
    assignment: z5.null(),
    fulfillment: z5.null()
  }),
  z5.object({
    ...targetSlotBase,
    lifecycleState: z5.literal(TargetSlotState.ASSIGNED),
    assignment: targetSlotAssignmentSchema,
    fulfillment: z5.null()
  }),
  z5.object({
    ...targetSlotBase,
    lifecycleState: z5.literal(TargetSlotState.FULFILLED),
    assignment: targetSlotAssignmentSchema,
    fulfillment: targetSlotDeliverySchema
  }),
  z5.object({
    ...targetSlotBase,
    lifecycleState: z5.literal(TargetSlotState.REPLACEMENT_NEEDED),
    assignment: z5.null(),
    fulfillment: z5.null()
  }),
  z5.object({
    ...targetSlotBase,
    lifecycleState: z5.literal(TargetSlotState.CANCELLED),
    assignment: z5.null(),
    fulfillment: z5.null()
  })
]);
var targetSlotSnapshotSchema = z5.object({
  scenarioId: z5.string().trim().min(1),
  /** Target-slot projection이 소유한 변경의 단조 증가 버전. 전체 로스터 응답의 ETag는 아니다. */
  boardRevision: z5.number().int().nonnegative().safe(),
  slots: z5.array(campaignTargetSlotSchema)
}).superRefine((snapshot, context) => {
  const slots = /* @__PURE__ */ new Set();
  const assignments = /* @__PURE__ */ new Set();
  const rows = /* @__PURE__ */ new Set();
  const deliveries = /* @__PURE__ */ new Set();
  for (const [index, slot] of snapshot.slots.entries()) {
    if (slots.has(slot.id)) {
      context.addIssue({
        code: "custom",
        path: ["slots", index, "id"],
        message: "\uC2AC\uB86F ID\uAC00 \uC911\uBCF5\uB429\uB2C8\uB2E4"
      });
    }
    slots.add(slot.id);
    if (slot.assignment) {
      if (assignments.has(slot.assignment.id)) {
        context.addIssue({
          code: "custom",
          path: ["slots", index, "assignment", "id"],
          message: "\uBC30\uC815 ID\uAC00 \uC911\uBCF5\uB429\uB2C8\uB2E4"
        });
      }
      if (rows.has(slot.assignment.rosterEntryId)) {
        context.addIssue({
          code: "custom",
          path: ["slots", index, "assignment", "rosterEntryId"],
          message: "\uB85C\uC2A4\uD130 \uD589\uC774 \uC5EC\uB7EC \uC2AC\uB86F\uC5D0 \uBC30\uC815\uB410\uC2B5\uB2C8\uB2E4"
        });
      }
      assignments.add(slot.assignment.id);
      rows.add(slot.assignment.rosterEntryId);
    }
    if (slot.fulfillment) {
      if (slot.fulfillment.scenarioId !== snapshot.scenarioId || slot.fulfillment.rosterEntryId !== slot.assignment.rosterEntryId) {
        context.addIssue({
          code: "custom",
          path: ["slots", index, "fulfillment"],
          message: "\uB0A9\uD488 \uADFC\uAC70\uAC00 \uD604\uC7AC \uCEA0\uD398\uC778\uACFC \uBC30\uC815\uC5D0 \uB9DE\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4"
        });
      }
      if (deliveries.has(slot.fulfillment.id)) {
        context.addIssue({
          code: "custom",
          path: ["slots", index, "fulfillment", "id"],
          message: "\uB0A9\uD488 \uADFC\uAC70\uAC00 \uC5EC\uB7EC \uC2AC\uB86F\uC5D0 \uADC0\uC18D\uB410\uC2B5\uB2C8\uB2E4"
        });
      }
      deliveries.add(slot.fulfillment.id);
    }
  }
});
function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function isTimestamp(value) {
  return hasText(value) && Number.isFinite(Date.parse(value));
}
function isTargetSlotState(value) {
  return Object.values(TargetSlotState).includes(value);
}
function isValidTargetSlotSnapshot(value) {
  return targetSlotSnapshotSchema.safeParse(value).success;
}
function createTargetSlotSnapshot(scenarioId, slotIds) {
  return targetSlotSnapshotSchema.parse({
    scenarioId,
    boardRevision: 0,
    slots: slotIds.map((id) => ({
      id,
      slotRevision: 0,
      lifecycleState: TargetSlotState.EMPTY,
      assignment: null,
      fulfillment: null
    }))
  });
}
function deliveryFromRow(row) {
  if (!isRosterUploadComplete(row.rosterStatus, row) || row.deliveryValid !== true || !hasText(row.deliveryId) || !isTimestamp(row.uploadCompletedAt))
    return null;
  return {
    id: row.deliveryId,
    scenarioId: row.scenarioId,
    rosterEntryId: row.rosterEntryId,
    uploadCompletedAt: row.uploadCompletedAt
  };
}
function currentAssignmentMatches(slot, assignmentId, rosterEntryId) {
  return slot.assignment?.id === assignmentId && slot.assignment.rosterEntryId === rosterEntryId;
}
function deliveryIsAllocated(snapshot, slotId, deliveryId) {
  return snapshot.slots.some(
    (other) => other.id !== slotId && other.fulfillment?.id === deliveryId
  );
}
function applyTargetSlotAction(snapshot, action) {
  const fail = (code) => ({ ok: false, code });
  if (!isValidTargetSlotSnapshot(snapshot)) return fail("INVALID_SNAPSHOT");
  if (!hasText(action.actionId) || !hasText(action.actorId) || !hasText(action.slotId) || !isTimestamp(action.occurredAt) || !Number.isSafeInteger(action.expectedSlotRevision) || action.expectedSlotRevision < 0) {
    return fail("INVALID_ACTION");
  }
  if (snapshot.boardRevision === Number.MAX_SAFE_INTEGER) return fail("INVALID_SNAPSHOT");
  const slot = snapshot.slots.find((item) => item.id === action.slotId);
  if (!slot) return fail("SLOT_NOT_FOUND");
  if (slot.slotRevision !== action.expectedSlotRevision) return fail("STALE_SLOT_REVISION");
  if (slot.slotRevision === Number.MAX_SAFE_INTEGER) return fail("INVALID_SNAPSHOT");
  if ("row" in action) {
    if (action.row.scenarioId !== snapshot.scenarioId) return fail("SCENARIO_MISMATCH");
    if (!hasText(action.row.rosterEntryId) || typeof action.row.dropped !== "boolean")
      return fail("INVALID_ACTION");
  }
  if ("assignmentId" in action && !hasText(action.assignmentId)) return fail("INVALID_ACTION");
  const unchanged = () => ({ ok: true, snapshot, transition: null });
  let next;
  switch (action.type) {
    case "ASSIGN": {
      if (!hasText(action.conditionsRevisionId)) return fail("INVALID_ACTION");
      if (slot.lifecycleState !== TargetSlotState.EMPTY && slot.lifecycleState !== TargetSlotState.REPLACEMENT_NEEDED) {
        return fail("INVALID_TRANSITION");
      }
      if (action.row.dropped) return fail("PARTICIPANT_DROPPED");
      if (action.eligibility.conditionsSatisfied !== true) return fail("CONDITIONS_NOT_SATISFIED");
      if (action.eligibility.selectionSatisfied !== true) return fail("SELECTION_NOT_SATISFIED");
      if (snapshot.slots.some((other) => other.assignment?.id === action.assignmentId))
        return fail("ASSIGNMENT_ALREADY_USED");
      if (snapshot.slots.some((other) => other.assignment?.rosterEntryId === action.row.rosterEntryId))
        return fail("ROSTER_ALREADY_ASSIGNED");
      const fulfillment = deliveryFromRow(action.row);
      if (isRosterUploadComplete(action.row.rosterStatus, action.row) && !fulfillment)
        return fail("VALID_DELIVERY_REQUIRED");
      if (fulfillment && deliveryIsAllocated(snapshot, slot.id, fulfillment.id))
        return fail("DELIVERY_ALREADY_ALLOCATED");
      const assignment = {
        id: action.assignmentId,
        rosterEntryId: action.row.rosterEntryId,
        assignedAt: action.occurredAt,
        conditionsRevisionId: action.conditionsRevisionId
      };
      next = fulfillment ? {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.FULFILLED,
        assignment,
        fulfillment
      } : {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.ASSIGNED,
        assignment,
        fulfillment: null
      };
      break;
    }
    case "RECORD_UPLOAD_COMPLETION": {
      if (!currentAssignmentMatches(slot, action.assignmentId, action.row.rosterEntryId))
        return fail("ASSIGNMENT_MISMATCH");
      if (action.row.dropped) return fail("PARTICIPANT_DROPPED");
      const fulfillment = deliveryFromRow(action.row);
      if (!fulfillment) return fail("VALID_DELIVERY_REQUIRED");
      if (slot.lifecycleState === TargetSlotState.FULFILLED) {
        return slot.fulfillment.id === fulfillment.id && slot.fulfillment.uploadCompletedAt === fulfillment.uploadCompletedAt ? unchanged() : fail("DELIVERY_MISMATCH");
      }
      if (slot.lifecycleState !== TargetSlotState.ASSIGNED) return fail("INVALID_TRANSITION");
      if (deliveryIsAllocated(snapshot, slot.id, fulfillment.id))
        return fail("DELIVERY_ALREADY_ALLOCATED");
      next = {
        ...slot,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.FULFILLED,
        fulfillment
      };
      break;
    }
    case "RECORD_PARTICIPANT_DROP": {
      if (!currentAssignmentMatches(slot, action.assignmentId, action.rosterEntryId))
        return fail("ASSIGNMENT_MISMATCH");
      if (slot.lifecycleState === TargetSlotState.FULFILLED) return unchanged();
      if (slot.lifecycleState !== TargetSlotState.ASSIGNED) return fail("INVALID_TRANSITION");
      next = {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.REPLACEMENT_NEEDED,
        assignment: null,
        fulfillment: null
      };
      break;
    }
    case "INVALIDATE_DELIVERY": {
      if (!hasText(action.reason)) return fail("REASON_REQUIRED");
      if (!currentAssignmentMatches(slot, action.assignmentId, action.row.rosterEntryId))
        return fail("ASSIGNMENT_MISMATCH");
      if (slot.lifecycleState !== TargetSlotState.FULFILLED) return fail("INVALID_TRANSITION");
      if (slot.fulfillment.id !== action.deliveryId) return fail("DELIVERY_MISMATCH");
      next = action.row.dropped ? {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.REPLACEMENT_NEEDED,
        assignment: null,
        fulfillment: null
      } : {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.ASSIGNED,
        assignment: slot.assignment,
        fulfillment: null
      };
      break;
    }
    case "CANCEL_OBLIGATION": {
      if (!hasText(action.reason)) return fail("REASON_REQUIRED");
      if (slot.lifecycleState === TargetSlotState.FULFILLED || slot.lifecycleState === TargetSlotState.CANCELLED) {
        return fail("INVALID_TRANSITION");
      }
      next = {
        id: slot.id,
        slotRevision: slot.slotRevision + 1,
        lifecycleState: TargetSlotState.CANCELLED,
        assignment: null,
        fulfillment: null
      };
      break;
    }
    default:
      return fail("INVALID_ACTION");
  }
  const updated = targetSlotSnapshotSchema.parse({
    ...snapshot,
    boardRevision: snapshot.boardRevision + 1,
    slots: snapshot.slots.map((item) => item.id === slot.id ? next : item)
  });
  return {
    ok: true,
    snapshot: updated,
    transition: {
      actionId: action.actionId,
      scenarioId: snapshot.scenarioId,
      slotId: slot.id,
      actionType: action.type,
      actorId: action.actorId,
      occurredAt: action.occurredAt,
      fromSlotRevision: slot.slotRevision,
      toSlotRevision: next.slotRevision,
      fromBoardRevision: snapshot.boardRevision,
      toBoardRevision: updated.boardRevision,
      before: slot,
      after: next,
      reason: "reason" in action ? action.reason : null
    }
  };
}

// src/domain/campaign/target-slot-progress.ts
function deriveTargetSlotCounts(snapshot, deliveries) {
  if (!isValidTargetSlotSnapshot(snapshot)) throw new Error("INVALID_TARGET_SLOT_SNAPSHOT");
  const seen = /* @__PURE__ */ new Map();
  const valid = /* @__PURE__ */ new Map();
  const rows = /* @__PURE__ */ new Set();
  for (const delivery of deliveries) {
    if (!delivery.id.trim() || !delivery.rosterEntryId.trim() || delivery.scenarioId !== snapshot.scenarioId || !Number.isFinite(Date.parse(delivery.uploadCompletedAt)) || typeof delivery.valid !== "boolean")
      throw new Error("INVALID_TARGET_SLOT_DELIVERIES");
    const previous = seen.get(delivery.id);
    if (previous) {
      if (previous.rosterEntryId !== delivery.rosterEntryId || previous.valid !== delivery.valid || previous.uploadCompletedAt !== delivery.uploadCompletedAt)
        throw new Error("INVALID_TARGET_SLOT_DELIVERIES");
      continue;
    }
    seen.set(delivery.id, delivery);
    if (!delivery.valid) continue;
    if (rows.has(delivery.rosterEntryId)) throw new Error("INVALID_TARGET_SLOT_DELIVERIES");
    rows.add(delivery.rosterEntryId);
    valid.set(delivery.id, delivery);
  }
  let fulfilledSlots = 0;
  for (const slot of snapshot.slots) {
    if (!slot.fulfillment) continue;
    const evidence = valid.get(slot.fulfillment.id);
    if (!evidence || evidence.rosterEntryId !== slot.fulfillment.rosterEntryId || evidence.uploadCompletedAt !== slot.fulfillment.uploadCompletedAt) {
      throw new Error("TARGET_SLOT_DELIVERY_MISMATCH");
    }
    fulfilledSlots++;
  }
  return {
    targetSlots: snapshot.slots.filter((slot) => slot.lifecycleState !== TargetSlotState.CANCELLED).length,
    fulfilledSlots,
    extraDeliveries: valid.size - fulfilledSlots,
    totalDeliveries: valid.size
  };
}

// src/domain/campaign/condition-change.ts
var CONDITION_AGREED_EVENT = "CONDITION_AGREED";
var FIELD_OF_REQUIREMENT = {
  countryCodes: "countryCode",
  productIds: "productId",
  contentFormats: "contentFormat",
  uploadDeadline: "uploadDeadline",
  minFollowers: "followers"
};
var ALL_FIELDS = [
  "countryCode",
  "productId",
  "contentFormat",
  "uploadDeadline",
  "followers"
];
var EMPTY_CONDITIONS = {
  countryCode: null,
  productId: null,
  contentFormat: null,
  uploadDeadline: null,
  followers: null
};
function issuesByField(issues) {
  const map = /* @__PURE__ */ new Map();
  for (const issue of issues) map.set(FIELD_OF_REQUIREMENT[issue.field], issue.code);
  return map;
}
function agreedAfterChange(row, changedAt) {
  return changedAt !== null && row.agreedAt != null && row.agreedAt >= changedAt;
}
function buckets(baseline, after, promised) {
  const result = {};
  if (promised === null) {
    for (const field of ALL_FIELDS) result[field] = "UNKNOWN";
    return result;
  }
  const wasBroken = issuesByField(evaluateTargetSlotConditions(baseline, promised));
  const isBroken = issuesByField(evaluateTargetSlotConditions(after, promised));
  for (const field of ALL_FIELDS) {
    const now = isBroken.get(field);
    if (now === "EVIDENCE_MISSING") result[field] = "UNKNOWN";
    else if (now === "CONDITION_MISMATCH" && wasBroken.get(field) === void 0)
      result[field] = "DIFFERS";
    else result[field] = "SAME";
  }
  return result;
}
function classifyConditionChange(baseline, after, promised) {
  return buckets(baseline, after, promised);
}
function buildConditionChangeTargets(baseline, after, rows, changedAt = null) {
  const targets = [];
  for (const row of rows) {
    if (agreedAfterChange(row, changedAt)) continue;
    const verdict = buckets(baseline, after, row.promised);
    const fields = ALL_FIELDS.filter((field) => verdict[field] === "DIFFERS");
    if (fields.length > 0) targets.push({ rosterEntryId: row.rosterEntryId, fields });
  }
  return targets;
}
function summarizeConditionChange(baseline, after, rows, changedAt = null) {
  const summary = { differs: 0, same: 0, unknown: 0 };
  for (const row of rows) {
    const verdict = buckets(baseline, after, row.promised);
    if (agreedAfterChange(row, changedAt) && row.promised !== null) summary.same += 1;
    else if (ALL_FIELDS.some((field) => verdict[field] === "DIFFERS")) summary.differs += 1;
    else if (ALL_FIELDS.some((field) => verdict[field] === "UNKNOWN")) summary.unknown += 1;
    else summary.same += 1;
  }
  return summary;
}
function isConditionAgreed(events) {
  return events.some((event) => event.type === CONDITION_AGREED_EVENT);
}
function resolveAppliedConditions(promised, _required) {
  return promised === null ? { ...EMPTY_CONDITIONS } : { ...promised };
}

// src/domain/campaign/settlement-withholding.ts
function computeSettlementWithholding(amount, kind) {
  if (!SETTLEMENT_AMOUNT_PATTERN.test(amount)) return null;
  const gross = Number(amount.split(".")[0]);
  if (kind === SettlementKind.PRODUCT_PURCHASE_REIMBURSEMENT) {
    return { gross, incomeTax: 0, localTax: 0, net: gross, withheld: false };
  }
  const incomeTax = Math.floor(gross * 3 / 1e3) * 10;
  const localTax = Math.floor(gross * 3 / 1e4) * 10;
  return { gross, incomeTax, localTax, net: gross - incomeTax - localTax, withheld: true };
}

// src/domain/campaign/domestic-settlement-summary.ts
var DOMESTIC_QUEUE_TAB_STATUS = {
  pending: SettlementStatus.REQUESTED,
  onHold: SettlementStatus.ON_HOLD,
  paid: SettlementStatus.PAID
};
var DAY_MS2 = 24 * 60 * 60 * 1e3;
function elapsedDays(requestedAt, now) {
  return Math.max(0, Math.floor((now.getTime() - new Date(requestedAt).getTime()) / DAY_MS2));
}
function personKey(item) {
  return `${item.scenario.id}\0${item.creator.handle ?? item.entry.rosterEntryId}`;
}
function summarizeDomesticQueue(items, now) {
  let gross = 0;
  let net = 0;
  const missing = /* @__PURE__ */ new Set();
  let oldest = null;
  const byKind = /* @__PURE__ */ new Map();
  const countByScenario = {};
  for (const item of items) {
    const withholding = computeSettlementWithholding(item.entry.amount, item.entry.kind);
    gross += withholding?.gross ?? 0;
    net += withholding?.net ?? 0;
    const kind = byKind.get(item.entry.kind) ?? {
      kind: item.entry.kind,
      count: 0,
      gross: 0,
      net: 0
    };
    kind.count += 1;
    kind.gross += withholding?.gross ?? 0;
    kind.net += withholding?.net ?? 0;
    byKind.set(item.entry.kind, kind);
    countByScenario[item.scenario.id] = (countByScenario[item.scenario.id] ?? 0) + 1;
    if (item.documentsReceivedAt === null) missing.add(personKey(item));
    const days = elapsedDays(item.requestedAt, now);
    oldest = oldest === null ? days : Math.max(oldest, days);
  }
  return {
    count: items.length,
    gross,
    net,
    missingDocsPeople: missing.size,
    oldestDays: oldest,
    byKind: [...byKind.values()].sort((a, b) => a.kind.localeCompare(b.kind)),
    countByScenario
  };
}

// src/domain/campaign/snapshot-revision.ts
function toTime2(createdAt) {
  return createdAt instanceof Date ? createdAt.getTime() : new Date(createdAt).getTime();
}
function deriveEffectiveRosterSnapshot(origin, revisions) {
  const ordered = revisions.map((revision, index) => ({ revision, index })).sort((a, b) => {
    const gap = toTime2(a.revision.createdAt) - toTime2(b.revision.createdAt);
    return gap !== 0 ? gap : a.index - b.index;
  });
  const effective = { ...origin };
  for (const { revision } of ordered) {
    if (revision.creatorHandleSnapshot != null) {
      effective.creatorHandleSnapshot = revision.creatorHandleSnapshot;
    }
    if (revision.creatorNicknameSnapshot != null) {
      effective.creatorNicknameSnapshot = revision.creatorNicknameSnapshot;
    }
    if (revision.tiktokUrlSnapshot != null) {
      effective.tiktokUrlSnapshot = revision.tiktokUrlSnapshot;
    }
    if (revision.followersSnapshot != null) {
      effective.followersSnapshot = revision.followersSnapshot;
    }
  }
  return effective;
}

// src/domain/campaign/video-extra-post.ts
var VideoPostPlatform = {
  TIKTOK: "TIKTOK",
  INSTAGRAM: "INSTAGRAM",
  YOUTUBE: "YOUTUBE",
  OTHER: "OTHER"
};
var VIDEO_POST_PLATFORM_LABEL = {
  TIKTOK: "\uD2F1\uD1A1",
  INSTAGRAM: "\uC778\uC2A4\uD0C0\uADF8\uB7A8",
  YOUTUBE: "\uC720\uD29C\uBE0C",
  OTHER: "\uAE30\uD0C0"
};
var VideoPostRole = {
  /** 계약상 올려야 하는 게시물 — 행의 `videoUrl`. */
  PRIMARY: "PRIMARY",
  /** 크리에이터가 스스로 더 올린 게시물. */
  EXTRA: "EXTRA"
};
var VideoPostStatus = {
  LIVE: "LIVE",
  TAKEN_DOWN: "TAKEN_DOWN"
};
var PLATFORM_DOMAINS = [
  [VideoPostPlatform.TIKTOK, ["tiktok.com"]],
  [VideoPostPlatform.INSTAGRAM, ["instagram.com"]],
  [VideoPostPlatform.YOUTUBE, ["youtube.com", "youtu.be"]]
];
function hostOf(normalizedUrl) {
  const authority = normalizedUrl.replace(/^[a-z][a-z0-9+.-]*:\/\//, "").split("/")[0] ?? "";
  const host = authority.slice(authority.lastIndexOf("@") + 1);
  return host.replace(/:\d+$/, "");
}
function detectVideoPostPlatform(url) {
  const normalized = normalizeVideoUrl(url);
  if (normalized === null) {
    return null;
  }
  const host = hostOf(normalized);
  for (const [platform, domains] of PLATFORM_DOMAINS) {
    if (domains.some((domain) => host === domain || host.endsWith(`.${domain}`))) {
      return platform;
    }
  }
  return VideoPostPlatform.OTHER;
}
function checkExtraPostUrl(input) {
  const normalizedUrl = normalizeVideoUrl(input.url);
  const platform = detectVideoPostPlatform(input.url);
  if (normalizedUrl === null || platform === null) {
    return { ok: false, reason: "MALFORMED_URL" };
  }
  if (input.primaryVideoUrl !== null && normalizeVideoUrl(input.primaryVideoUrl) === normalizedUrl) {
    return { ok: false, reason: "SAME_AS_PRIMARY" };
  }
  const duplicate = input.existing.some(
    (post) => post.deletedAt === null && normalizeVideoUrl(post.url) === normalizedUrl
  );
  if (duplicate) {
    return { ok: false, reason: "DUPLICATE_POST" };
  }
  return { ok: true, normalizedUrl, platform };
}
function listContentPosts(input) {
  const posts = [];
  if (input.primaryVideoUrl !== null) {
    posts.push({
      role: VideoPostRole.PRIMARY,
      platform: detectVideoPostPlatform(input.primaryVideoUrl),
      url: input.primaryVideoUrl,
      status: null,
      id: null
    });
  }
  for (const extra of input.extras) {
    if (extra.deletedAt !== null) {
      continue;
    }
    posts.push({
      role: VideoPostRole.EXTRA,
      platform: extra.platform,
      url: extra.url,
      status: extra.takenDownAt === null ? VideoPostStatus.LIVE : VideoPostStatus.TAKEN_DOWN,
      id: extra.id
    });
  }
  return posts;
}

// src/domain/campaign/video-post-retention.ts
var POST_RETENTION_DAYS = 90;
var DAY_MS3 = 24 * 60 * 60 * 1e3;
var KST_OFFSET_MS = 9 * 60 * 60 * 1e3;
function retentionStartOf(input) {
  if (input.replacementPublishedAt) return input.replacementPublishedAt;
  if (input.uploadDate) return new Date(input.uploadDate.getTime() - KST_OFFSET_MS);
  return input.uploadCompletedAt ?? input.publishedAt;
}
function retentionWindow(input) {
  const startsAt = retentionStartOf(input);
  if (!startsAt) return null;
  return { startsAt, endsAt: new Date(startsAt.getTime() + POST_RETENTION_DAYS * DAY_MS3) };
}
function summarizeCreatorPosts(rows, extrasByRow) {
  let extraPostCount = 0;
  for (const row of rows) {
    extraPostCount += (extrasByRow.get(row.id) ?? []).filter(
      (post) => post.deletedAt === null
    ).length;
  }
  return {
    contractVideoCount: rows.length,
    retentionTargetCount: rows.filter((row) => row.videoUrl !== null).length,
    extraPostCount
  };
}

// src/domain/campaign/video-retention-assessment.ts
function retentionBusinessDate(timestamp) {
  return toKstDateString(new Date(Date.parse(timestamp)));
}
function assessVideoRetention(startsAt, until, checks, reviews, now = (/* @__PURE__ */ new Date()).toISOString()) {
  const empty = { result: null, hasViolation: false, missingDays: 0, unresolvedDays: 0 };
  if (!startsAt || !until) return empty;
  const first = retentionBusinessDate(startsAt);
  const last = retentionBusinessDate(until);
  const scoped = checks.filter(
    (check) => check.businessDate >= first && check.businessDate <= last
  );
  const reviewOf = new Map(reviews.map((review) => [review.checkId, review.verdict]));
  const hasViolation = scoped.some(
    (check) => check.outcome === "GONE" || reviews.some((review) => review.checkId === check.checkId && review.verdict === "PRIVATE")
  );
  const days = /* @__PURE__ */ new Map();
  for (const check of scoped)
    days.set(check.businessDate, [...days.get(check.businessDate) ?? [], check]);
  const final = scoped.some(
    (check) => check.jobKind !== "RECOVERY" && check.isFinal && check.businessDate === last && Date.parse(check.checkedAt) >= Date.parse(until)
  );
  const closedDay = retentionBusinessDate(new Date(Date.parse(now) - 18 * 36e5).toISOString());
  const through = final ? last : closedDay < last ? closedDay : last;
  let missingDays = 0;
  let unresolvedDays = 0;
  for (let day = Date.parse(`${first}T00:00:00Z`); day <= Date.parse(`${through}T00:00:00Z`); day += 864e5) {
    const date = toUtcDateString(new Date(day));
    const daily = days.get(date) ?? [];
    const cutoff = Math.max(
      day + 9 * 36e5,
      date === first ? Date.parse(startsAt) + 6 * 36e5 : 0,
      date === last ? Date.parse(until) + 6 * 36e5 : 0
    );
    if (!final && Date.parse(now) < cutoff) continue;
    const finalMissing = daily.some(
      (check) => check.jobKind !== "RECOVERY" && check.isFinal && check.outcome === "MISSING"
    );
    if (finalMissing || !daily.some((check) => check.outcome !== "MISSING")) missingDays += 1;
    if (daily.some(
      (check) => check.outcome === "RESTRICTED" && reviewOf.get(check.checkId) !== "PUBLIC"
    ))
      unresolvedDays += 1;
  }
  return {
    result: final ? hasViolation ? "UNSATISFIED" : missingDays || unresolvedDays ? "REVIEW_REQUIRED" : "SATISFIED" : null,
    hasViolation,
    missingDays,
    unresolvedDays
  };
}
function readPostAccess(trackings) {
  const readings = trackings.map((tracking) => {
    const check = tracking.visibilityChecks[0];
    if (!check) return { state: "UNKNOWN", checkedAt: null };
    return {
      state: accessOf(check.outcome, check.reviews[0]?.verdict),
      checkedAt: check.checkedAt
    };
  });
  const [first] = readings;
  if (!first || readings.some((reading) => reading.state !== first.state))
    return { state: "UNKNOWN", checkedAt: null };
  const checkedAt = readings.map((reading) => reading.checkedAt).filter((at) => at !== null).sort()[0];
  return { state: first.state, checkedAt: checkedAt ?? null };
}
function accessOf(outcome, verdict) {
  if (verdict === "RESTORED" || verdict === "PUBLIC") return "PUBLIC";
  if (verdict === "PRIVATE" || verdict === "PLATFORM_RESTRICTED") return "RESTRICTED";
  if (outcome === "PUBLIC" || outcome === "RESTRICTED" || outcome === "GONE") return outcome;
  return "UNKNOWN";
}

// src/domain/campaign/contract-video.ts
var FIRST_CONTRACT_VIDEO_NO = 1;
function nextContractVideoNo(siblings) {
  const max = siblings.reduce(
    (acc, sibling) => Math.max(acc, sibling.contractVideoNo),
    FIRST_CONTRACT_VIDEO_NO - 1
  );
  return max + 1;
}
function planAdditionalContractVideo(source, siblings) {
  if (source.deletedAt !== null) {
    return { ok: false, reason: "SOURCE_DELETED" };
  }
  if (source.droppedAt !== null) {
    return { ok: false, reason: "SOURCE_DROPPED" };
  }
  return {
    ok: true,
    contractVideoNo: nextContractVideoNo(siblings),
    rosterStatus: RosterStatus.GUIDELINE_SENT,
    approvalSourceEntryId: source.id,
    row: {
      scenarioId: source.scenarioId,
      sourceSystem: source.sourceSystem,
      externalCreatorId: source.externalCreatorId,
      creatorHandleSnapshot: source.creatorHandleSnapshot,
      creatorNicknameSnapshot: source.creatorNicknameSnapshot,
      assigneeId: source.assigneeId,
      videoUrl: null
    }
  };
}

// src/domain/creator-mail/archive-routing.ts
function isAutomatedArchiveReply(headers) {
  const autoSubmitted = headers.autoSubmitted?.trim().toLowerCase();
  return autoSubmitted !== void 0 && autoSubmitted !== "" && autoSubmitted !== "no" || Boolean(headers.xAutoreply?.trim()) || Boolean(headers.xAutorespond?.trim());
}
function tagOf(address, archiveAddress) {
  const at = archiveAddress.lastIndexOf("@");
  const archiveLocal = archiveAddress.slice(0, at).toLowerCase();
  const archiveDomain = archiveAddress.slice(at + 1).toLowerCase();
  const match = /([^\s<>,;"]+)\+([^\s<>@,;"]+)@([^\s<>,;"]+)/.exec(address);
  const local = match?.[1];
  const tag = match?.[2];
  const domain = match?.[3];
  if (!local || !tag || !domain) return null;
  if (local.toLowerCase() !== archiveLocal) return null;
  if (domain.toLowerCase() !== archiveDomain) return null;
  return tag.toLowerCase();
}
function knownTagIn(lines, context) {
  for (const line of lines) {
    const tag = tagOf(line, context.archiveAddress);
    if (tag !== null && context.knownTokens.has(tag)) return tag;
  }
  return null;
}
function addressOf(value) {
  return (/<([^>]+)>/.exec(value)?.[1] ?? value).trim().toLowerCase();
}
function normalizeMessageId(value) {
  return value.trim().replace(/^<|>$/g, "").toLowerCase();
}
function tokenByMessageId(candidate, context) {
  const wanted = normalizeMessageId(candidate);
  for (const [messageId, token] of context.sentMessageTokens) {
    if (normalizeMessageId(messageId) === wanted) return token;
  }
  return null;
}
function classifyArchiveMessage(headers, context) {
  const replyToTag = knownTagIn(headers.replyTo, context);
  const recipientTag = knownTagIn([...headers.deliveredTo, ...headers.to, ...headers.cc], context);
  if (replyToTag !== null && recipientTag === null) {
    const from = headers.from ? addressOf(headers.from) : null;
    return from !== null && context.senderAddresses.has(from) ? { kind: "SENT_COPY", token: replyToTag } : { kind: "UNRESOLVED" };
  }
  if (recipientTag !== null) {
    return { kind: "REPLY", token: recipientTag, via: "TAG" };
  }
  if (headers.inReplyTo !== null) {
    const token = tokenByMessageId(headers.inReplyTo, context);
    if (token !== null) return { kind: "REPLY", token, via: "HEADER" };
  }
  for (const reference of [...headers.references].reverse()) {
    const token = tokenByMessageId(reference, context);
    if (token !== null) return { kind: "REPLY", token, via: "HEADER" };
  }
  return { kind: "UNRESOLVED" };
}

// src/domain/creator-mail/assignment.ts
function assigneeAfterSend(currentAssigneeId, senderId) {
  return currentAssigneeId ?? senderId;
}

// src/domain/creator-mail/bulk-reply-match.ts
var BOUNCE_LOCAL_PARTS = /* @__PURE__ */ new Set(["mailer-daemon", "postmaster"]);
var SUBJECT_PREFIX = /^\s*(re|fw|fwd|aw|wg|sv|답장|회신|전달)\s*(\[\d+\])?\s*:\s*/i;
function normalizeSubject(subject) {
  let current = subject;
  let previous;
  do {
    previous = current;
    current = current.replace(SUBJECT_PREFIX, "");
  } while (current !== previous);
  return current.trim().toLowerCase();
}
function isAutomated(headers) {
  const autoSubmitted = headers.autoSubmitted?.trim().toLowerCase();
  return autoSubmitted !== void 0 && autoSubmitted !== "" && autoSubmitted !== "no" || Boolean(headers.xAutoreply?.trim()) || Boolean(headers.xAutorespond?.trim());
}
function parseFromAddress(from) {
  if (!from) return null;
  const angle = /<([^<>\s]+@[^<>\s]+)>/.exec(from);
  const bare = angle ? angle[1] : /([^\s"<>,;]+@[^\s"<>,;]+)/.exec(from)?.[1];
  return bare ? bare.trim().toLowerCase() : null;
}
function matchBulkReply(headers, context) {
  const from = parseFromAddress(headers.from);
  if (from === null) return { kind: "UNRESOLVED", reason: "NO_FROM" };
  const local = from.split("@")[0] ?? "";
  const contentType = headers.contentType?.toLowerCase() ?? "";
  if (BOUNCE_LOCAL_PARTS.has(local) || contentType.startsWith("multipart/report")) {
    return { kind: "IGNORED", reason: "BOUNCE" };
  }
  if (isAutomated(headers)) return { kind: "IGNORED", reason: "AUTOMATED" };
  if (context.ourAddresses.has(from)) return { kind: "IGNORED", reason: "OUR_COPY" };
  const reached = context.candidates.filter(
    (candidate) => candidate.recipientEmails.some((email) => email.trim().toLowerCase() === from)
  );
  if (reached.length === 0) return { kind: "UNRESOLVED", reason: "NO_RECIPIENT" };
  const pick = (rounds) => {
    const scenarios = new Set(rounds.map((round) => round.scenarioId));
    if (scenarios.size !== 1) return null;
    const [first] = rounds;
    if (!first) return null;
    return { kind: "MATCHED", scenarioId: first.scenarioId, roundId: first.roundId };
  };
  const single = pick(reached);
  if (single) return single;
  const subject = normalizeSubject(headers.subject ?? "");
  const bySubject = reached.filter((round) => normalizeSubject(round.roundSubject) === subject);
  return pick(bySubject) ?? { kind: "UNRESOLVED", reason: "AMBIGUOUS" };
}

// src/domain/creator-mail/conversation-filter.ts
function selectConversationMessages(messages, token) {
  const wanted = token.toLowerCase();
  return messages.filter((message) => {
    const route = message.route;
    if (route.kind === "UNRESOLVED") return false;
    return route.token.toLowerCase() === wanted;
  });
}

// src/domain/creator-mail/draft-takeover.ts
var CREATOR_MAIL_DRAFT_LEASE_MS = 12e4;
var CREATOR_MAIL_DRAFT_HEARTBEAT_MS = 3e4;
function creatorMailDraftLeaseExpiry(now) {
  return new Date(now.getTime() + CREATOR_MAIL_DRAFT_LEASE_MS);
}
function isCreatorMailDraftLeaseAlive(leaseExpiresAt, now) {
  if (leaseExpiresAt === null || leaseExpiresAt === void 0) return false;
  return new Date(leaseExpiresAt).getTime() > now.getTime();
}
var isTakeover = (value) => {
  if (typeof value !== "object" || value === null) return false;
  const row = value;
  return typeof row.fromUserId === "string" && typeof row.toUserId === "string" && typeof row.at === "string";
};
function readCreatorMailDraftTakeovers(stored) {
  if (!Array.isArray(stored)) return [];
  return stored.filter(isTakeover).map(({ fromUserId, toUserId, at }) => ({
    fromUserId,
    toUserId,
    at
  }));
}
function creatorMailDraftHistory(input) {
  const takeovers = readCreatorMailDraftTakeovers(input.takeovers);
  if (input.originAuthorId === null || takeovers.length === 0) return null;
  return { originAuthorId: input.originAuthorId, takeovers };
}

// src/domain/creator-mail/our-turn.ts
function isOurTurn(row) {
  return row.lastRepliedAt !== null && (row.lastContactedAt === null || row.lastContactedAt.getTime() < row.lastRepliedAt.getTime());
}

// src/domain/creator-mail/reply-neglect.ts
import { addDays as addDays2, isWeekend as isWeekend3 } from "date-fns";
var REPLY_NEGLECT_BUSINESS_DAYS = 2;
function isReplyNeglected({
  repliedAt,
  lastFollowUpAt,
  now,
  holidays,
  thresholdBusinessDays = REPLY_NEGLECT_BUSINESS_DAYS
}) {
  if (lastFollowUpAt !== null && lastFollowUpAt.getTime() > repliedAt.getTime()) {
    return false;
  }
  if (thresholdBusinessDays <= 0) return true;
  const today = toKstDateString(now);
  let cursor = kstDate(repliedAt);
  let elapsed = 0;
  for (let i = 0; i < 366; i += 1) {
    cursor = addDays2(cursor, 1);
    const date = toKstDateString(cursor);
    if (date > today) break;
    if (isWeekend3(cursor) || holidays.has(date)) continue;
    elapsed += 1;
    if (elapsed >= thresholdBusinessDays) return true;
  }
  return false;
}

// src/domain/creator-mail/reply-urgency.ts
import { addDays as addDays3, isWeekend as isWeekend4 } from "date-fns";
function toDate4(value) {
  if (value === null) return null;
  return toKstDateString(typeof value === "string" ? new Date(value) : value);
}
function earliest(dates) {
  return dates.reduce(
    (min, date) => date !== null && (min === null || date < min) ? date : min,
    null
  );
}
function resolveReplyDeadline(input) {
  const upload = input.entries.filter((entry) => entry.droppedAt === null && entry.uploadCompletedAt === null).map(
    (entry) => entry.participantUploadDeadline ?? input.commonUploadDeadline ?? toDate4(entry.uploadDate)
  );
  const campaign = toDate4(input.internalDeadline) ?? toDate4(input.advertiserDeadline) ?? toDate4(input.deadline);
  return earliest([...upload, campaign]);
}
function isReplyDeadlineAtRisk(deadline, now, holidays, thresholdBusinessDays = REPLY_NEGLECT_BUSINESS_DAYS) {
  if (deadline === null) return false;
  const today = toKstDateString(now);
  if (deadline <= today) return true;
  let cursor = kstDate(now);
  let businessDays = 0;
  for (let i = 0; i < 366; i += 1) {
    cursor = addDays3(cursor, 1);
    const date = toKstDateString(cursor);
    if (!isWeekend4(cursor) && !holidays.has(date)) businessDays += 1;
    if (date >= deadline) return businessDays <= thresholdBusinessDays;
    if (businessDays > thresholdBusinessDays) return false;
  }
  return false;
}
function compareReplyUrgency(a, b) {
  if (a.deadline !== b.deadline) {
    if (a.deadline === null) return 1;
    if (b.deadline === null) return -1;
    return a.deadline < b.deadline ? -1 : 1;
  }
  if (a.followedUp !== b.followedUp) return a.followedUp ? 1 : -1;
  const repliedDiff = new Date(a.repliedAt).getTime() - new Date(b.repliedAt).getTime();
  if (repliedDiff !== 0) return repliedDiff;
  if (a.conversationId === b.conversationId) return 0;
  return a.conversationId < b.conversationId ? -1 : 1;
}

// src/domain/creator-mail/silence-watch.ts
var POLLING_STALE_THRESHOLD_MINUTES = 30;
var ARCHIVE_COPY_GRACE_MINUTES = 15;
function readPollingSilence({
  lastSuccessAt,
  now,
  thresholdMinutes = POLLING_STALE_THRESHOLD_MINUTES
}) {
  if (lastSuccessAt === null) return { kind: "NEVER" };
  const minutesSince = Math.floor((now.getTime() - lastSuccessAt.getTime()) / 6e4);
  const kind = minutesSince >= thresholdMinutes ? "STALE" : "OK";
  return { kind, lastSuccessAt, minutesSince };
}
function isArchiveCopyMissing({
  lastContactedAt,
  lastSentCopyAt,
  now,
  graceMinutes = ARCHIVE_COPY_GRACE_MINUTES
}) {
  if (lastContactedAt === null) return false;
  if (lastSentCopyAt !== null && lastSentCopyAt.getTime() >= lastContactedAt.getTime()) {
    return false;
  }
  return now.getTime() - lastContactedAt.getTime() > graceMinutes * 6e4;
}
function classifyArchiveGap({
  polling,
  missingCopyCount
}) {
  if (polling.kind !== "OK") return "POLLING_STOPPED";
  return missingCopyCount > 0 ? "COPY_MISSING" : "NONE";
}

// src/domain/payout/index.ts
var PayoutStatus = {
  PENDING: "PENDING",
  PAID: "PAID",
  VOIDED: "VOIDED"
};

// src/domain/presence/index.ts
import { z as z6 } from "zod";
var QuestPresenceUserSchema = z6.object({
  userId: z6.string(),
  displayName: z6.string(),
  avatarUrl: z6.string().nullable(),
  joinedAt: z6.string()
});
var QuestPresenceEntrySchema = z6.object({
  questId: z6.string(),
  users: z6.array(QuestPresenceUserSchema)
});

// src/domain/notification/index.ts
var NOTIFICATION_SCOPES = ["PERSONAL", "TEAM", "GLOBAL"];
var NOTIFICATION_TYPES = [
  "MENTION",
  "REPLY",
  "PIN",
  "ATTENDANCE_MISSED_CLOCKOUT",
  "ATTENDANCE_CORRECTION_REQUEST",
  // HR 경로 전용 — 결근일 정정·무팀장 폴백은 팀장이 아닌 HR 이 받는다. 전용 타입이라야
  // 딥링크가 청중별로 갈린다(팀장→로스터 'corrections' / HR→전사 'company-corrections').
  // off-day OFF_DAY_APPROVAL_PENDING vs OFF_DAY_APPROVAL_ESCALATED 와 동일 결.
  "ATTENDANCE_CORRECTION_ESCALATED",
  "ATTENDANCE_CORRECTION_RESOLVED",
  // 신청자에게 보내는 '마감 임박' 전용 타입. RESOLVED('처리 완료') 재사용 금지 —
  // 대기중 알림에 완료 아이콘이 붙는 시제 오류를 피한다(D-T4-Q3). 딥링크는 RESOLVED 와 같은
  // 'attendance' 창이지만 아이콘·시제가 달라 타입을 분리한다.
  "ATTENDANCE_CORRECTION_DEADLINE",
  "ATTENDANCE_CREATED_BY_LEADER",
  "LEAVE_REQUEST_RECEIVED",
  "LEAVE_APPROVED",
  "LEAVE_REJECTED",
  "LEAVE_PROOF_SUBMITTED",
  "LEAVE_PROOF_REVIEWED",
  "LEAVE_PROOF_RESUBMIT_REQUESTED",
  "WORKLOG_SUBMITTED",
  "WORKLOG_NOT_SUBMITTED",
  "WEEKLY_REPORT_NOT_WRITTEN",
  "OFF_DAY_APPROVAL_PENDING",
  "OFF_DAY_APPROVAL_ESCALATED",
  "OFF_DAY_APPROVED",
  "OFF_DAY_REJECTED",
  "QUEST_COMPLETED",
  "QUEST_UPDATED",
  "QUEST_DELETED",
  // 퀘스트 ACTIVE 전이(생성 즉시 승인 or 승인 성공) 시 팀원/전 직원 fan-out.
  // 'QUEST_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  // 타임라인 이벤트 TimelineEventType.QUEST_CREATED 와 혼동 금지 — 그쪽은 알림과 무관.
  "QUEST_PUBLISHED",
  // 선행이 끝나 후행을 시작할 수 있게 됐다 → 후행 수락자, 없으면 QUEST_PUBLISHED 와 같은 범위.
  // 'QUEST_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  "QUEST_PREREQUISITES_MET",
  // 정성(completion) 체크포인트 '검토 요청' → 생성자·소유팀 팀장·시나리오 진행 책임자.
  // 'CHECKPOINT_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  // 형제 CHECKPOINT_APPROVED 와 같은 딥링크('quests')를 쓰지만 아이콘·문구가 달라 타입을 분리한다
  // (증빙 도착 QUEST_PROOF_SUBMITTED 와도 별개 — 그쪽은 수치 CP proof 트랙).
  "CHECKPOINT_SUBMITTED",
  "CHECKPOINT_APPROVED",
  // 정성 CP 반려 → 반려된 증빙의 제출자(퀘스트 06bcc120). 증빙 단위 반려 QUEST_PROOF_REJECTED 와 같은 착지.
  // 'CHECKPOINT_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  "CHECKPOINT_REJECTED",
  "OUTBREAK_TRIGGERED",
  "OUTBREAK_REVOKED",
  // 전사 공개모집 철회(E8). 형제 OUTBREAK_REVOKED 와 달리 전사가 아니라 **수락자에게만** 간다.
  // 'OUTBREAK_' 접두사를 안 쓰므로 toNotificationCategory 자동 편입이 안 된다 —
  // category.ts 의 quest 분기에 명시 비교로 등록돼 있다.
  "OPEN_CALL_REVOKED",
  // 승인 큐 결과를 **만든 사람에게** 돌려준다. 형제 QUEST_PUBLISHED 는 참여 권유 fan-out 이라
  // 생성자를 일부러 빼므로, 이 둘이 없으면 제안한 팀장만 결과를 못 듣는다.
  // 'QUEST_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  "QUEST_APPROVED",
  "QUEST_REJECTED",
  "RANK_CHANGED",
  "POINT_SETTLED",
  "BUG_REPORT_CREATED",
  "BUG_REPORT_ASSIGNED",
  "BUG_REPORT_STATUS_CHANGED",
  "BUG_REPORT_COMMENT",
  "BUG_REPORT_AFFECTED",
  // 담당자별 방치 버그 집계 다이제스트(매일 아침 크론). 건별 알림이 아니라 집계라
  // bugReportId 를 싣지 않는다. 회수 쿼리가 type 으로 대상을 고르므로 실제 제보 알림
  // (BUG_REPORT_CREATED)과 타입을 공유하면 매니저 겸 담당자의 진짜 제보 알림을 오폭한다.
  "BUG_REPORT_STALE_DIGEST",
  "CP_TASK_COMPLETED",
  // 팀장·운영자가 태스크에 피드백을 달면 그 태스크 담당자에게 1건. 'QUEST_' 접두사라
  // toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  // 형제 CP_TASK_COMPLETED 와 달리 딥링크가 있다 — 태스크가 물고 있는 퀘스트로 착지한다.
  "QUEST_TASK_FEEDBACK_ADDED",
  // 검토 대기 중인 정성 CP 에 자료가 보태진 순간 → 승인 권한을 가진 팀 LEADER.
  // 'QUEST_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  // 형제 CHECKPOINT_SUBMITTED(검토 시작 요청)와 별개 — 그쪽은 OPEN→IN_REVIEW 전이가 발사한다.
  "QUEST_TASK_ATTACHMENT_ADDED",
  "ADCODE_PROOF_SUBMITTED",
  "ADCODE_PROOF_ACCEPTED",
  "ADCODE_PROOF_REJECTED",
  // 시나리오에서 애드코드를 신청해 퀘스트가 자동 생성된 시점 → 구독(opt-in)을 켠 ADCODE
  // capability 보유자에게. 위 PROOF 3종과 달리 **구독이 1차 게이트**라 자격만으로는 받지 않는다.
  // 'ADCODE_' 접두사라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  // 수신자 fan-out(구독자 조회 · 배정팀 멤버 한정 · 신청자 억제)은 notification 리스너 담당.
  "ADCODE_QUEST_CREATED",
  // 일반 퀘스트 증빙 제출 → 생성자·소유팀 팀장·진행 책임자. 'QUEST_' 접두사라
  // toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다(ADCODE 3종은 별도 경로).
  "QUEST_PROOF_SUBMITTED",
  // 일반 퀘스트 증빙 반려 → 제출자 1명. 제출·승인만 알림이 가고 반려는 침묵이라
  // 제출자가 목록을 다시 열어보기 전까지 반려 사실도 사유도 알 수 없었다.
  // ADCODE_PROOF_REJECTED 와 별개 경로다(그쪽은 시나리오 요청자 트랙).
  "QUEST_PROOF_REJECTED",
  // 2영업일 넘게 검수되지 않은 증빙 → 실제 검수 가능자(생성자 ∪ ADMIN ∪ 퀘스트 팀 LEADER).
  // QUEST_PROOF_SUBMITTED 가 제출 순간 1회 밀어주는 반면 이쪽은 방치가 풀릴 때까지
  // 매일 다시 뜨는 다이제스트다 — 검수 권한 자체가 팀 스코프이므로(findQuestForReview
  // 2차 게이트) 수신자도 해당 팀까지만이고, 팀장 전원으로는 넓히지 않는다.
  "QUEST_PROOF_REVIEW_OVERDUE",
  // 팀장이 퀘스트 상세에서 누른 제출 알림 → 수락만 하고 증빙을 안 낸 사람(퀘스트 b1cfcba4).
  // 누를 수 있는 사람은 증빙 검수 가능자(생성자 ∪ ADMIN ∪ 그 팀 LEADER)와 같다. 같은 사람·같은
  // 퀘스트에 KST 하루 1회 — idempotencyKey 가 `${questId}:${날짜}` 라 두 번째는 조용히 빠진다.
  // 자동 반복 발송은 없다. 'QUEST_' 접두사라 toNotificationCategory 가 'quest' 에 자동 편입한다.
  // 수락자가 받으므로 QUEST_PROOF_REJECTED 와 같은 questScope 규칙으로 그 퀘스트 상세를 연다.
  "QUEST_PROOF_SUBMIT_REMINDER",
  // 배당 확정·기간 마감 5종 (design/budget-ledger-period-close.md §8). 급여기간 마감을
  // 사람이 손으로 누르기로 정한 뒤 아무도 알려주지 않으면 기간이 열린 채 남는다.
  // 발송 **시각**은 §8 이 고정한 09:00·13:00·18:00 이지만 발송 **날짜**는 회사 설정
  // payDay 에서 파생한다(shared getSettlementSchedule) — 상수로 두면 급여일을 바꾸는
  // 순간 엉뚱한 날 간다.
  //
  // ⚠️ 이 5종을 NOTIFICATION_FEATURE_MAP 에 등록하지 말 것. 착지 창 'ceo-console' 이
  // 'teaser' 라 무심코 매핑하기 쉬운데, 그 맵에 걸리면 isNotificationAllowed 가 false 를
  // 돌려 알림이 INSERT 전에 통째로 버려진다(예외도 로그도 없다 — OPEN_CALL_REVOKED 선례).
  //
  // 접두사(DIVIDEND_ · BUDGET_)는 toNotificationCategory 의 자동 편입 규칙에 안 걸리므로
  // category.ts 에 **명시 비교**로 등록돼 있다('point' 토글 그룹).
  /** N1 — 팀장, D 09:00, 배당 풀 > 0 인 팀 */
  "DIVIDEND_CONFIRM_REQUESTED",
  /** N3 — 팀장, D 18:00, 미확정 팀만. N1 과 청중은 같지만 시제·문구가 달라 타입을 분리한다 */
  "DIVIDEND_CONFIRM_REMINDER",
  /** N2 — 대표, D 13:00, 항상 */
  "BUDGET_PERIOD_SETTLING_STARTED",
  /** N4 — 대표, 마감 조건 (a)(b) 가 충족되는 순간 1회 */
  "BUDGET_PERIOD_CLOSE_READY",
  // N6 — D+1 부터 매일. 설계 §8 이 **한 행에 두 청중**(팀장 09:00 · 대표 13:00)으로 둔 것을
  // 그대로 한 타입으로 옮긴다. 그 대가로 getDeepLink 은 이 타입을 착지시키지 않는다 —
  // 타입만 보고 창을 정하는 구조라 어느 쪽으로 보내도 나머지 절반은 자기 액션 지점이
  // 아닌 창에 떨어지기 때문이다. 청중별로 갈라야 할 근거가 생기면 그때 타입을 쪼갠다
  // (ATTENDANCE_CORRECTION_ESCALATED 선례).
  "BUDGET_PERIOD_CLOSE_PENDING",
  "CLUB_POLL_CONFIRMED",
  "CLUB_FEED_APPROVED",
  "CLUB_FEED_REJECTED",
  "RESERVATION_ATTENDEE_ADDED",
  "RESERVATION_ATTENDEE_REMOVED",
  "RESERVATION_CANCELLED",
  "RESERVATION_RESCHEDULED",
  "RESERVATION_REMINDER",
  // 출퇴근 달력 일정의 리마인더 — 전날 17:00·당일 09:00(KST) 회차마다. 팀에 공유한 일정이면
  // 그 팀 전원, 아니면 쓴 사람만 받는다(docs/plan/attendance-schedule-notify/cp1-decision.md).
  "ATTENDANCE_DAY_SCHEDULE_REMINDER",
  // 리마인더가 이미 나간 일정을 쓴 사람이 지웠을 때, 그 리마인더를 받은 사람(지운 본인 제외)에게.
  // 리마인더와 시제·문구가 달라 타입을 나눈다(RESERVATION_REMINDER vs _CANCELLED 선례).
  "ATTENDANCE_DAY_SCHEDULE_CANCELLED",
  // 지운 일정을 「되돌리기」로 살렸을 때, 취소 알림을 받은 사람에게만.
  "ATTENDANCE_DAY_SCHEDULE_RESTORED",
  // D1 진행 책임자 지정·회수 → 당사자에게 PERSONAL 통보. 지정/회수를 한 타입으로 합칠 수 없다 —
  // notificationMeta 의 MetaRow 는 NotificationType 단위라 렌더 시점 판별자가 없고, 한 타입이면
  // 지정과 회수가 같은 글리프·같은 시제 문구를 쓰게 된다(ATTENDANCE_CORRECTION_RESOLVED vs
  // _DEADLINE, RESERVATION_ATTENDEE_ADDED vs _REMOVED 와 동일한 분리 근거).
  // ⚠️ 'SCENARIO_' 접두사는 category.ts 가 명시적으로 'quest' 로 편입한다(기본 폴백은 'system').
  "SCENARIO_PROGRESS_OWNER_ASSIGNED",
  "SCENARIO_PROGRESS_OWNER_REVOKED",
  // 운영 시그널 어려움 보고 4종 (IA §6 N-1~N-6). 이벤트는 3개(생성·댓글·상태전이)인데 타입이 4개인
  // 이유는 생성 하나가 helpNeeded 로 갈리기 때문 — REPORT_CREATED(N-5, 팀장)와
  // HELP_NEEDED(N-4+N-6, 팀 전원)는 청중·문구·글리프가 달라 위 분리 기준이 그대로 적용된다.
  // 반대로 N-1(팀장 댓글)/N-3(동료 댓글)은 청중(보고자 본인)·시제·글리프가 전부 같아
  // COMMENT_ADDED 하나로 **병합**한다(분리 관례의 대우). N-2 도 해결/취소를 body 문구로만 가른다.
  // ⚠️ 'SCENARIO_' 가 아닌 'OPS_SIGNAL_' 접두사인 이유 — category.ts 가 SCENARIO_* 를 'quest' 로
  // 명시 편입 중이라 같은 접두사를 쓰면 새 도메인의 카테고리 결정이 그 체계와 얽힌다.
  // 이 4종은 category.ts 에서 명시 비교로 'system' 에 귀속한다(폴백 의존 금지).
  "OPS_SIGNAL_REPORT_CREATED",
  "OPS_SIGNAL_HELP_NEEDED",
  "OPS_SIGNAL_COMMENT_ADDED",
  "OPS_SIGNAL_STATUS_CHANGED",
  // 퀘스트 제안함 (docs/design/quest-proposal-inbox.md). 백엔드 이벤트는 2개인데
  // 타입이 3개인 이유는 처리(RESOLVED)가 status 로 갈리기 때문 — 위 REPORT_CREATED/
  // HELP_NEEDED 분리와 같은 기준이다. 채택과 반려는 청중(제안자)은 같지만 글리프·
  // 카테고리 라벨·문구가 전부 달라야 목록에서 두 사건이 구분된다.
  // 접두사가 'QUEST_' 라 category.ts 의 startsWith('QUEST_') 규칙에 자연히 걸려 'quest'
  // 그룹에 귀속된다 — 알림 설정에서 퀘스트 토글과 함께 움직이는 것이 의도다.
  "QUEST_PROPOSAL_CREATED",
  "QUEST_PROPOSAL_ADOPTED",
  "QUEST_PROPOSAL_DECLINED",
  // 퀘스트 질문 스레드에 질문이 등록됨 → 그 퀘스트가 걸린 팀의 팀장 전원.
  // 수신자가 팀장인 근거는 위계가 아니라 **답글 권한**이다 — canAnswerQuestQuestion 이
  // quest.teamId 의 LEADER 와 질문 작성자만 허용한다(생성자·ADMIN·OPERATOR 는 403).
  // 답글 도착 알림은 이번 범위 밖이라 타입이 하나뿐이다 — 짝 타입을 미리 만들면 emit 도
  // 리스너도 없는 dead letter 가 된다.
  // 접두사가 'QUEST_' 라 toNotificationCategory 가 자동으로 'quest' 카테고리에 편입한다.
  "QUEST_QUESTION_CREATED",
  // 로스터 정체 다이제스트 (roster-stage-axis.md §8-1) — 매일 10:10 크론이 수신자 × 캠페인마다
  // 한 건씩 만들고 다음 날 선두에서 읽음 처리한다. 방치 두 종류(응답 방치 · 미응답)를 타입으로
  // 가르지 않는 것은 청중이 겹치기 때문이다 — 진행 책임자는 둘 다 받으므로 타입을 나누면 같은
  // 캠페인에 대해 하루 두 건을 받는다. 대신 수신자별로 자기에게 해당하는 건만 담고 문구가 가른다
  // (RESERVATION_ATTENDEE_ADDED/_REMOVED 분리 기준의 대우 — 청중·글리프가 같으면 병합).
  // ⚠️ 'CAMPAIGN_' 접두사는 category.ts 가 명시적으로 'quest' 로 편입한다(기본 폴백은 'system').
  "CAMPAIGN_ROSTER_STALLED",
  // 크리에이터가 섭외 메일에 답장했다 (퀘스트 6bec61df, creator-email-in-axiom.md §3-6).
  // 수신자는 **그 행의 담당자만**이다 — 팀장에게 전 응답을 중계하면 성수기에 도배돼 결재·증빙
  // 알림까지 함께 묻힌다. 담당이 빈 행뿐이면 캠페인 진행 담당자로 떨어진다.
  // 착지는 그 캠페인 브리프다 — 회신은 (캠페인 × 크리에이터) 대화에 붙고 로스터 행은 여럿일 수
  // 있어 한 행을 고르면 임의가 된다.
  "CREATOR_MAIL_REPLY_RECEIVED",
  // 회신이 왔는데 2영업일간 후속 동작이 없다 (같은 퀘스트·같은 문서 §3-6).
  // 수신자는 **그 캠페인 팀의 팀장**이다 — 담당자에게 다시 보내면 이미 안 본 알림을 한 번 더
  // 보내는 것이라, 미는 사람을 한 층 올리는 것이 이 알림의 전부다. 휴가 구멍도 이것이 닫는다.
  // 내용이 "응답이 왔다" 가 아니라 "응답이 방치되고 있다" 라 도착 알림과 타입을 가른다.
  "CREATOR_MAIL_REPLY_NEGLECTED",
  // 로스터 밖 섭외가 PREMIUM 크리에이터 관계에 붙었다 (퀘스트 0a9446c9 결정 필요4). 팀원도 제한 대상에
  // 섭외를 남길 수 있게 하면서, 막지 않는 대신 그 크리에이터의 담당자(담당이 없으면 그 크리에이터를
  // 볼 수 있는 시딩 팀장)에게 알린다. 관계 층의 유일한 알림이다(규칙 40).
  "CREATOR_RELATION_PREMIUM_CONTACT_RECORDED",
  // 섭외 예약 회차에 사람이 알아야 할 일이 생겼다 (퀘스트 96445435, creator-outreach-scheduling.md §3).
  // 잠금 뒤 들어온 회신·폼 제출자(마감 후 도착) · 받을 사람이 모두 빠져 취소 · 폼 감시가 멈춰 취소 ·
  // 예약 확인 실패. 수신자는 **그 회차를 누른 사람**이다 — 예약을 건 사람이 다시 예약하거나 스티비를 본다.
  // 넷을 타입으로 가르지 않는 것은 청중·착지(캠페인 브리프)가 같아서다 — 문구가 가른다.
  "OUTREACH_RESERVATION_ALERT",
  // 입사예정자의 입사일 도래 — 매일 자정 cron 이 PENDING_ONBOARD → ACTIVE 로 전이하며 보낸다.
  // 수신자는 그 사람을 맞을 팀의 팀장과 인사 권한자(ADMIN ∪ HR_MANAGEMENT 팀) — 본인에게는
  // 보내지 않는다. 그날 계정을 처음 여는 사람에게 "오늘 입사했습니다" 는 알릴 것이 없다.
  // 접두사가 어디에도 안 걸려 category.ts 는 'system' 폴백으로 잡는다 — 인사 통지라 의도한 자리다.
  "MEMBER_ONBOARD_TODAY",
  // 자가 가입 계정이 메일 인증코드로 소유를 확인했는데 활성 부서가 없어 열리지 못한다(87bac89b §5).
  // 수신자는 인사 권한자(ADMIN ∪ HR_MANAGEMENT 팀) — 팀을 모르니 팀장은 고를 수 없다. 본인은 뺀다.
  // 받은 사람이 할 일은 구성원 추가 한 번이라 입사 통지와 같은 자리(구성원 관리)로 착지한다.
  "SIGNUP_VERIFIED_AWAITING_TEAM",
  // 캠페인 영상 등록 결과(퀘스트 edcc65e0). 등록은 비동기라 링크를 넣은 사람은 결과를 기다리지 않고
  // 알림으로 받는다(docs/design/campaign-video-tracking-ui.md §5). 수신자는 그 링크를 넣은 사람이다.
  // 성공과 실패를 타입으로 가르는 이유 — 실패만 할 일(「링크 확인 필요」)이 있어 목록에서 글리프가 달라야 한다.
  "VIDEO_RETENTION_ATTENTION",
  "VIDEO_RETENTION_FINAL",
  "VIDEO_TRACKING_REGISTERED",
  "VIDEO_TRACKING_REGISTRATION_FAILED",
  // 재업로드 요청한 칸에 새 영상 링크가 들어왔다 — 담당 팀장님이 기존·새 영상을 보고 교체를 확정할 차례다
  // (docs/design/campaign-video-tracking-ui.md §6). 수신자는 그 캠페인 참여팀의 팀장이다.
  "VIDEO_TRACKING_REPLACEMENT_CONFIRM_REQUESTED"
];
var SETTLEMENT_NOTIFICATION_TYPES = [
  "DIVIDEND_CONFIRM_REQUESTED",
  "DIVIDEND_CONFIRM_REMINDER",
  "BUDGET_PERIOD_SETTLING_STARTED",
  "BUDGET_PERIOD_CLOSE_READY",
  "BUDGET_PERIOD_CLOSE_PENDING"
];
function isSettlementNotificationType(type) {
  return SETTLEMENT_NOTIFICATION_TYPES.includes(type);
}
function isNotificationScope(value) {
  return typeof value === "string" && NOTIFICATION_SCOPES.includes(value);
}
function isNotificationType(value) {
  return typeof value === "string" && NOTIFICATION_TYPES.includes(value);
}
function formatWorklogNotSubmittedBody(names, logDate) {
  return `${names}\uB2D8\uC774 ${logDate} \uC5C5\uBB34\uC77C\uC9C0\uB97C \uC81C\uCD9C\uD558\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4`;
}
var QUEST_UPDATED_FIELD_LABELS = {
  title: "\uC81C\uBAA9",
  description: "\uC124\uBA85",
  deadline: "\uB9C8\uAC10\uC77C",
  rewardPoints: "\uBCF4\uC0C1",
  targetValue: "\uBAA9\uD45C\uAC12",
  metricKey: "\uC9C0\uD45C"
};
function subjectParticle(word) {
  const code = word.charCodeAt(word.length - 1);
  if (Number.isNaN(code) || code < 44032 || code > 55203) return "\uAC00";
  return (code - 44032) % 28 !== 0 ? "\uC774" : "\uAC00";
}
function formatQuestUpdatedBody(questTitle, fields, checkpointTitle) {
  const labels = fields.map((f) => QUEST_UPDATED_FIELD_LABELS[f]).join("\xB7");
  const particle = subjectParticle(labels);
  return checkpointTitle ? `'${questTitle}' \uD018\uC2A4\uD2B8\uC758 \uCCB4\uD06C\uD3EC\uC778\uD2B8 '${checkpointTitle}'\uC758 ${labels}${particle} \uC218\uC815\uB418\uC5C8\uC5B4\uC694` : `'${questTitle}' \uD018\uC2A4\uD2B8\uC758 ${labels}${particle} \uC218\uC815\uB418\uC5C8\uC5B4\uC694`;
}
function formatQuestDeletedBody(questTitle) {
  return `'${questTitle}' \uD018\uC2A4\uD2B8\uAC00 \uC0AD\uC81C\uB418\uC5C8\uC5B4\uC694`;
}
function formatQuestProofSubmittedBody(questTitle, checkpointTitle) {
  return checkpointTitle ? `'${questTitle}' \uD018\uC2A4\uD2B8\uC758 \uCCB4\uD06C\uD3EC\uC778\uD2B8 '${checkpointTitle}' \uC99D\uBE59\uC774 \uC81C\uCD9C\uB418\uC5B4 \uAC80\uD1A0\uB97C \uAE30\uB2E4\uB9BD\uB2C8\uB2E4` : `'${questTitle}' \uD018\uC2A4\uD2B8\uC5D0 \uC99D\uBE59\uC774 \uC81C\uCD9C\uB418\uC5B4 \uAC80\uD1A0\uB97C \uAE30\uB2E4\uB9BD\uB2C8\uB2E4`;
}
function formatQuestProofSubmitReminderBody(questTitle, actorName) {
  return `'${questTitle}' \uD018\uC2A4\uD2B8\uC758 \uC99D\uBE59\uC744 ${actorName}\uB2D8\uC774 \uC694\uCCAD\uD588\uC2B5\uB2C8\uB2E4. \uC81C\uCD9C \uC790\uB9AC\uB85C \uC5F4\uB9BD\uB2C8\uB2E4`;
}
function formatCheckpointSubmittedBody(questTitle, checkpointTitle) {
  return `'${questTitle}' \uD018\uC2A4\uD2B8\uC758 \uCCB4\uD06C\uD3EC\uC778\uD2B8 '${checkpointTitle}' \uAC80\uD1A0 \uC694\uCCAD\uC774 \uB3C4\uCC29\uD588\uC2B5\uB2C8\uB2E4`;
}
function formatQuestTaskFeedbackAddedBody(taskTitle) {
  return `'${taskTitle}' \uD0DC\uC2A4\uD06C\uC5D0 \uD300\uC7A5 \uD53C\uB4DC\uBC31\uC774 \uB2EC\uB838\uC5B4\uC694`;
}
function formatQuestTaskAttachmentAddedBody(checkpointTitle) {
  return `\uAC80\uD1A0 \uC911\uC778 \uCCB4\uD06C\uD3EC\uC778\uD2B8 '${checkpointTitle}' \uC5D0 \uC790\uB8CC\uAC00 \uCD94\uAC00\uB418\uC5C8\uC2B5\uB2C8\uB2E4`;
}
function formatQuestPublishedBody(questTitle) {
  return `${questTitle} \uD018\uC2A4\uD2B8\uAC00 \uC5F4\uB838\uC2B5\uB2C8\uB2E4 \u2014 \uC9C0\uAE08 \uCC38\uC5EC\uD558\uC138\uC694`;
}
function formatQuestPrerequisitesMetBody(questTitle) {
  return `${questTitle} \uD018\uC2A4\uD2B8\uC758 \uC120\uD589\uC774 \uBAA8\uB450 \uB05D\uB098 \uC774\uC81C \uC2DC\uC791\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4`;
}
function formatOpenCallRevokedBody(questTitle, reason) {
  return `${questTitle} \u2014 ${reason}`;
}
function formatQuestApprovedBody(questTitle) {
  return `${questTitle} \uBC1C\uD589\uB418\uC5C8\uC2B5\uB2C8\uB2E4`;
}
function formatQuestRejectedBody(questTitle, reason) {
  const trimmed = reason?.trim();
  return trimmed ? `${questTitle} \u2014 ${trimmed}` : questTitle;
}
function formatScenarioProgressOwnerAssignedBody(scenarioTitle) {
  return `'${scenarioTitle}' \uC2DC\uB098\uB9AC\uC624\uC758 \uC9C4\uD589 \uCC45\uC784\uC790\uB85C \uC9C0\uC815\uB418\uC5C8\uC5B4\uC694`;
}
function formatScenarioProgressOwnerRevokedBody(scenarioTitle) {
  return `'${scenarioTitle}' \uC2DC\uB098\uB9AC\uC624\uC758 \uC9C4\uD589 \uCC45\uC784\uC790\uC5D0\uC11C \uD574\uC81C\uB418\uC5C8\uC5B4\uC694`;
}
var OPS_SIGNAL_COMMENT_BODY_MAX_LENGTH = 100;
function formatOpsSignalReportCreatedBody(scenarioTitle) {
  if (!scenarioTitle) return "\uC2DC\uB098\uB9AC\uC624\uC5D0 \uC0C8 \uC6B4\uC601 \uBCF4\uACE0\uAC00 \uB4F1\uB85D\uB418\uC5C8\uC5B4\uC694";
  return `'${scenarioTitle}' \uC2DC\uB098\uB9AC\uC624\uC5D0 \uC0C8 \uC6B4\uC601 \uBCF4\uACE0\uAC00 \uB4F1\uB85D\uB418\uC5C8\uC5B4\uC694`;
}
function formatOpsSignalHelpNeededBody(scenarioTitle) {
  if (!scenarioTitle) return "\uC2DC\uB098\uB9AC\uC624\uC5D0 \uB3C4\uC6C0\uC774 \uD544\uC694\uD55C \uC6B4\uC601 \uBCF4\uACE0\uAC00 \uC62C\uB77C\uC654\uC5B4\uC694";
  return `'${scenarioTitle}' \uC2DC\uB098\uB9AC\uC624\uC5D0 \uB3C4\uC6C0\uC774 \uD544\uC694\uD55C \uC6B4\uC601 \uBCF4\uACE0\uAC00 \uC62C\uB77C\uC654\uC5B4\uC694`;
}
var QUEST_PROPOSAL_TITLE_BODY_MAX_LENGTH = 40;
function truncateProposalTitle(title) {
  if (title.length <= QUEST_PROPOSAL_TITLE_BODY_MAX_LENGTH) return title;
  return `${title.slice(0, QUEST_PROPOSAL_TITLE_BODY_MAX_LENGTH)}...`;
}
function formatQuestProposalCreatedBody(proposalTitle) {
  return `'${truncateProposalTitle(proposalTitle)}' \uC81C\uC548\uC774 \uC62C\uB77C\uC654\uC5B4\uC694`;
}
function formatQuestProposalAdoptedBody(proposalTitle) {
  return `'${truncateProposalTitle(proposalTitle)}' \uC81C\uC548\uC774 \uD018\uC2A4\uD2B8\uAC00 \uB418\uC5C8\uC5B4\uC694`;
}
function formatQuestProposalDeclinedBody(proposalTitle) {
  return `'${truncateProposalTitle(proposalTitle)}' \uC81C\uC548\uC774 \uBC18\uB824\uB418\uC5C8\uC5B4\uC694`;
}
function formatOpsSignalCommentAddedBody(commentBody) {
  if (commentBody.length <= OPS_SIGNAL_COMMENT_BODY_MAX_LENGTH) return commentBody;
  return `${commentBody.slice(0, OPS_SIGNAL_COMMENT_BODY_MAX_LENGTH)}...`;
}
function formatOpsSignalStatusChangedBody(status) {
  return status === "RESOLVED" ? "\uBCF4\uACE0\uD55C \uC6B4\uC601 \uC774\uC288\uAC00 \uD574\uACB0 \uCC98\uB9AC\uB418\uC5C8\uC5B4\uC694" : "\uBCF4\uACE0\uD55C \uC6B4\uC601 \uC774\uC288\uAC00 \uCDE8\uC18C \uCC98\uB9AC\uB418\uC5C8\uC5B4\uC694";
}
function formatAdcodeRequestedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const requested = kstDate(date);
  return `\uC2E0\uCCAD ${String(requested.getMonth() + 1).padStart(2, "0")}/${String(
    requested.getDate()
  ).padStart(2, "0")} ${String(requested.getHours()).padStart(2, "0")}:${String(
    requested.getMinutes()
  ).padStart(2, "0")}`;
}
function formatAdcodeQuestCreatedBody(scenarioTitle, requestedAt) {
  const requestedLabel = formatAdcodeRequestedAt(requestedAt);
  return [scenarioTitle, requestedLabel].filter((value) => value !== null).join("\n");
}
var QUEST_QUESTION_TITLE_BODY_MAX_LENGTH = 40;
function formatQuestQuestionCreatedBody(questTitle) {
  const title = questTitle.length <= QUEST_QUESTION_TITLE_BODY_MAX_LENGTH ? questTitle : `${questTitle.slice(0, QUEST_QUESTION_TITLE_BODY_MAX_LENGTH)}...`;
  return `'${title}' \uD018\uC2A4\uD2B8\uC5D0 \uC9C8\uBB38\uC774 \uC62C\uB77C\uC654\uC5B4\uC694`;
}

// src/domain/assistant/assistant.ts
var ASSISTANT_WAITING_TIMEOUT_MS = 9e4;
var DEFAULT_AGENT_SLUG = "dokkaebi";
var NARRATOR_AGENT_SLUG = "narrator";
var DOKKAEBI_PROACTIVE_TITLE_PREFIX = "[\uB3C4\uAE68\uBE44]";
var ConversationEntityType = {
  QUEST: "quest",
  TASK: "task"
};

// src/domain/quest-template/quest-template.ts
function createQuestTemplate(params) {
  if (params.rewardPoints < 0) {
    throw new Error(`rewardPoints cannot be negative, got ${params.rewardPoints}`);
  }
  for (const cp of params.checkpoints) {
    if (cp.rewardRatio < 0) {
      throw new Error(`rewardRatio cannot be negative, got ${cp.rewardRatio}`);
    }
  }
  const ratioSum = params.checkpoints.reduce((sum, cp) => sum + cp.rewardRatio, 0);
  if (ratioSum > 1 + 1e-9) {
    throw new Error(`rewardRatio sum (${ratioSum}) exceeds 1.0`);
  }
  const checkpoints = params.checkpoints.map((cp) => ({
    id: cp.id,
    templateId: params.id,
    title: cp.title,
    description: cp.description,
    rewardRatio: cp.rewardRatio,
    position: cp.position
  }));
  const template = {
    id: params.id,
    name: params.name,
    title: params.title,
    description: params.description,
    scope: params.scope,
    targetType: params.targetType,
    rewardPoints: params.rewardPoints,
    creatorId: params.creatorId,
    checkpoints,
    createdAt: params.now,
    updatedAt: params.now
  };
  return { template, checkpoints };
}
function updateQuestTemplate(template, params) {
  const rewardPoints = params.rewardPoints ?? template.rewardPoints;
  if (rewardPoints < 0) {
    throw new Error(`rewardPoints cannot be negative, got ${rewardPoints}`);
  }
  return {
    ...template,
    name: params.name ?? template.name,
    title: params.title ?? template.title,
    description: params.description !== void 0 ? params.description : template.description,
    scope: params.scope ?? template.scope,
    targetType: params.targetType ?? template.targetType,
    rewardPoints,
    updatedAt: params.now
  };
}
function instantiateQuest(template, params) {
  if (template.scope === "SYSTEM" && params.teamId) {
    throw new Error("teamId must be null when scope is SYSTEM");
  }
  if (params.checkpointIds.length !== template.checkpoints.length) {
    throw new Error(
      `checkpointIds length (${params.checkpointIds.length}) must match template checkpoints (${template.checkpoints.length})`
    );
  }
  const rewardPoints = params.rewardPoints ?? template.rewardPoints;
  const quest = {
    id: params.questId,
    title: template.title,
    description: template.description,
    scope: template.scope,
    targetType: template.targetType,
    teamId: params.teamId,
    status: "DRAFT",
    templateId: template.id,
    creatorId: params.creatorId,
    rewardPoints,
    deadline: params.deadline,
    difficulty: params.difficulty ?? null,
    scenarioId: null,
    approvalStatus: "DRAFT",
    approvedBy: null,
    approvedAt: null,
    rejectionReason: null,
    createdAt: params.now,
    updatedAt: params.now
  };
  const sorted = [...template.checkpoints].sort((a, b) => a.position - b.position);
  const allocated = allocateCheckpointRewards(
    rewardPoints,
    sorted.map((cp) => cp.rewardRatio)
  );
  const checkpoints = sorted.map((tplCp, i) => ({
    id: params.checkpointIds[i],
    title: tplCp.title,
    description: tplCp.description,
    order: tplCp.position,
    status: "OPEN",
    questId: params.questId,
    deadline: null,
    assignedTo: null,
    rewardPoints: allocated[i],
    chainedToPrevious: false,
    checkpointType: "completion",
    targetValue: null,
    createdAt: params.now,
    updatedAt: params.now
  }));
  return { quest, checkpoints };
}
function allocateCheckpointRewards(total, ratios) {
  const raw = ratios.map((r) => total * r);
  const floors = raw.map(Math.floor);
  const sumFloor = floors.reduce((a, b) => a + b, 0);
  const target = Math.min(Math.round(raw.reduce((a, b) => a + b, 0)), total);
  const leftover = target - sumFloor;
  const order = ratios.map((_, i) => i).sort((a, b) => raw[b] - floors[b] - (raw[a] - floors[a]) || a - b);
  const result = [...floors];
  for (let k = 0; k < leftover; k++) result[order[k]]++;
  return result;
}

// src/domain/bug-report/index.ts
import { z as z7 } from "zod";
var BugReportStatus = {
  OPEN: "OPEN",
  CONFIRMED: "CONFIRMED",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
  REOPENED: "REOPENED",
  WONT_FIX: "WONT_FIX",
  CANNOT_REPRODUCE: "CANNOT_REPRODUCE"
};
var TRIAGER_ONLY_STATUSES = [
  BugReportStatus.WONT_FIX,
  BugReportStatus.CANNOT_REPRODUCE,
  BugReportStatus.CLOSED
];
var BugReportType = {
  BUG: "BUG",
  FEATURE: "FEATURE"
};
var BugReportEventType = {
  COMMENT: "COMMENT",
  STATUS_CHANGE: "STATUS_CHANGE",
  ASSIGNEE_CHANGE: "ASSIGNEE_CHANGE",
  AFFECTED_USER_ADDED: "AFFECTED_USER_ADDED",
  CONTENT_EDIT: "CONTENT_EDIT",
  ATTACHMENT_EDIT: "ATTACHMENT_EDIT"
};
var BugReportAttachmentSchema = z7.object({
  path: z7.string(),
  originalName: z7.string()
});
var BUG_REPORT_PROCESSED_STATUSES = [
  BugReportStatus.RESOLVED,
  BugReportStatus.CLOSED
];
var BUG_REPORT_QUEST_TEAM_NAME = "\uAC1C\uBC1C\uD300";
var VALID_TRANSITIONS = {
  OPEN: ["CONFIRMED", "WONT_FIX", "CANNOT_REPRODUCE"],
  CONFIRMED: ["IN_PROGRESS", "WONT_FIX", "CANNOT_REPRODUCE"],
  IN_PROGRESS: ["RESOLVED", "CONFIRMED"],
  RESOLVED: ["CLOSED", "REOPENED"],
  CLOSED: [],
  REOPENED: ["CONFIRMED", "IN_PROGRESS"],
  WONT_FIX: ["CLOSED", "REOPENED"],
  CANNOT_REPRODUCE: ["CLOSED", "REOPENED"]
};
function canTransitionTo(from, to, type = BugReportType.BUG) {
  if (from === to) return false;
  if (type === BugReportType.FEATURE && to === BugReportStatus.CANNOT_REPRODUCE) return false;
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}
function calculateStalenessPercent(lastActivityAt, thresholdDays, now = Date.now()) {
  const elapsed = now - new Date(lastActivityAt).getTime();
  if (elapsed <= 0) return 0;
  const thresholdMs = thresholdDays * 24 * 60 * 60 * 1e3;
  return Math.min(100, Math.round(elapsed / thresholdMs * 100));
}
function calculateLeadTime(createdAt, resolvedAt) {
  if (resolvedAt === null) return null;
  const diffMs = new Date(resolvedAt).getTime() - new Date(createdAt).getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1e3));
}
function isVoteEligible(level, teamCapabilities, pageContext, roles, teamRole) {
  if (!isAppView(pageContext)) return true;
  return canAccessViewFull(level, teamCapabilities, pageContext, roles, teamRole, [
    ...VIEW_ELIGIBILITIES
  ]);
}

// src/domain/quest-task/values.ts
var QuestTaskStatus = {
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE"
};
var QuestTaskCloseReason = {
  CHECKOUT: "CHECKOUT",
  CARRIED: "CARRIED",
  SWEPT: "SWEPT",
  ROSTER_MOVED: "ROSTER_MOVED"
};

// src/domain/quest-task/quest-task.ts
function createQuestTask(params) {
  if (!params.title.trim()) {
    throw new Error("title cannot be empty");
  }
  if (params.value !== null && params.value < 0) {
    throw new Error("value cannot be negative");
  }
  return {
    id: params.id,
    teamId: params.teamId,
    title: params.title,
    status: QuestTaskStatus.IN_PROGRESS,
    assigneeId: params.assigneeId,
    questId: params.questId,
    checkpointId: params.checkpointId,
    value: params.value,
    actualValue: null,
    varianceReason: null,
    memo: params.memo,
    createdAt: params.now,
    completedAt: null,
    predecessorTaskId: params.predecessorTaskId ?? null,
    rosterEntryId: params.rosterEntryId ?? null
  };
}
function remainingAfterTask(predecessor) {
  if (predecessor.value == null) return null;
  return Math.max(0, predecessor.value - (predecessor.actualValue ?? 0));
}
function completeQuestTask(task, now) {
  if (task.status === QuestTaskStatus.DONE) {
    throw new Error(`Task ${task.id} is already DONE`);
  }
  return {
    ...task,
    status: QuestTaskStatus.DONE,
    completedAt: now,
    closeReason: QuestTaskCloseReason.CHECKOUT
  };
}
function checkoutQuestTask(task, params, now) {
  if (task.status === QuestTaskStatus.DONE) {
    throw new Error(`Task ${task.id} is already DONE`);
  }
  const planned = task.value;
  const actual = params.actualValue;
  const differs = planned != null && actual != null && actual !== planned;
  const reason = params.varianceReason?.trim() ? params.varianceReason.trim() : null;
  if (differs && !reason) {
    throw new Error("\uC2E4\uC81C \uC218\uCE58\uAC00 \uACC4\uD68D\uACFC \uB2E4\uB974\uBA74 \uC0AC\uC720\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4");
  }
  return {
    ...task,
    status: QuestTaskStatus.DONE,
    completedAt: now,
    actualValue: actual,
    varianceReason: differs ? reason : null,
    closeReason: QuestTaskCloseReason.CHECKOUT
  };
}
function isSameDayTitleFix(task, params, now) {
  if (params.title === void 0 || params.memo !== void 0 || params.value !== void 0) {
    return false;
  }
  if (task.completedAt == null) return false;
  return toKstWorkDateString(new Date(task.completedAt)) === toKstWorkDateString(now);
}
function updateQuestTask(task, params, now = /* @__PURE__ */ new Date()) {
  if (task.status === QuestTaskStatus.DONE && !isSameDayTitleFix(task, params, now)) {
    throw new Error(`Cannot update task ${task.id}: status is DONE`);
  }
  if (params.title !== void 0 && !params.title.trim()) {
    throw new Error("title cannot be empty");
  }
  return {
    ...task,
    ...params.title !== void 0 && { title: params.title },
    ...params.memo !== void 0 && { memo: params.memo },
    ...params.value !== void 0 && { value: params.value }
  };
}
var AUTO_FILL_TASK_TITLE = "\uC99D\uBE59 \uC790\uB3D9 \uC644\uB8CC\uBD84";
var AUTO_FILL_TASK_MEMO = "\uC99D\uBE59 \uC2B9\uC778 \uC790\uB3D9 \uBCF4\uC815";
function isAutoFillTask(task) {
  return task.memo === AUTO_FILL_TASK_MEMO || task.title === AUTO_FILL_TASK_TITLE;
}
function deleteQuestTask(task, ctx) {
  if (isAutoFillTask(task)) {
    throw new Error("Cannot delete a system-generated auto-fill quest task");
  }
  if (ctx.feedbackCount > 0) {
    throw new Error("Cannot delete a quest task that has feedback");
  }
}
function effectiveTaskValue(task) {
  if (task.actualValue != null) return task.actualValue;
  if (task.closeReason === QuestTaskCloseReason.CHECKOUT) return task.value ?? null;
  return 0;
}
function needsActualValue(task) {
  if (task.value != null) return true;
  return task.checkpointId != null && task.checkpointTargetValue != null;
}
function priorContribution(tasks, assigneeId, excludeTaskId) {
  let sum = 0;
  for (const task of tasks) {
    if (task.assigneeId !== assigneeId) continue;
    if (task.status !== QuestTaskStatus.DONE) continue;
    if (excludeTaskId != null && task.id === excludeTaskId) continue;
    sum += effectiveTaskValue(task) ?? 0;
  }
  return sum;
}
function deltaFromCumulative(cumulativeValue, priorValue) {
  if (cumulativeValue < priorValue) {
    throw new Error(
      `\uB204\uC801 \uC644\uB8CC \uAC74\uC218(${cumulativeValue})\uAC00 \uC9C0\uAE08\uAE4C\uC9C0 \uC801\uB9BD\uB41C \uAC12(${priorValue})\uBCF4\uB2E4 \uC791\uC2B5\uB2C8\uB2E4`
    );
  }
  return cumulativeValue - priorValue;
}
function calculateGaugeProgress(tasks, targetValue) {
  const byAssignee = /* @__PURE__ */ new Map();
  for (const task of tasks) {
    if (task.status !== QuestTaskStatus.DONE) continue;
    const v = effectiveTaskValue(task);
    if (v == null) continue;
    byAssignee.set(task.assigneeId, (byAssignee.get(task.assigneeId) ?? 0) + v);
  }
  const contributions = [...byAssignee.entries()].map(
    ([assigneeId, value]) => ({ assigneeId, value })
  );
  const totalValue = contributions.reduce((sum, c) => sum + c.value, 0);
  const progress = targetValue > 0 ? Math.min(totalValue / targetValue, 1) : 0;
  return { targetValue, totalValue, progress, contributions };
}
function computeCheckpointFillValue(tasks, targetValue) {
  const { totalValue } = calculateGaugeProgress(tasks, targetValue);
  return Math.max(0, targetValue - totalValue);
}

// src/domain/quest-task/index.ts
var UNCLAIMED_CLOSE_REASONS = [
  QuestTaskCloseReason.CARRIED,
  QuestTaskCloseReason.SWEPT,
  QuestTaskCloseReason.ROSTER_MOVED
];
var QuestTaskCreatedVia = {
  WEB: "WEB",
  TERMINAL: "TERMINAL",
  CONNECTOR: "CONNECTOR",
  SYSTEM: "SYSTEM"
};
function isUnclaimedCloseReason(closeReason) {
  return closeReason != null && UNCLAIMED_CLOSE_REASONS.includes(closeReason);
}
var QuestTaskValueRevisionSource = {
  /** 본인 체크아웃 시 실제값 확정 */
  CHECKOUT: "CHECKOUT",
  /** 팀리더가 value를 override */
  LEADER_OVERRIDE: "LEADER_OVERRIDE"
};

// src/domain/quest-question/reply-review.ts
var CONTEXT_LIMIT = 10;
var ACKNOWLEDGEMENTS = /* @__PURE__ */ new Set([
  "\uAC10\uC0AC\uD569\uB2C8\uB2E4",
  "\uD655\uC778 \uAC10\uC0AC\uD569\uB2C8\uB2E4",
  "\uB2F5\uBCC0 \uAC10\uC0AC\uD569\uB2C8\uB2E4",
  "\uD655\uC778\uD588\uC2B5\uB2C8\uB2E4"
]);
function reviewQuestQuestionReplies(input) {
  const ordered = [...input.comments].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
  );
  const leaderIndex = ordered.findLastIndex((c) => input.leaderIds.has(c.authorId));
  const lastLeader = ordered[leaderIndex];
  const followUps = ordered.slice(leaderIndex + 1);
  const omittedFollowUpCount = Math.max(0, followUps.length - CONTEXT_LIMIT);
  const candidate = !!lastLeader && followUps.length > 0 && omittedFollowUpCount === 0 && followUps.every(
    (c) => c.authorId === input.questionAuthorId && ACKNOWLEDGEMENTS.has(c.body.trim().replace(/[.!]$/u, "").trim())
  );
  const context = (c) => ({
    id: c.id,
    authorName: c.authorName,
    createdAt: c.createdAt,
    body: c.body
  });
  return {
    status: !lastLeader ? "NO_LEADER_COMMENT" : candidate ? "ACKNOWLEDGEMENT_CANDIDATE" : "FOLLOW_UP_REVIEW_REQUIRED",
    lastLeaderComment: lastLeader ? context(lastLeader) : null,
    followUpComments: (lastLeader ? followUps.slice(0, CONTEXT_LIMIT) : followUps.slice(-CONTEXT_LIMIT)).map(context),
    omittedFollowUpCount
  };
}

// src/domain/quest-question/question.ts
function canWriteQuestQuestion(ctx) {
  return ctx.isAcceptor;
}
function canCommentOnQuestQuestion(ctx) {
  return ctx.isTeamLeader || ctx.actorId === ctx.questionAuthorId;
}
function createQuestQuestion(params) {
  assertThreadBody(params.body, "question body");
  return {
    id: params.id,
    questId: params.questId,
    authorId: params.authorId,
    body: params.body,
    createdAt: params.now
  };
}
function createQuestQuestionComment(params) {
  assertThreadBody(params.body, "question comment body");
  return {
    id: params.id,
    questionId: params.questionId,
    authorId: params.authorId,
    body: params.body,
    createdAt: params.now
  };
}
function awaitingLeaderReply(input) {
  if (input.leaderIds.has(input.questionAuthorId)) {
    return { awaiting: false, reason: null, waitingSince: null };
  }
  const ordered = [...input.comments].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
  );
  const last = ordered.at(-1);
  if (!last) return { awaiting: true, reason: "NO_COMMENT", waitingSince: input.questionCreatedAt };
  if (input.leaderIds.has(last.authorId))
    return { awaiting: false, reason: null, waitingSince: null };
  const lastLeaderIndex = ordered.findLastIndex((c) => input.leaderIds.has(c.authorId));
  if (lastLeaderIndex < 0) {
    return { awaiting: true, reason: "AUTHOR_ONLY", waitingSince: input.questionCreatedAt };
  }
  return {
    awaiting: true,
    reason: "AUTHOR_AFTER_LEADER",
    waitingSince: ordered[lastLeaderIndex + 1]?.createdAt ?? last.createdAt
  };
}
var QUEST_QUESTION_NO_REPLY_MARKERS = ["[\uCC29\uC218 \uBA54\uBAA8]", "[\uB2F5\uBCC0 \uBD88\uD544\uC694]"];
function hasNoReplyMarker(body) {
  return QUEST_QUESTION_NO_REPLY_MARKERS.some((marker) => body.startsWith(marker));
}

// src/domain/ceo/team-usage-retention.ts
function judgeTeamRetention(weeks, memberCount, thresholdPct) {
  if (thresholdPct === null || !Number.isInteger(thresholdPct) || thresholdPct <= 0 || thresholdPct > 100) {
    return null;
  }
  if (weeks.length !== 4) {
    throw new Error(`judgeTeamRetention: \uC644\uACB0 4\uC8FC\uAC00 \uD544\uC694\uD558\uB2E4 (\uBC1B\uC740 ${weeks.length}\uC8FC)`);
  }
  const weeksMet = memberCount === 0 ? 0 : weeks.filter((w) => w.actors * 100 >= thresholdPct * memberCount).length;
  return { weeksMet, retained: weeksMet === 4 };
}

// src/domain/ceo/index.ts
var BUDGET_TYPE_LABELS = {
  TEAM: "\uD300",
  WORLD: "\uC804\uC0AC \uC6D4\uB4DC",
  FLEX: "\uC804\uC0AC \uD50C\uB809\uC2A4"
};

// src/domain/usage-dashboard/index.ts
import { z as z8 } from "zod";
var USAGE_DASHBOARD_MAX_DAYS = 89;
var USAGE_DASHBOARD_DEFAULT_DAYS = 28;
var usageDashboardQuerySchema = z8.object({
  days: z8.coerce.number().int().min(1).max(USAGE_DASHBOARD_MAX_DAYS).default(USAGE_DASHBOARD_DEFAULT_DAYS)
}).strict();
var USAGE_TELEMETRY_CAVEATS = {
  zeroIsAmbiguous: "0\uAC74\uC740 \uC218\uC9D1\uB418\uC9C0 \uC54A\uC740 \uAC83\uACFC \uC4F0\uC774\uC9C0 \uC54A\uC740 \uAC83\uC744 \uAD6C\uBCC4\uD558\uC9C0 \uBABB\uD569\uB2C8\uB2E4.",
  usersNotAdditive: "\uC774\uBCA4\uD2B8\uB9C8\uB2E4 \uC13C \uC0AC\uB78C \uC218\uB294 \uC11C\uB85C \uB354\uD558\uBA74 \uC548 \uB429\uB2C8\uB2E4."
};
var USAGE_DENOMINATOR_NOTE = "\uACFC\uAC70 \uC8FC\uB3C4 \uD604\uC7AC \uD65C\uC131 \uC778\uC6D0\uC744 \uBD84\uBAA8\uB85C \uACC4\uC0B0\uD569\uB2C8\uB2E4. \uD3C9\uADE0 \uC81C\uC678 \uC778\uC6D0\uC744 \uBE7C\uB294 \uD300\uC7A5 \uB85C\uC2A4\uD130\uC640 \uBE44\uC728\uC774 \uB2E4\uB97C \uC218 \uC788\uC2B5\uB2C8\uB2E4.";

// src/domain/product/product.ts
var POSTURE_MIGRATED_REASON = "\uD0DC\uB3C4 \uCD95 \uC774\uAD00 \uC2DC \uC790\uB3D9 \uBC30\uC815";
var POSTURE_CREATED_REASON = "\uC81C\uD488 \uB4F1\uB85D";
var POSTURE_STALE_DAYS = 30;
function assertName(name) {
  if (!name.trim()) {
    throw new Error("Product name cannot be empty");
  }
}
function utcDayMs(at) {
  const d = new Date(at);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
function createProduct(params) {
  assertName(params.name);
  return {
    id: params.id,
    name: params.name,
    description: params.description ?? null,
    // 만든 사람이 주력으로 선언한 것으로 친다. 등록만 해 두고 아무도 안 붙으면
    // 그 사실이 곧 어긋남 문장으로 드러나므로, 조용한 기본값이 되지 않는다.
    posture: "PRIMARY",
    declaration: {
      posture: "PRIMARY",
      declaredByName: params.declaredByName ?? null,
      declaredAt: params.now,
      reason: POSTURE_CREATED_REASON
    },
    teamId: params.teamId ?? null,
    // 대상 사용자는 생성 시 붙이지 않는다 — 만드는 쪽과 쓰는 쪽이 다르고,
    // 대상이 정해지지 않은 채로 태어나는 게 정상이다.
    targets: [],
    // 삼킬 일도 마찬가지다. 만들 때 무엇을 없앨지까지 정해져 있는 경우가 오히려 드물고,
    // 빈 채로 두는 것이 "아직 선언하지 않았다"라는 사실 그대로다.
    subsumeWorks: [],
    // 갓 만든 제품에는 투입도 완료도 없다. 생성 시각으로 메우지 않는다 —
    // 등록만 하고 아무도 안 붙은 제품이 방금 손댄 것처럼 보인다.
    lastActivityAt: null,
    lastCompletedQuestAt: null,
    createdAt: params.now,
    updatedAt: params.now
  };
}
function updateProduct(product, updates, now) {
  if (updates.name !== void 0) assertName(updates.name);
  return {
    ...product,
    name: updates.name !== void 0 ? updates.name : product.name,
    description: updates.description !== void 0 ? updates.description : product.description,
    updatedAt: now
  };
}
function declareProductPosture(product, params, now) {
  if (!params.declaredByName.trim()) {
    throw new Error("Posture declaration requires a declarer");
  }
  if (!params.reason.trim()) {
    throw new Error("Posture declaration requires a reason");
  }
  return {
    ...product,
    posture: params.posture,
    declaration: {
      posture: params.posture,
      declaredByName: params.declaredByName,
      declaredAt: now,
      reason: params.reason
    },
    updatedAt: now
  };
}
function daysSinceInvestment(product, now) {
  if (product.lastActivityAt === null) return null;
  return Math.max(0, Math.floor((utcDayMs(now) - utcDayMs(product.lastActivityAt)) / 864e5));
}
function isInvestmentStale(product, now) {
  const days = daysSinceInvestment(product, now);
  return days === null || days > POSTURE_STALE_DAYS;
}
function hasPostureMismatch(product, now) {
  const stale = isInvestmentStale(product, now);
  return product.posture === "PRIMARY" ? stale : !stale;
}
function pendingSubsumeWorks(product) {
  return product.subsumeWorks.filter((w) => w.confirmedAt === null);
}
function confirmedSubsumeWorks(product) {
  return product.subsumeWorks.filter((w) => w.confirmedAt !== null).sort((a, b) => a.confirmedAt < b.confirmedAt ? 1 : -1);
}
function hasLostAllTargets(product) {
  return product.targets.length > 0 && product.targets.every((t) => t.lost);
}
function hasLostSomeTargets(product) {
  return product.targets.some((t) => t.lost) && !hasLostAllTargets(product);
}

// src/domain/scenario-product/scenario-product.ts
function createScenarioProduct(params) {
  if (!params.name.trim()) {
    throw new Error("ScenarioProduct name cannot be empty");
  }
  return {
    id: params.id,
    scenarioId: params.scenarioId,
    name: params.name,
    description: params.description ?? null,
    externalUrl: params.externalUrl ?? null,
    createdAt: params.now,
    updatedAt: params.now
  };
}
function updateScenarioProduct(product, updates, now) {
  return {
    ...product,
    name: updates.name !== void 0 ? updates.name : product.name,
    description: updates.description !== void 0 ? updates.description : product.description,
    externalUrl: updates.externalUrl !== void 0 ? updates.externalUrl : product.externalUrl,
    updatedAt: now
  };
}

// src/domain/budget/budget.ts
function budgetRemaining({
  allocated,
  spent,
  refunded
}) {
  return allocated - spent + refunded;
}
function budgetProjectedRemaining({
  allocated,
  spent,
  reserved,
  refunded
}) {
  return allocated - (spent + reserved) + refunded;
}
var CARRYOVER_POLICY = {
  TEAM: { rate: 1, capRate: null },
  WORLD: { rate: 1, capRate: null },
  FLEX: { rate: 0, capRate: null }
};
function budgetCarryover({
  budgetType,
  allocated,
  spent,
  refunded,
  baseAllocated
}) {
  const policy = CARRYOVER_POLICY[budgetType];
  if (!policy || policy.rate === 0) return 0;
  const carryable = Math.max(0, allocated - spent + refunded);
  const amount = Math.floor(carryable * policy.rate);
  if (policy.capRate === null) return amount;
  return Math.min(amount, Math.floor(baseAllocated * policy.capRate));
}
function isSamePool(a, b) {
  return a.budgetType === b.budgetType;
}
function spendRate({ allocated, spent }) {
  return allocated === 0 ? 0 : spent / allocated;
}
function projectedSpendRate({
  allocated,
  projectedSpent
}) {
  return allocated === 0 ? 0 : projectedSpent / allocated;
}
function budgetVariance({ allocated, spent }) {
  return allocated - spent;
}
function budgetVarianceRate({
  allocated,
  spent
}) {
  return allocated === 0 ? 0 : (allocated - spent) / allocated;
}
function teamBudgetTypeCoef(teamType) {
  return teamType === "TASK_FORCE" ? 0.7 : 1;
}
function recommendedTeamBudget({
  base,
  memberCount,
  teamType
}) {
  return Math.round(base * Math.sqrt(memberCount) * teamBudgetTypeCoef(teamType));
}
function isBudgetLedgerClosed(ledger) {
  return ledger.closedAt !== null && ledger.closedAt !== void 0;
}
function budgetPoolKey(budgetType, ownerId) {
  return `${budgetType}:${ownerId ?? "GLOBAL"}`;
}
function isBudgetLedgerActive(ledger) {
  return ledger.activePoolKey != null && ledger.startedAt != null && ledger.closedAt === null;
}

// src/domain/budget/spend-anomaly.ts
var DAY_MS4 = 24 * 60 * 60 * 1e3;
var SPEND_RATE_LOW_FLAG_DEFAULT = 0.4;
var SPEND_RATE_HIGH_FLAG_DEFAULT = 0.95;
var SPEND_IDLE_GRACE_DAYS_DEFAULT = 7;
function payPeriodElapsedDays({
  payDay,
  period,
  now
}) {
  const { start } = getPayPeriodUtcWindow(payDay ?? DEFAULT_PAY_DAY, period);
  return Math.floor((now.getTime() - start.getTime()) / DAY_MS4);
}
function classifySpendAnomaly({
  budgetType,
  allocated,
  spent,
  remaining,
  lowFlag,
  highFlag,
  judgeIdle
}) {
  if (budgetType !== "TEAM") return null;
  if (allocated <= 0 && spent <= 0) return null;
  if (spent <= 0 && !judgeIdle) return null;
  const rate = spendRate({ allocated, spent });
  if (rate > highFlag || remaining < 0) return "HIGH";
  if (rate < lowFlag) return "LOW";
  return null;
}

// src/domain/budget/dividend.ts
var DIVIDEND_POOL_RATE = 0.2;
function dividendPool({
  remaining,
  paidTotal,
  rate = DIVIDEND_POOL_RATE
}) {
  const cap = paidTotal * rate;
  return Math.max(0, Math.floor(Math.min(remaining, cap)));
}
function dividendGrantNote({
  period,
  completions,
  pool
}) {
  return `${period} \uAE09\uC5EC\uAE30\uAC04 \uBC30\uB2F9 (\uBB34\uB2E8\uAC00 \uC644\uB8CC ${completions}\uAC74 / \uD480 ${pool.toLocaleString("ko-KR")}P)`;
}
function distributeDividend({
  pool,
  completions
}) {
  const totalCompletions = completions.reduce((sum, c) => sum + c.completions, 0);
  const members = [...completions].sort((a, b) => b.completions - a.completions || a.userId.localeCompare(b.userId)).map((c) => ({
    ...c,
    dividendAmount: totalCompletions > 0 ? Math.floor(pool * c.completions / totalCompletions) : 0
  }));
  const paid = members.reduce((sum, m) => sum + m.dividendAmount, 0);
  return {
    members,
    totalCompletions,
    undistributedRemainder: pool - paid
  };
}

// src/domain/budget/settlement-notification.ts
var DAY_MS5 = 24 * 60 * 60 * 1e3;
var SETTLEMENT_NOTIFICATION_HOURS = {
  /** N1(배당 확정 요청) · N6 팀장 리마인드 */
  LEADER_FIRST: 9,
  /** N2(정산 시작 안내) · N6 대표 리마인드 */
  EXECUTIVE_FIRST: 13,
  /** N3(미확정 팀장 2차) — D 당일 한 번뿐 */
  LEADER_SECOND: 18
};
function firstDayOfPeriod(payDay, period) {
  return toKstDateString(getPayPeriodUtcWindow(payDay, period).start);
}
function getSettlementSchedule(payDay, now) {
  const today = toKstDateString(now);
  const current = getLedgerPeriod(payDay, now);
  const period = previousKstPeriod(current);
  const settlementStartDate = firstDayOfPeriod(payDay, current);
  const startUtc = Date.parse(`${settlementStartDate}T00:00:00.000Z`);
  const todayUtc = Date.parse(`${today}T00:00:00.000Z`);
  return {
    period,
    settlementStartDate,
    dayOffset: Math.round((todayUtc - startUtc) / DAY_MS5)
  };
}

// src/domain/chat-graph/chat-graph.ts
function indexById(graph) {
  const map = /* @__PURE__ */ new Map();
  for (const n of graph.nodes) map.set(n.id, n);
  return map;
}
function ancestorPath(graph, selectedId) {
  const byId = indexById(graph);
  const start = byId.get(selectedId);
  if (!start) return [];
  const chain = [];
  const seen = /* @__PURE__ */ new Set();
  let current = start;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.push(current);
    const parentId = current.parentId;
    current = parentId === null ? void 0 : byId.get(parentId);
  }
  return chain.reverse();
}
function contextNodes(graph, selectedId) {
  const path = selectedId === null ? [] : ancestorPath(graph, selectedId);
  const inPath = new Set(path.map((n) => n.id));
  const pinsNotInPath = graph.nodes.filter((n) => n.pinned && !inPath.has(n.id));
  return [...pinsNotInPath, ...path];
}
function isInContext(graph, selectedId, nodeId) {
  return contextNodes(graph, selectedId).some((n) => n.id === nodeId);
}
function isActionFlowNode(node) {
  return node.actionPending === true;
}
function childrenOf(graph, nodeId) {
  return graph.nodes.filter((n) => n.parentId === nodeId);
}
function descendantIds(graph, nodeId) {
  const childMap = /* @__PURE__ */ new Map();
  for (const n of graph.nodes) {
    const bucket = childMap.get(n.parentId);
    if (bucket) bucket.push(n);
    else childMap.set(n.parentId, [n]);
  }
  const result = [];
  const seen = /* @__PURE__ */ new Set([nodeId]);
  const stack = [...childMap.get(nodeId) ?? []];
  while (stack.length > 0) {
    const cur = stack.pop();
    if (seen.has(cur.id)) continue;
    seen.add(cur.id);
    result.push(cur.id);
    const kids = childMap.get(cur.id);
    if (kids) stack.push(...kids);
  }
  return result;
}

// src/domain/chat-graph/serialize.ts
function stripMarkers(text) {
  return text.replace(/<!--(?:FORM_UPDATE|ACTION)-->[\s\S]*?<!--\/(?:FORM_UPDATE|ACTION)-->/g, "").replace(/<!--(?:FORM_UPDATE|ACTION)-->[\s\S]*$/, "").replace(/<!--(?:\/(?:FORM_UPDATE|ACTION))?[\s\S]*$/, "").trim();
}
function hasActionMarker(text) {
  return /<!--(?:FORM_UPDATE|ACTION)-->/.test(text);
}
function messagesToGraph(messages, opts = {}) {
  const ordered = messages.map((m, i) => ({ m, i })).sort((x, y) => {
    const t = x.m.createdAt.localeCompare(y.m.createdAt);
    return t !== 0 ? t : x.i - y.i;
  }).map((w) => w.m);
  const userMsgs = ordered.filter((m) => m.role === "USER");
  const userIdSet = new Set(userMsgs.map((m) => m.id));
  const answerParts = /* @__PURE__ */ new Map();
  const markerFor = /* @__PURE__ */ new Set();
  let lastUserId = null;
  for (const m of ordered) {
    if (m.role === "USER") {
      lastUserId = m.id;
      continue;
    }
    const pid = m.parentId ?? null;
    const target = pid && userIdSet.has(pid) ? pid : lastUserId;
    if (target === null) continue;
    if (hasActionMarker(m.content)) markerFor.add(target);
    const stripped = stripMarkers(m.content);
    if (stripped === "") continue;
    const arr = answerParts.get(target);
    if (arr) arr.push(stripped);
    else answerParts.set(target, [stripped]);
  }
  const answerFor = /* @__PURE__ */ new Map();
  for (const [uid, parts] of answerParts) answerFor.set(uid, parts.join("\n\n"));
  const hasUserChild = /* @__PURE__ */ new Set();
  let prevNullSpineTip = null;
  const nodes = userMsgs.map((u) => {
    const explicitParent = u.parentId ?? null;
    let parentId;
    if (explicitParent && userIdSet.has(explicitParent)) {
      parentId = explicitParent;
    } else {
      parentId = prevNullSpineTip;
      prevNullSpineTip = u.id;
    }
    if (parentId !== null) hasUserChild.add(parentId);
    return {
      id: u.id,
      parentId,
      prompt: stripMarkers(u.content),
      answer: answerFor.get(u.id) ?? "",
      pinned: false,
      collapsed: false,
      status: "persistent",
      createdAt: u.createdAt
    };
  });
  for (const n of nodes) {
    if (markerFor.has(n.id) && !hasUserChild.has(n.id)) n.actionPending = true;
  }
  const lastActiveId = nodes.length > 0 ? nodes[nodes.length - 1].id : null;
  return {
    id: opts.id ?? messages[0]?.conversationId ?? "chat-graph",
    title: opts.title ?? "",
    nodes,
    lastActiveId
  };
}
var DEFAULT_MAX_CHARS = 8e3;
var TRUNCATION_MARKER = "\u2026 (\uC774\uC804 \uB9E5\uB77D \uC0DD\uB7B5) \u2026";
function renderNode(node, userLabel, assistantLabel) {
  const lines = [`${userLabel}: ${node.prompt}`];
  const answer = stripMarkers(node.answer);
  if (answer !== "") lines.push(`${assistantLabel}: ${answer}`);
  return lines.join("\n");
}
function serializeContext(graph, selectedId, pinnedIds, opts) {
  const maxChars = opts.maxChars ?? DEFAULT_MAX_CHARS;
  const userLabel = opts.userLabel ?? "\uC720\uC800";
  const assistantLabel = opts.assistantLabel ?? "\uB3C4\uAE68\uBE44";
  const tag = `[conversationId:${opts.conversationId}][userId:${opts.userId}]`;
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const pinSet = new Set(pinnedIds);
  const pinNodes = pinnedIds.map((id) => byId.get(id)).filter((n) => n !== void 0);
  const ancestorNodes = contextNodes(graph, selectedId).filter((n) => !pinSet.has(n.id));
  const renderPinBlock = (nodes) => {
    if (nodes.length === 0) return "";
    const body = nodes.map((n) => renderNode(n, userLabel, assistantLabel)).join("\n");
    return `[\uACE0\uC815\uB9E5\uB77D]
${body}`;
  };
  const renderAncestorBlock = (nodes, truncated) => {
    const body = nodes.map((n) => renderNode(n, userLabel, assistantLabel)).join("\n");
    if (truncated) return body === "" ? TRUNCATION_MARKER : `${TRUNCATION_MARKER}
${body}`;
    return body;
  };
  const assemble = (ancestors, truncated) => {
    const parts = [tag];
    const pinBlock = renderPinBlock(pinNodes);
    if (pinBlock !== "") parts.push(pinBlock);
    const ancestorBlock = renderAncestorBlock(ancestors, truncated);
    if (ancestorBlock !== "") parts.push(ancestorBlock);
    return parts.join("\n\n");
  };
  const full = assemble(ancestorNodes, false);
  if (full.length <= maxChars) return full;
  let kept = [...ancestorNodes];
  while (kept.length > 0) {
    const candidate = assemble(kept, true);
    if (candidate.length <= maxChars) return candidate;
    kept = kept.slice(1);
  }
  return assemble([], true);
}

// src/domain/reply-template/permissions.ts
var isAdmin = (actor) => actor.roles.includes("ADMIN");
var isLeaderOf = (actor, teamIds) => actor.memberships.some((m) => m.role === "LEADER" && teamIds.includes(m.teamId));
var isMemberOf = (actor, teamIds) => actor.memberships.some((m) => teamIds.includes(m.teamId));
function canReadReplyTemplate(actor, template, campaign) {
  if (template.layer === "PERSONAL") return template.ownerUserId === actor.userId;
  if (template.layer === "CAMPAIGN") {
    const teamIds = campaign?.scenarioTeamIds ?? [template.teamId];
    return isAdmin(actor) || actor.level === "EXECUTIVE" || isMemberOf(actor, teamIds);
  }
  return isAdmin(actor) || actor.level === "EXECUTIVE" || isMemberOf(actor, [template.teamId]);
}
function canEditReplyTemplate(actor, template, campaign) {
  if (template.layer === "PERSONAL") return template.ownerUserId === actor.userId;
  if (template.layer === "CAMPAIGN") {
    return campaign ? canSaveCampaignDefault(actor, campaign) : canPromoteToTeamTemplate(actor, template.teamId);
  }
  return canPromoteToTeamTemplate(actor, template.teamId);
}
function canSaveCampaignDefault(actor, campaign) {
  if (isAdmin(actor)) return true;
  if (isLeaderOf(actor, campaign.scenarioTeamIds)) return true;
  const owns = campaign.progressOwnerId === actor.userId || campaign.subOwnerIds.includes(actor.userId);
  return owns && isMemberOf(actor, campaign.scenarioTeamIds);
}
function canPromoteToTeamTemplate(actor, teamId) {
  return isAdmin(actor) || isLeaderOf(actor, [teamId]);
}

// src/domain/reply-template/copy.ts
var copyImage = (image) => image === null ? null : { ...image, crop: { ...image.crop } };
function createDraftContentFromTemplate(template, defaults) {
  return {
    subject: template.subject,
    body: template.body,
    image: copyImage(template.image ?? defaults.image),
    source: { templateId: template.id, layer: template.layer, parentId: template.parentId }
  };
}
function cloneReplyTemplate(source, target) {
  return {
    layer: target.layer,
    teamId: source.teamId,
    scenarioId: target.layer === "CAMPAIGN" ? target.scenarioId : null,
    // 나라는 캠페인 층의 축이다 — 내 임시로 내려가면 뗀다.
    country: target.layer === "CAMPAIGN" ? target.country : null,
    ownerUserId: target.layer === "PERSONAL" ? target.ownerUserId : null,
    parentId: source.id,
    stage: source.stage,
    kind: source.kind,
    title: source.title,
    subject: source.subject,
    body: source.body,
    image: copyImage(source.image)
  };
}

// src/domain/reply-template/usage-record.ts
var normalize = (body) => body.replace(/\r\n/g, "\n").trim();
function buildTemplateUsagePayload(input) {
  const payload = {
    templateId: input.source.templateId,
    templateLayer: input.source.layer,
    edited: String(normalize(input.loadedBody) !== normalize(input.sentBody))
  };
  if (input.source.parentId !== null) payload.templateParentId = input.source.parentId;
  const majorChange = input.majorChange?.trim();
  if (majorChange) payload.majorChange = majorChange;
  return payload;
}

// src/domain/outreach-draft/candidate-contact.ts
var CANDIDATE_CONTACT_STATES = [
  "unknown",
  "no_reply",
  "replied",
  "form_submitted"
];
var TOUCH_TYPES = /* @__PURE__ */ new Set(["CONTACT_SENT", "REPLY_RECEIVED"]);
function rosterFormEventAt(row) {
  return row.formSubmittedAt !== null && row.formSubmittedAt > row.createdAt ? row.formSubmittedAt : row.createdAt;
}
function lastEventMs(entries, scenarioId, type) {
  return entries.filter((entry) => entry.scenarioId === scenarioId && entry.type === type).reduce((max, entry) => {
    const ms = toEpochMs(entry.occurredAt);
    return max === null || ms > max ? ms : max;
  }, null);
}
function deriveCandidateContact({
  scenarioId,
  entries,
  rosterEntryId,
  rosterEventAt
}) {
  const lastReplyMs = lastEventMs(entries, scenarioId, "REPLY_RECEIVED");
  if (rosterEntryId !== null) {
    const rosterMs = rosterEventAt === void 0 || rosterEventAt === null ? null : toEpochMs(rosterEventAt);
    const lastResubmitMs = lastEventMs(entries, scenarioId, "FORM_RESUBMITTED");
    const evidenceMs = [rosterMs, lastReplyMs, lastResubmitMs].reduce(
      (max, ms) => ms === null ? max : max === null || ms > max ? ms : max,
      null
    );
    return {
      state: "form_submitted",
      at: null,
      rosterEntryId,
      everReplied: lastReplyMs !== null,
      defaultExcludedAt: evidenceMs === null ? null : new Date(evidenceMs).toISOString()
    };
  }
  const touches = sortRelationHistory(
    entries.filter((entry) => entry.scenarioId === scenarioId && TOUCH_TYPES.has(entry.type))
  );
  const last = touches.at(-1);
  const everReplied = lastReplyMs !== null;
  if (last === void 0) {
    return {
      state: "unknown",
      at: null,
      rosterEntryId: null,
      everReplied: false,
      defaultExcludedAt: null
    };
  }
  return {
    state: last.type === "REPLY_RECEIVED" ? "replied" : "no_reply",
    at: new Date(toEpochMs(last.occurredAt)).toISOString(),
    rosterEntryId: null,
    everReplied,
    defaultExcludedAt: lastReplyMs === null ? null : new Date(lastReplyMs).toISOString()
  };
}
function isDefaultExcluded(state) {
  return state === "replied" || state === "form_submitted";
}
function isDefaultExcludedContact(contact) {
  return contact.state === "form_submitted" || contact.everReplied || isDefaultExcluded(contact.state);
}

// src/domain/outreach-draft/variables.ts
var OUTREACH_BLOCKING_VARIABLES = ["\uC81C\uD488\uBA85", "\uAE08\uC561"];
function outreachProductName(products) {
  const [only] = products;
  return products.length === 1 && only ? only.name : null;
}
function formatOutreachAmount(amount) {
  const [whole = "", fraction] = amount.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === void 0 ? grouped : `${grouped}.${fraction}`;
}
function unfilledOutreachVariables(content, productName) {
  const missing = [];
  if (content.body.includes("{\uC81C\uD488\uBA85}") && !productName) missing.push("\uC81C\uD488\uBA85");
  if (content.body.includes("{\uAE08\uC561}") && !content.proposedAmount) missing.push("\uAE08\uC561");
  return missing;
}

// src/domain/outreach-draft/render.ts
var OUTREACH_FOOTER_LINES = [
  "Crevasse.AI (c)",
  "talk@pinply.com",
  "AEGISLAB, 64, Janghan-ro, Dongdaemun-gu, Seoul, Republic of Korea. +82) 010-2553-7557"
];
function escapeOutreachHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function paragraphs(body) {
  return body.split(/\n{2,}/).map((block) => block.trim()).filter((block) => block.length > 0).map(
    (block) => `<p style="margin:0 0 12px">${escapeOutreachHtml(block).replace(/\n/g, "<br>")}</p>`
  ).join("\n");
}
function fillVariables(content, productName) {
  const format3 = content.scheduleDateFormat ?? DEFAULT_OUTREACH_DATE_FORMAT;
  const dates = {
    \uCD08\uC548\uC77C: content.proposedDraftDueDate,
    \uC5C5\uB85C\uB4DC\uC77C: content.proposedUploadDate
  };
  return content.body.replace(/\{(초안일|업로드일|제품명|금액)\}/g, (token, name) => {
    if (name === "\uC81C\uD488\uBA85") return productName || token;
    if (name === "\uAE08\uC561") {
      return content.proposedAmount ? formatOutreachAmount(content.proposedAmount) : token;
    }
    const value = dates[name];
    if (value === null || value === void 0 || value === "") return token;
    return formatOutreachDate(value, format3);
  });
}
function ctaAlign(align) {
  if (align === "left") return "left";
  if (align === "right") return "right";
  return "center";
}
function renderOutreachDraftHtml(content, options) {
  const isLive = options.linkMode === "live";
  const lines = [
    "<!DOCTYPE html>",
    /*
      푸터의 주소·전화를 **메일 클라이언트가 제멋대로 링크로 바꾼다.** 색을 안 잡으면 파랗게
      튀고 색만 덮으면 밑줄이 남는다(2026-09-28 실측 두 번). `format-detection` 으로 자동
      인식을 끄라고 알리고, 그래도 걸린 것은 아래 규칙이 회색·밑줄 없음으로 덮는다.
      `x-apple-data-detectors` 는 애플 메일이 붙이는 표식이고, `tel`·`mailto` 는 Gmail 이
      만드는 링크다. 완전히 막히는 종류가 아니라 수단을 겹쳐 건다.
    */
    '<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="format-detection" content="telephone=no,date=no,address=no,email=no"><style>a[x-apple-data-detectors],a[href^="tel"],a[href^="mailto"]{color:#888!important;text-decoration:none!important}</style></head>',
    '<body style="margin:0;padding:24px;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#222;line-height:1.6">',
    `<h2 style="margin:0 0 16px;font-size:20px">${escapeOutreachHtml(content.subject ?? "")}</h2>`
  ];
  if (options.imageUrl !== null) {
    const img = `<img src="${escapeOutreachHtml(options.imageUrl)}" alt="${escapeOutreachHtml(content.image?.altText ?? "")}" style="width:100%;max-width:552px;display:block;border:0">`;
    const productUrl = content.productUrl;
    lines.push(
      isLive && productUrl ? `<p style="margin:0 0 16px"><a href="${escapeOutreachHtml(productUrl)}">${img}</a></p>` : `<p style="margin:0 0 16px">${img}</p>`
    );
    if (!isLive) {
      lines.push(
        `<p style="font-size:12px;color:#888;margin:-8px 0 16px;word-break:break-all">${productUrl ? `\uC81C\uD488 \uB9C1\uD06C ${escapeOutreachHtml(productUrl)} \u2014 \uC774\uBBF8\uC9C0\uB97C \uB204\uB974\uBA74 \uC5F4\uB9AC\uACE0, \uBBF8\uB9AC\uBCF4\uAE30\uC5D0\uC11C\uB294 \uC5F4\uB9AC\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.` : "\uC81C\uD488 \uB9C1\uD06C\uAC00 \uC5C6\uC5B4 \uC774\uBBF8\uC9C0\uB97C \uB20C\uB7EC\uB3C4 \uC544\uBB34 \uACF3\uC73C\uB85C \uAC00\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."}</p>`
      );
    }
  }
  lines.push(paragraphs(fillVariables(content, options.productName)));
  const label = escapeOutreachHtml(content.cta.label);
  const width = content.cta.width === "full" ? "display:block;text-align:center;" : "display:inline-block;";
  const button = `padding:12px 20px;background:#111;color:#fff;text-decoration:none;border-radius:6px;${width}`;
  if (isLive && options.formUrl !== null) {
    lines.push(
      `<p style="margin:24px 0;text-align:${ctaAlign(content.cta.align)}"><a href="${escapeOutreachHtml(options.formUrl)}" style="${button}">${label}</a></p>`
    );
  } else {
    lines.push(
      `<p style="margin:24px 0;text-align:${ctaAlign(content.cta.align)}"><span style="${button}">${label}</span></p>`,
      /*
        주소는 띄어쓰기가 없어 한 줄로 뻗는다. 발송 확인의 좁은 「받는 사람에게 보이는 모습」 칸이
        가로 스크롤을 달았다(2026-09-29 실측). 글자 사이에서 끊어 칸 안에 접는다 — 이 줄은
        미리보기 갈래에만 나오므로 실제로 나가는 메일은 안 바뀐다.
      */
      `<p style="font-size:12px;color:#888;word-break:break-all">\uC124\uBB38\uD3FC \uB9C1\uD06C ${escapeOutreachHtml(options.formUrl ?? "\uCEA0\uD398\uC778 \uC124\uBB38\uD3FC \uBBF8\uC124\uC815")} \u2014 \uBBF8\uB9AC\uBCF4\uAE30\uC5D0\uC11C\uB294 \uC5F4\uB9AC\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.</p>`
    );
  }
  const unsubscribe = "href" in options.unsubscribe ? `<a href="${escapeOutreachHtml(options.unsubscribe.href)}" style="color:#888">\uC218\uC2E0\uAC70\uBD80 Unsubscribe</a>` : escapeOutreachHtml(options.unsubscribe.note);
  lines.push(
    '<div style="margin-top:32px;text-align:center;font-size:12px;color:#888;line-height:1.8">',
    ...OUTREACH_FOOTER_LINES.map((text) => `<div style="color:#888">${text}</div>`),
    `<div style="color:#888">${unsubscribe}</div>`,
    "</div>",
    "</body></html>"
  );
  return lines.filter((line) => line.length > 0).join("\n");
}

// src/domain/outreach-draft/unwritten.ts
function isUnwrittenOutreachDraft(content) {
  return (content.subject ?? "").trim() === "" && content.body.trim() === "";
}

// src/domain/outreach-schedule/suppression.ts
var OUTREACH_SUPPRESSION_REASONS = ["UNSUBSCRIBED", "HARD_BOUNCED"];
function isOutreachSuppressionReason(value) {
  return OUTREACH_SUPPRESSION_REASONS.includes(value);
}
function normalizeSuppressionEmail(email) {
  return email.trim().toLowerCase();
}
function strongerSuppression(a, b) {
  return a === "UNSUBSCRIBED" ? a : b;
}
function suppressionOfPerson(input) {
  const reasonOf2 = (email) => input.suppressed.get(normalizeSuppressionEmail(email));
  const picked = input.email === null ? void 0 : reasonOf2(input.email);
  if (input.otherEmails.some((email) => reasonOf2(email) === "UNSUBSCRIBED")) {
    return "UNSUBSCRIBED";
  }
  return picked ?? null;
}

// src/domain/outreach-schedule/exclusion.ts
var EXCLUSION_LOCK_LEAD_MS = 30 * 6e4;
function exclusionLockAt(scheduledAt) {
  return new Date(scheduledAt.getTime() - EXCLUSION_LOCK_LEAD_MS);
}
function isRoundLocked(input) {
  if (input.sendStarted) return true;
  return input.now.getTime() >= exclusionLockAt(input.scheduledAt).getTime();
}
function planRoundExclusions(input) {
  const {
    recipients,
    formSubmitterIds,
    replierIds,
    otherTeamRecentIds,
    blockedIds,
    eventAt,
    suppressedAddresses,
    alreadyExcludedIds,
    lateIds,
    locked,
    sendStarted = locked
  } = input;
  const reasonOf2 = (recipient) => {
    const id = recipient.externalCreatorId;
    if (blockedIds?.has(id)) return "BLOCKED";
    const reason = formSubmitterIds.has(id) ? "FORM_SUBMITTED" : replierIds.has(id) ? "REPLIED" : otherTeamRecentIds?.has(id) ? "OTHER_TEAM_RECENT" : null;
    if (reason === null) return null;
    const evidence = recipient.reincludeEvidenceAt;
    if (evidence === void 0 || evidence === null) return reason;
    const latest = eventAt?.get(id);
    if (latest !== void 0 && latest.getTime() <= evidence.getTime()) return null;
    return reason;
  };
  const byAddress = /* @__PURE__ */ new Map();
  for (const recipient of recipients) {
    const key = recipient.email.trim().toLowerCase();
    byAddress.set(key, [...byAddress.get(key) ?? [], recipient]);
  }
  const release = [];
  const late = [];
  let remainingAddressCount = 0;
  for (const [email, members] of byAddress) {
    const already = members.some((m) => alreadyExcludedIds.has(m.externalCreatorId));
    const pending = members.filter((m) => !alreadyExcludedIds.has(m.externalCreatorId));
    const suppression = strongestSuppressionOf(members, suppressedAddresses);
    const stillInGroup = pending.length > 0 || members.some((m) => lateIds?.has(m.externalCreatorId));
    if (suppression !== void 0 && stillInGroup) {
      if (sendStarted) {
        for (const member of pending)
          late.push({ externalCreatorId: member.externalCreatorId, reason: suppression });
        if (!already) remainingAddressCount += 1;
      } else {
        release.push({
          email,
          externalCreatorIds: members.map((m) => m.externalCreatorId),
          reason: suppression
        });
      }
      continue;
    }
    const triggered = pending.map((m) => ({ member: m, reason: reasonOf2(m) })).filter(
      (t) => t.reason !== null
    );
    if (locked) {
      for (const t of triggered)
        late.push({ externalCreatorId: t.member.externalCreatorId, reason: t.reason });
      if (!already) remainingAddressCount += 1;
      continue;
    }
    if (triggered.length > 0) {
      const strongest = triggered.some((t) => t.reason === "BLOCKED") ? "BLOCKED" : triggered.some((t) => t.reason === "FORM_SUBMITTED") ? "FORM_SUBMITTED" : triggered.some((t) => t.reason === "REPLIED") ? "REPLIED" : "OTHER_TEAM_RECENT";
      release.push({
        email,
        externalCreatorIds: members.map((m) => m.externalCreatorId),
        reason: strongest
      });
      continue;
    }
    if (already) continue;
    remainingAddressCount += 1;
  }
  return {
    release,
    late,
    remainingAddressCount,
    // 잠긴 뒤에도 제외 명단이 마지막 주소까지 뺐으면 빈 예약을 남기지 않는다.
    cancelRound: remainingAddressCount === 0 && (!locked || !sendStarted && release.length > 0)
  };
}
function strongestSuppressionOf(members, suppressed) {
  if (suppressed === void 0) return void 0;
  let strongest;
  for (const member of members) {
    const reason = suppressionOfPerson({
      email: member.email,
      otherEmails: member.otherEmails ?? [],
      suppressed
    });
    if (reason !== null) strongest = strongerSuppression(strongest, reason);
  }
  return strongest;
}
function reservationGuardAtLock(input) {
  if (!input.hasForm) return "PROCEED";
  return input.watch === "WATCHING" ? "PROCEED" : "CANCEL_FORM_WATCH";
}

// src/domain/outreach-schedule/reservation.ts
var RESERVATION_CONFIRM_POLL_MS = 500;
var RESERVATION_CONFIRM_WINDOW_MS = 1e4;
var RESERVATION_MAX_ATTEMPTS = 2;
var CANCEL_WAIT_MS = 1e3;
var STIBEE_RETRY_DELAY_MS = 6e4;
var STIBEE_MAX_RETRIES = 1;
var RESERVATION_WATCH_OFFSETS_MS = [
  -5 * 6e4,
  -6e4,
  6e4,
  10 * 6e4
];
var SEND_STARTED_STATUSES = /* @__PURE__ */ new Set(["SENDING", "SENDING_DELAYED", "SENT"]);
var FAILED_STATUSES = /* @__PURE__ */ new Set(["FAILED", "SPAM"]);
function nextReservationConfirmStep(input) {
  const { status, elapsedMs, reserveAttempts } = input;
  if (status === "RESERVED" || SEND_STARTED_STATUSES.has(status)) {
    return { kind: "CONFIRMED" };
  }
  if (FAILED_STATUSES.has(status)) return { kind: "UNCONFIRMED" };
  const windowPassed = elapsedMs > RESERVATION_CONFIRM_WINDOW_MS;
  if (!windowPassed) return { kind: "POLL", afterMs: RESERVATION_CONFIRM_POLL_MS };
  if (status === "WRITING" && reserveAttempts < RESERVATION_MAX_ATTEMPTS) {
    return { kind: "RE_RESERVE" };
  }
  return { kind: "UNCONFIRMED" };
}
function cancelActionFor(status) {
  if (status === "RESERVED") return "CALL_CANCEL";
  if (status === "WRITING") return "ALREADY_CANCELLED";
  if (status === "PROCESSING") return "WAIT";
  return "TOO_LATE";
}
function isCancelConfirmed(status) {
  return status === "WRITING";
}
function isSendStarted(input) {
  if (SEND_STARTED_STATUSES.has(input.status)) return true;
  if (input.status === "PROCESSING") return input.now.getTime() >= input.scheduledAt.getTime();
  return false;
}
function classifyStibeeFailure(input) {
  if (input.timedOut) return "TIMEOUT";
  if (input.httpStatus === 429) return "RATE_LIMITED";
  if (input.httpStatus !== null && input.httpStatus >= 500) return "SERVER";
  if (input.code === "Errors.Service.TemporaryError") return "TEMPORARY";
  if (input.code === "Errors.Service.Common") return "AMBIGUOUS_COMMON";
  if (input.code === "Errors.Data.InvalidRequest") return "INVALID_REQUEST";
  return "OTHER";
}
function stibeeRetryDecision(kind, retriesSoFar) {
  switch (kind) {
    case "RATE_LIMITED":
    case "SERVER":
    case "TEMPORARY":
      return retriesSoFar < STIBEE_MAX_RETRIES ? { kind: "RETRY", afterMs: STIBEE_RETRY_DELAY_MS } : { kind: "UNCONFIRMED" };
    case "TIMEOUT":
    case "AMBIGUOUS_COMMON":
      return { kind: "REQUERY" };
    case "INVALID_REQUEST":
      return { kind: "FAIL" };
    default:
      return { kind: "UNCONFIRMED" };
  }
}
var KST_OFFSET_MS2 = 9 * 60 * 6e4;
function stibeeReserveTime(at) {
  const kst = new Date(at.getTime() + KST_OFFSET_MS2);
  const pad = (n) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}${pad(kst.getUTCMonth() + 1)}${pad(kst.getUTCDate())}${pad(kst.getUTCHours())}${pad(kst.getUTCMinutes())}${pad(kst.getUTCSeconds())}`;
}
var LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
function offsetMinutesAt(epochMs, timeZone) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).formatToParts(new Date(epochMs));
  } catch {
    return null;
  }
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second")
  );
  return Math.round((asUtc - Math.floor(epochMs / 1e3) * 1e3) / 6e4);
}
function resolveScheduledInstant(local, timeZone, now) {
  const match = LOCAL_PATTERN.exec(local);
  if (!match) return { ok: false, reason: "INVALID" };
  const [year = 0, month = 0, day = 0, hour = 0, minute = 0] = match.slice(1).map(Number);
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const back = new Date(wallAsUtc);
  if (back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day || hour > 23 || minute > 59) {
    return { ok: false, reason: "INVALID" };
  }
  const probes = [wallAsUtc - 24 * 60 * 6e4, wallAsUtc, wallAsUtc + 24 * 60 * 6e4];
  const offsets = /* @__PURE__ */ new Set();
  for (const probe of probes) {
    const offset = offsetMinutesAt(probe, timeZone);
    if (offset === null) return { ok: false, reason: "INVALID" };
    offsets.add(offset);
  }
  const instants = [...offsets].map((offset) => wallAsUtc - offset * 6e4).filter((epoch) => offsetMinutesAt(epoch, timeZone) === (wallAsUtc - epoch) / 6e4);
  const unique = [...new Set(instants)];
  if (unique.length === 0) return { ok: false, reason: "NONEXISTENT_LOCAL_TIME" };
  if (unique.length > 1) return { ok: false, reason: "AMBIGUOUS_LOCAL_TIME" };
  const [only] = unique;
  if (only === void 0) return { ok: false, reason: "NONEXISTENT_LOCAL_TIME" };
  const at = new Date(only);
  if (at.getTime() <= now.getTime()) return { ok: false, reason: "PAST" };
  return { ok: true, at };
}

// src/domain/outreach-schedule/time-zone.ts
var COUNTRY_TIME_ZONE = {
  KR: "Asia/Seoul",
  JP: "Asia/Tokyo",
  CN: "Asia/Shanghai",
  TW: "Asia/Taipei",
  HK: "Asia/Hong_Kong",
  TH: "Asia/Bangkok",
  VN: "Asia/Ho_Chi_Minh",
  PH: "Asia/Manila",
  ID: "Asia/Jakarta",
  MY: "Asia/Kuala_Lumpur",
  SG: "Asia/Singapore",
  IN: "Asia/Kolkata",
  AE: "Asia/Dubai",
  SA: "Asia/Riyadh",
  US: "America/New_York",
  CA: "America/Toronto",
  MX: "America/Mexico_City",
  BR: "America/Sao_Paulo",
  GB: "Europe/London",
  DE: "Europe/Berlin",
  FR: "Europe/Paris",
  ES: "Europe/Madrid",
  IT: "Europe/Rome",
  NL: "Europe/Amsterdam",
  PL: "Europe/Warsaw",
  AU: "Australia/Sydney",
  NZ: "Pacific/Auckland"
};
function defaultOutreachTimeZone(country) {
  return COUNTRY_TIME_ZONE[country.toUpperCase()] ?? "Asia/Seoul";
}
function formatScheduledLocal(iso2, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).formatToParts(new Date(iso2));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return `${get("month")}\uC6D4 ${get("day")}\uC77C ${get("hour")}:${get("minute")}`;
  } catch {
    return "";
  }
}

// src/domain/campaign-video-review.ts
import { z as z9 } from "zod";
var GuidelineReviewItemKind = {
  REQUIRED_SCENE: "REQUIRED_SCENE",
  CAUTION: "CAUTION",
  OPTIONAL_SCENE: "OPTIONAL_SCENE"
};
var guidelineReviewRequirementSchema = z9.object({
  kind: z9.enum(GuidelineReviewItemKind),
  text: z9.string().trim().min(1).max(2e3)
}).strict();
var guidelineReviewRequirementsSchema = z9.array(guidelineReviewRequirementSchema).max(100);
var CampaignVideoReviewVerdict = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};
var DRAFT_REVIEW_ROUND_RISK_THRESHOLD = 3;
function countDraftReviewRounds(revisions) {
  return revisions.filter((revision) => !revision.cancelledAt && !revision.supersededAt).length;
}
function isDraftReviewRoundAtRisk(roundCount) {
  return roundCount >= DRAFT_REVIEW_ROUND_RISK_THRESHOLD;
}

// src/domain/wiki/sections.ts
var HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;
var FENCE = /^ {0,3}(`{3,}|~{3,})/;
function headingLines(body) {
  const out = [];
  let fence = null;
  let offset = 0;
  for (const line of body.split("\n")) {
    const fenceMatch = FENCE.exec(line);
    if (fence !== null) {
      if (fenceMatch && fenceMatch[1][0] === fence[0] && fenceMatch[1].length >= fence.length)
        fence = null;
    } else if (fenceMatch) {
      fence = fenceMatch[1];
    } else {
      const h = HEADING.exec(line);
      const title = h?.[2]?.trim();
      if (h && title) out.push({ start: offset, depth: Math.max(1, h[1].length - 1), title });
    }
    offset += line.length + 1;
  }
  return out;
}
function splitWikiSections(body) {
  const lines = headingLines(body);
  const levels = [];
  for (const line of lines) levels.push(Math.min(line.depth, (levels.at(-1) ?? 0) + 1));
  const sections = [];
  const counters = [];
  lines.forEach((line, index) => {
    const level = levels[index];
    counters.length = level;
    counters[level - 1] = (counters[level - 1] ?? 0) + 1;
    let end = body.length;
    for (let j = index + 1; j < lines.length; j++) {
      if (levels[j] <= level) {
        end = lines[j].start;
        break;
      }
    }
    sections.push({
      index,
      level,
      number: counters.join("."),
      title: line.title,
      start: line.start,
      end
    });
  });
  return {
    preamble: { start: 0, end: lines[0]?.start ?? body.length },
    sections
  };
}
var OWN_NUMBER = /^(?:\d{1,3}\.(?:\d+(?:\.\d+)*)?|[A-Z]\.\d+(?:\.\d+)*|[A-Z]{1,2}\d{2,3})\s/;
function hasOwnSectionNumbers(sections) {
  return sections.some((section) => OWN_NUMBER.test(section.title));
}
function extractWikiSection(body, index) {
  const section = splitWikiSections(body).sections[index];
  return section ? body.slice(section.start, section.end) : null;
}
function replaceWikiSection(body, index, text) {
  const section = splitWikiSections(body).sections[index];
  if (!section) return null;
  const tail = body.slice(section.end);
  const replaced = tail.length > 0 ? `${text.trimEnd()}

` : text.trimEnd();
  return `${body.slice(0, section.start)}${replaced}${tail}`;
}

// src/domain/wiki/search.ts
var INITIALS = "\u3131\u3132\u3134\u3137\u3138\u3139\u3141\u3142\u3143\u3145\u3146\u3147\u3148\u3149\u314A\u314B\u314C\u314D\u314E";
var HANGUL_FIRST = 44032;
var HANGUL_LAST = 55203;
var PER_INITIAL = 21 * 28;
function hangulInitials(text) {
  let out = "";
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    out += code >= HANGUL_FIRST && code <= HANGUL_LAST ? INITIALS[Math.floor((code - HANGUL_FIRST) / PER_INITIAL)] ?? ch : ch;
  }
  return out;
}
function isInitialsQuery(query) {
  const compact = query.replace(/\s+/g, "");
  return compact.length >= 2 && [...compact].every((ch) => INITIALS.includes(ch));
}
function bigrams(text) {
  const chars = [...text.replace(/\s+/g, "").toLowerCase()];
  const grams = [];
  for (let i = 0; i < chars.length - 1; i += 1) grams.push(`${chars[i]}${chars[i + 1]}`);
  return grams;
}
function titleSimilarity(a, b) {
  const ga = bigrams(a);
  const gb = bigrams(b);
  if (ga.length === 0 || gb.length === 0) {
    return a.replace(/\s+/g, "").toLowerCase() === b.replace(/\s+/g, "").toLowerCase() ? 1 : 0;
  }
  const pool = [...gb];
  let hit = 0;
  for (const g of ga) {
    const at = pool.indexOf(g);
    if (at >= 0) {
      hit += 1;
      pool.splice(at, 1);
    }
  }
  return 2 * hit / (ga.length + gb.length);
}

export {
  DOKKAEBI_EVENTS,
  APP_EVENTS,
  TokenType,
  createPermission,
  hasPermission,
  RolePermissions,
  TeamRolePermissionModifiers,
  CapabilityPermissionModifiers,
  getPermissionsForUser,
  canPerform,
  isCeo,
  canViewUsageDashboard,
  MCP_TOKEN_PREFIX,
  MCP_TOKEN_RANDOM_BYTES,
  MCP_SCOPES,
  MCP_FINANCIAL_WRITE_SCOPES,
  MCP_IMPERSONATE_SCOPE,
  isMcpScope,
  MCP_SCOPE_ISSUE_REQUIREMENTS,
  MCP_SCOPE_PERMISSIONS,
  permissionsFromScopes,
  ungrantableScopes,
  MCP_SCOPE_PRESETS,
  mcpScopeSchema,
  createMcpTokenSchema,
  OAUTH_OFFLINE_ACCESS_SCOPE,
  OAUTH_AUTHORIZATION_CODE_TTL_SECONDS,
  OAUTH_AUTHORIZE_REQUEST_TTL_SECONDS,
  OAUTH_REFRESH_TOKEN_PREFIX,
  OAUTH_REFRESH_TOKEN_TTL_SECONDS,
  oauthConsentApproveSchema,
  oauthConsentDenySchema,
  ONBOARDING_ALLOWED_VIEWS,
  isOnboardingIsolated,
  canOpenWindowDuringOnboarding,
  VIEW_ELIGIBILITIES,
  APP_VIEW_REGISTRY,
  VIEW_API_PERMISSION,
  APP_VIEWS,
  isAppView,
  getAppViewMeta,
  getAppViewLabel,
  canAccessView,
  getAccessibleViews,
  getCapabilityViews,
  getAllAccessibleViews,
  isCapabilityView,
  canAccessViewFull,
  KNOWLEDGE_SOURCE_MEMBER_ROLES,
  KNOWLEDGE_AUTHORITY_STATUSES,
  KNOWLEDGE_INDEX_STATES,
  KNOWLEDGE_INDEXING_STATUSES,
  KNOWLEDGE_DOCUMENT_AVAILABILITIES,
  KNOWLEDGE_SYNC_JOB_STATES,
  KNOWLEDGE_INITIAL_EXTRACTOR_VERSION,
  KNOWLEDGE_INITIAL_CHUNKER_VERSION,
  KnowledgeIndexConfigSchema,
  SearchKnowledgeQuerySchema,
  GetKnowledgeDocumentQuerySchema,
  CreateKnowledgeSourceSchema,
  UpdateKnowledgeSourceSchema,
  KnowledgeSourceMemberSchema,
  CreateKnowledgeAuthorityDecisionSchema,
  CLUB_DURATION_CAP_MS,
  calculateClubDuration,
  checkClubDurationCap,
  approveFeed,
  ClubPollStatus,
  confirmPoll,
  CLUB_TINT_PALETTE,
  hashToAvatarTint,
  memoryImageTint,
  clubCodePrefix,
  generateClubCode,
  clubDDay,
  clubDeadlineLabel,
  clubDateLabel,
  assembleHeroPoll,
  ABSENCE_OPTION_CONTENT,
  ClubFeedStatus,
  FLEX_WEEKLY_WORK_THRESHOLD_MS,
  qualifiesForFlexWeeklyWork,
  MUST_FIX_SCORE,
  INFO_SCORE,
  ANOMALY_LABELS,
  detectSingleRecord,
  detectMultiRecord,
  scoreOf,
  isoWeekRange,
  isoWeekKey,
  assertRuleWellFormed,
  CONSISTENCY_RULES,
  parsePriorityBasis,
  findBlockedByAbsorbedPredecessor,
  findQuestsMissingBudgetLedger,
  findPricedQuestsWithoutPromisedDeadline,
  CheckpointStatus,
  TERMINAL_CHECKPOINT_STATUSES,
  isCheckpointClosed,
  CheckpointType,
  isValidCheckpointType,
  createCheckpoint,
  assignCheckpoint,
  completeCheckpoint,
  releaseCheckpoint,
  forfeitCheckpoint,
  forfeitCheckpointsOnQuestFail,
  releaseCheckpointsOnDrop,
  isCheckpointLockExpired,
  canModifyCheckpoint,
  canDeleteCheckpoint,
  compareCheckpointOrder,
  recalculateLockStates,
  updateCheckpoint,
  assertCanDeleteCheckpoint,
  LEGACY_CHECKPOINT_STATUS,
  CHECKPOINT_TRANSITIONS,
  canTransitionCheckpoint,
  checkpointTransitionSourcesTo,
  CheckpointRevisionKind,
  CHECKPOINT_REVISION_FIELDS,
  ProofStatus,
  createProof,
  submitProof,
  submitQuestProof,
  approveProof,
  rejectProof,
  updateProofDraft,
  resubmitProof,
  cancelProof,
  PROOF_REVIEW_OVERDUE_BUSINESS_DAYS,
  isProofReviewOverdue,
  AttachmentType,
  WorkLogStatus,
  createWorkLog,
  updateWorkLog,
  submitWorkLog,
  revisedAfterLogDay,
  isRosterCreatorOperable,
  parseOutreachFormUrl,
  isValidOutreachFormUrl,
  outreachFormRespondUrl,
  resolveFormQuestionIds,
  mapFormResponses,
  formEntryKeyOf,
  outreachHandleEntryKeys,
  deriveOutreachFormWatch,
  deriveImportRosterStage,
  importRosterStageAdvance,
  SYSTEM_ACTOR_USER_ID,
  SYSTEM_ACTOR_USER_SEED,
  buildSystemActorUpsertArgs,
  ensureSystemActor,
  creditedActorOf,
  isForwardRosterStageTransition,
  RosterAssigneeChangeCause,
  ManagerBonusSkipReason,
  isManagerBonusEligibleRow,
  managerBonusAllocationOf,
  calculateManagerBonus,
  ManagerBonusReasonCode,
  MANAGER_BONUS_REASON_MESSAGE,
  campaignZeroReasonOf,
  notSettledReasonOf,
  shareUnpaidReasonOf,
  myShareReasonOf,
  VIDEO_TRACKING_SUCCEEDED_END_REASONS,
  isChainOpen,
  isLinkedChainOpen,
  CHAIN_OPEN_WHERE,
  LINKED_CHAIN_OPEN_WHERE,
  isCurrentVideoTracking,
  PERFORMANCE_REPORT_ANCHOR_DAYS,
  PERFORMANCE_REPORT_STATUS,
  summarizeCampaignPerformance,
  formatViews,
  statusLabel,
  formatKstMinute,
  videoRemark,
  formatPerformanceReportNotionTable,
  performanceReportPrintHeader,
  canConfirmCampaignPerformanceReport,
  deriveAnchorCountsFromOutreachRounds,
  CAMPAIGN_RETRO_LESSON,
  campaignRetroDueAt,
  deriveCampaignRetroPermissions,
  validateCampaignRetroDeadline,
  isExportUploadConfirmed,
  orderRosterForExport,
  summarizeRosterNudges,
  canNudgeRosterRow,
  RosterDraftEventKind,
  summarizeRosterDraftEvents,
  resolveRosterDraftReceivedAt,
  ROSTER_DRAFT_ARRIVAL_STATUSES,
  canMarkRosterDraftArrival,
  canRerequestRosterRevision,
  deriveAnchorCountsFromRoster,
  seedEventTypeForRosterStage,
  getStageProofGuide,
  RosterStallKind,
  ROSTER_STALL_KIND_LABEL,
  ROSTER_STALL_THRESHOLD_DAYS,
  ROSTER_NO_REPLY_NUDGE_THRESHOLD,
  deriveRosterStallSignal,
  RosterDeliveryBlock,
  RosterDeliveryCondition,
  ROSTER_DELIVERY_RULES,
  rosterDeliveryBlock,
  missingRosterDeliveryConditions,
  RosterShippingState,
  deriveRosterShippingState,
  canExportRosterShipping,
  normalizeShippingCarrier,
  normalizeTrackingNumber,
  readTrackingSheet,
  TrackingImportResult,
  TRACKING_IMPORT_APPLIED,
  planTrackingImport,
  rosterPayoutWriteSchema,
  parsePayoutInput,
  maskPayoutValue,
  RosterDropEventAction,
  ROSTER_DROP_EVENT_ACTIONS,
  isRosterDropEventAction,
  formatRosterDropEvent,
  isAdvertiserReviewPending,
  deriveAdvertiserReviewMark,
  describeAdvertiserReviewRound,
  TargetSlotState,
  targetSlotAssignmentSchema,
  targetSlotDeliverySchema,
  campaignTargetSlotSchema,
  targetSlotSnapshotSchema,
  isTargetSlotState,
  isValidTargetSlotSnapshot,
  createTargetSlotSnapshot,
  applyTargetSlotAction,
  deriveTargetSlotCounts,
  CONDITION_AGREED_EVENT,
  classifyConditionChange,
  buildConditionChangeTargets,
  summarizeConditionChange,
  isConditionAgreed,
  resolveAppliedConditions,
  computeSettlementWithholding,
  DOMESTIC_QUEUE_TAB_STATUS,
  elapsedDays,
  summarizeDomesticQueue,
  deriveEffectiveRosterSnapshot,
  VideoPostPlatform,
  VIDEO_POST_PLATFORM_LABEL,
  VideoPostRole,
  VideoPostStatus,
  detectVideoPostPlatform,
  checkExtraPostUrl,
  listContentPosts,
  POST_RETENTION_DAYS,
  retentionStartOf,
  retentionWindow,
  summarizeCreatorPosts,
  retentionBusinessDate,
  assessVideoRetention,
  readPostAccess,
  FIRST_CONTRACT_VIDEO_NO,
  nextContractVideoNo,
  planAdditionalContractVideo,
  isAutomatedArchiveReply,
  classifyArchiveMessage,
  assigneeAfterSend,
  parseFromAddress,
  matchBulkReply,
  selectConversationMessages,
  CREATOR_MAIL_DRAFT_LEASE_MS,
  CREATOR_MAIL_DRAFT_HEARTBEAT_MS,
  creatorMailDraftLeaseExpiry,
  isCreatorMailDraftLeaseAlive,
  readCreatorMailDraftTakeovers,
  creatorMailDraftHistory,
  isOurTurn,
  REPLY_NEGLECT_BUSINESS_DAYS,
  isReplyNeglected,
  resolveReplyDeadline,
  isReplyDeadlineAtRisk,
  compareReplyUrgency,
  POLLING_STALE_THRESHOLD_MINUTES,
  ARCHIVE_COPY_GRACE_MINUTES,
  readPollingSilence,
  isArchiveCopyMissing,
  classifyArchiveGap,
  PayoutStatus,
  QuestPresenceUserSchema,
  QuestPresenceEntrySchema,
  NOTIFICATION_SCOPES,
  NOTIFICATION_TYPES,
  SETTLEMENT_NOTIFICATION_TYPES,
  isSettlementNotificationType,
  isNotificationScope,
  isNotificationType,
  formatWorklogNotSubmittedBody,
  QUEST_UPDATED_FIELD_LABELS,
  formatQuestUpdatedBody,
  formatQuestDeletedBody,
  formatQuestProofSubmittedBody,
  formatQuestProofSubmitReminderBody,
  formatCheckpointSubmittedBody,
  formatQuestTaskFeedbackAddedBody,
  formatQuestTaskAttachmentAddedBody,
  formatQuestPublishedBody,
  formatQuestPrerequisitesMetBody,
  formatOpenCallRevokedBody,
  formatQuestApprovedBody,
  formatQuestRejectedBody,
  formatScenarioProgressOwnerAssignedBody,
  formatScenarioProgressOwnerRevokedBody,
  formatOpsSignalReportCreatedBody,
  formatOpsSignalHelpNeededBody,
  formatQuestProposalCreatedBody,
  formatQuestProposalAdoptedBody,
  formatQuestProposalDeclinedBody,
  formatOpsSignalCommentAddedBody,
  formatOpsSignalStatusChangedBody,
  formatAdcodeRequestedAt,
  formatAdcodeQuestCreatedBody,
  formatQuestQuestionCreatedBody,
  ASSISTANT_WAITING_TIMEOUT_MS,
  DEFAULT_AGENT_SLUG,
  NARRATOR_AGENT_SLUG,
  DOKKAEBI_PROACTIVE_TITLE_PREFIX,
  ConversationEntityType,
  createQuestTemplate,
  updateQuestTemplate,
  instantiateQuest,
  BugReportStatus,
  TRIAGER_ONLY_STATUSES,
  BugReportType,
  BugReportEventType,
  BugReportAttachmentSchema,
  BUG_REPORT_PROCESSED_STATUSES,
  BUG_REPORT_QUEST_TEAM_NAME,
  canTransitionTo,
  calculateStalenessPercent,
  calculateLeadTime,
  isVoteEligible,
  QuestTaskStatus,
  QuestTaskCloseReason,
  createQuestTask,
  remainingAfterTask,
  completeQuestTask,
  checkoutQuestTask,
  isSameDayTitleFix,
  updateQuestTask,
  AUTO_FILL_TASK_TITLE,
  AUTO_FILL_TASK_MEMO,
  isAutoFillTask,
  deleteQuestTask,
  effectiveTaskValue,
  needsActualValue,
  priorContribution,
  deltaFromCumulative,
  calculateGaugeProgress,
  computeCheckpointFillValue,
  UNCLAIMED_CLOSE_REASONS,
  QuestTaskCreatedVia,
  isUnclaimedCloseReason,
  QuestTaskValueRevisionSource,
  reviewQuestQuestionReplies,
  canWriteQuestQuestion,
  canCommentOnQuestQuestion,
  createQuestQuestion,
  createQuestQuestionComment,
  awaitingLeaderReply,
  QUEST_QUESTION_NO_REPLY_MARKERS,
  hasNoReplyMarker,
  judgeTeamRetention,
  BUDGET_TYPE_LABELS,
  USAGE_DASHBOARD_MAX_DAYS,
  USAGE_DASHBOARD_DEFAULT_DAYS,
  usageDashboardQuerySchema,
  USAGE_TELEMETRY_CAVEATS,
  USAGE_DENOMINATOR_NOTE,
  POSTURE_MIGRATED_REASON,
  POSTURE_CREATED_REASON,
  POSTURE_STALE_DAYS,
  createProduct,
  updateProduct,
  declareProductPosture,
  daysSinceInvestment,
  isInvestmentStale,
  hasPostureMismatch,
  pendingSubsumeWorks,
  confirmedSubsumeWorks,
  hasLostAllTargets,
  hasLostSomeTargets,
  createScenarioProduct,
  updateScenarioProduct,
  budgetRemaining,
  budgetProjectedRemaining,
  budgetCarryover,
  isSamePool,
  spendRate,
  projectedSpendRate,
  budgetVariance,
  budgetVarianceRate,
  teamBudgetTypeCoef,
  recommendedTeamBudget,
  isBudgetLedgerClosed,
  budgetPoolKey,
  isBudgetLedgerActive,
  SPEND_RATE_LOW_FLAG_DEFAULT,
  SPEND_RATE_HIGH_FLAG_DEFAULT,
  SPEND_IDLE_GRACE_DAYS_DEFAULT,
  payPeriodElapsedDays,
  classifySpendAnomaly,
  DIVIDEND_POOL_RATE,
  dividendPool,
  dividendGrantNote,
  distributeDividend,
  SETTLEMENT_NOTIFICATION_HOURS,
  getSettlementSchedule,
  contextNodes,
  isInContext,
  isActionFlowNode,
  childrenOf,
  descendantIds,
  stripMarkers,
  hasActionMarker,
  messagesToGraph,
  serializeContext,
  canReadReplyTemplate,
  canEditReplyTemplate,
  canSaveCampaignDefault,
  canPromoteToTeamTemplate,
  createDraftContentFromTemplate,
  cloneReplyTemplate,
  buildTemplateUsagePayload,
  CANDIDATE_CONTACT_STATES,
  rosterFormEventAt,
  deriveCandidateContact,
  isDefaultExcluded,
  isDefaultExcludedContact,
  OUTREACH_BLOCKING_VARIABLES,
  outreachProductName,
  formatOutreachAmount,
  unfilledOutreachVariables,
  escapeOutreachHtml,
  renderOutreachDraftHtml,
  isUnwrittenOutreachDraft,
  OUTREACH_SUPPRESSION_REASONS,
  isOutreachSuppressionReason,
  normalizeSuppressionEmail,
  strongerSuppression,
  suppressionOfPerson,
  EXCLUSION_LOCK_LEAD_MS,
  exclusionLockAt,
  isRoundLocked,
  planRoundExclusions,
  reservationGuardAtLock,
  RESERVATION_CONFIRM_POLL_MS,
  RESERVATION_CONFIRM_WINDOW_MS,
  RESERVATION_MAX_ATTEMPTS,
  CANCEL_WAIT_MS,
  STIBEE_RETRY_DELAY_MS,
  STIBEE_MAX_RETRIES,
  RESERVATION_WATCH_OFFSETS_MS,
  nextReservationConfirmStep,
  cancelActionFor,
  isCancelConfirmed,
  isSendStarted,
  classifyStibeeFailure,
  stibeeRetryDecision,
  stibeeReserveTime,
  resolveScheduledInstant,
  defaultOutreachTimeZone,
  formatScheduledLocal,
  GuidelineReviewItemKind,
  guidelineReviewRequirementSchema,
  guidelineReviewRequirementsSchema,
  CampaignVideoReviewVerdict,
  DRAFT_REVIEW_ROUND_RISK_THRESHOLD,
  countDraftReviewRounds,
  isDraftReviewRoundAtRisk,
  splitWikiSections,
  hasOwnSectionNumbers,
  extractWikiSection,
  replaceWikiSection,
  hangulInitials,
  isInitialsQuery,
  titleSimilarity
};
//# sourceMappingURL=chunk-M7MYNPXI.mjs.map